import React, { useEffect, useState, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AnalogClockTimePicker from "./AnalogClockTimePicker";
import { parseSiteRegisterDate, formatSiteRegisterDate, siteRegisterDatePickerProps } from "./adminEditModalTheme";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { ensureFormUuid, noopCreateSuccess } from "../../utils/formUuid";
import { buildSharePointFileName, toDdMmYyyy, addedByName } from "../../utils/formPdfHelpers";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import onsiteproLogo from '../../assets/logo2 2.png';
import "../forms/formTitle.css";
import "../forms/themeCheckbox.css";
import "../forms/themePlaceholder.css";

// Import images
import img22 from "../../assets/icons/img22.png";
import img333 from "../../assets/icons/img333.png";
import arcProof from "../../assets/icons/arc_proof.png";
import bootMan from "../../assets/icons/boot_man.png";
import mandatoryGloves from "../../assets/icons/mandatory_gloves1.png";
import mandatoryHar from "../../assets/icons/mandatory_har.png";
import visitorImg from "../../assets/icons/vistor_img.png";
import CheckTick from "../../assets/icons/Check-Tick.png";

// Import PPE icons
import icon1 from "../../assets/icons/1.png";
import icon2 from "../../assets/icons/2.png";
import icon3 from "../../assets/icons/3.png";
import icon4 from "../../assets/icons/4.png";
import icon5 from "../../assets/icons/5.png";
import icon6 from "../../assets/icons/6.png";
import icon7 from "../../assets/icons/7.png";
import icon8 from "../../assets/icons/8.png";
import icon9 from "../../assets/icons/9.jpg";
import icon10 from "../../assets/icons/10.png";
import icon11 from "../../assets/icons/11.png";
import icon12 from "../../assets/icons/12.jpg";
import icon13 from "../../assets/icons/13.jpg";
import icon14 from "../../assets/icons/14.jpg";
import icon15 from "../../assets/icons/15.png";
import icon16 from "../../assets/icons/16.png";
import icon17 from "../../assets/icons/17.png";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
// Create a context for all images in assets/icons folder for dynamic loading
const iconsContext = require.context('../../assets/icons', true, /\.(png|jpg|jpeg|gif|svg)$/);

// Image mapping function
const getImageSrc = (imagePath) => {
  if (!imagePath) return '';
  
  const imageMap = {
    'assets/icons/img22.png': img22,
    'assets/icons/img333.png': img333,
    'assets/icons/arc_proof.png': arcProof,
    'assets/icons/boot_man.png': bootMan,
    'assets/icons/mandatory_gloves1.png': mandatoryGloves,
    'assets/icons/mandatory_har.png': mandatoryHar,
    'assets/icons/vistor_img.png': visitorImg,
    'assets/icons/1.png': icon1,
    'assets/icons/2.png': icon2,
    'assets/icons/3.png': icon3,
    'assets/icons/4.png': icon4,
    'assets/icons/5.png': icon5,
    'assets/icons/6.png': icon6,
    'assets/icons/7.png': icon7,
    'assets/icons/8.png': icon8,
    'assets/icons/9.jpg': icon9,
    'assets/icons/9.png': icon9,
  };
  
  return imageMap[imagePath] || imagePath;
};

function PowAssessmentPage() {
  const canEditDelete = canEditDeleteRecords();
  const [records, setRecords] = useState([]);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [uploadingKey, setUploadingKey] = useState(null);
  const isSavingRef = useRef(false); // Prevent duplicate save calls
  const generatingPdfRef = useRef(false);
  
  // SharePoint upload modal state
  const [showSharePointUploadModal, setShowSharePointUploadModal] = useState(false);
  const [sharePointUploadRecord, setSharePointUploadRecord] = useState(null);
  const [sharePointPhaseType, setSharePointPhaseType] = useState('phase');
  const [sharePointPhaseNumber, setSharePointPhaseNumber] = useState('15');
  const [sharePointNetwork, setSharePointNetwork] = useState('');
  const [pendingDirectUpload, setPendingDirectUpload] = useState(false);
  useEffect(() => {
    if (pendingDirectUpload && sharePointUploadRecord) {
      setPendingDirectUpload(false);
      // eslint-disable-next-line no-use-before-define
      handleSharePointUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDirectUpload, sharePointUploadRecord]);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editRecord, setEditRecord] = useState(null);
  const [assessmentReviewData, setAssessmentReviewData] = useState({
    header: "",
    descriptions: "",
    time: [''],
    timeHint: "",
    sssbReissue: [''],
    sssbReissueHint: ""
  });
  const [deleteId, setDeleteId] = useState(null);

  const [newRecord, setNewRecord] = useState({
    formUuid: "",
    userId: "",
    mcqQuestionsList: [],
    locationDescription: "",
    date: "",
    jobNumber: "",
    personInCharge: "",
    recodedOfMultiSiteVisit: "",
    step4: "",
    assessmentReview: "",
    step4Data: {
      header: "",
      descriptions: "",
      name: [''],
      nameHint: "",
      signature: ['']
    }
  });


  // Helper function to parse single-quoted JSON with sophisticated approach
  const parseSingleQuotedJSON = (str) => {
    if (!str || typeof str !== 'string') return [];
    
    try {
      // Method 1: Try direct JSON.parse first
      return JSON.parse(str);
    } catch (e1) {
      try {
        // Method 2: Convert single quotes to double quotes and parse
        let cleaned = str
          .replace(/'/g, '"')  // Replace all single quotes with double quotes
          .replace(/"true"/g, 'true')  // Fix boolean values
          .replace(/"false"/g, 'false')
          .replace(/"null"/g, 'null');
        return JSON.parse(cleaned);
      } catch (e2) {
        return [];
      }
    }
  };

  // Custom MCQ parser for malformed strings - EXACT COPY FROM PPMReportPage.js
  const parseCustomMcqFormat = (str) => {
    try {
      // Remove the outer brackets and split by sections
      const cleanStr = str.replace(/^\[|\]$/g, '');
      
      // Split by section headers (looking for {header: pattern)
      const sections = [];
      let currentSection = '';
      let braceCount = 0;
      let inSection = false;
      
      for (let i = 0; i < cleanStr.length; i++) {
        const char = cleanStr[i];
        if (char === '{') {
          braceCount++;
          if (cleanStr.substring(i, i + 8) === '{header:') {
            if (currentSection.trim()) {
              sections.push(currentSection.trim());
            }
            currentSection = '';
            inSection = true;
          }
        }
        
        if (inSection) {
          currentSection += char;
        }
        
        if (char === '}') {
          braceCount--;
          if (braceCount === 0 && inSection) {
            sections.push(currentSection.trim());
            currentSection = '';
            inSection = false;
          }
        }
      }
      
      // If no sections found with the above method, try a different approach
      if (sections.length === 0) {
        // Split by looking for the pattern {header: followed by content until the next {header:
        const sectionMatches = cleanStr.match(/\{header:[^}]+\}/g);
        if (sectionMatches) {
          sections.push(...sectionMatches);
        }
      }
      
      // If still no sections, try one more approach - split by major sections
      if (sections.length === 0) {
        // Look for the major section headers we know exist
        const majorHeaders = [
          'MCQ Questions',
          'Steps List', 
          'Safe Check List',
          'MCQ Questions HSS'
        ];
        
        let lastIndex = 0;
        majorHeaders.forEach((header, index) => {
          const headerIndex = cleanStr.indexOf(`{header: ${header}`);
          if (headerIndex !== -1) {
            if (index > 0) {
              // Add the previous section
              const sectionContent = cleanStr.substring(lastIndex, headerIndex).trim();
              if (sectionContent) {
                sections.push(sectionContent);
              }
            }
            lastIndex = headerIndex;
          }
        });
        
        // Add the last section
        if (lastIndex < cleanStr.length) {
          const lastSection = cleanStr.substring(lastIndex).trim();
          if (lastSection) {
            sections.push(lastSection);
          }
        }
      }
      
      
      // Parse each section
      const parsedSections = sections.map(section => {
        try {
          // Extract header
          const headerMatch = section.match(/header:\s*([^,]+)/);
          const header = headerMatch ? headerMatch[1].trim().replace(/['"]/g, '') : '';
          
          // Extract checkbox options
          const checkboxOptionsMatch = section.match(/checkboxOptions:\s*\[([^\]]*)\]/);
          const checkboxOptions = checkboxOptionsMatch ? 
            checkboxOptionsMatch[1].split(',').map(opt => opt.trim().replace(/['"]/g, '')).filter(opt => opt) : [];
          
          // Extract checkbox values
          const checkboxValuesMatch = section.match(/checkboxValues:\s*\{([^}]*)\}/);
          const checkboxValues = {};
          if (checkboxValuesMatch) {
            const pairs = checkboxValuesMatch[1].split(',').map(pair => pair.trim());
            pairs.forEach(pair => {
              const [key, value] = pair.split(':').map(s => s.trim().replace(/['"]/g, ''));
              if (key && value !== undefined) {
                checkboxValues[key] = value === 'true';
              }
            });
          }
          
          // Extract items
          const itemsMatch = section.match(/items:\s*\[([^\]]*)\]/);
          const items = [];
          if (itemsMatch) {
            const itemsStr = itemsMatch[1];
            // Split items by looking for {title: pattern
            let itemStart = itemsStr.indexOf('{title:');
            while (itemStart !== -1) {
              let itemEnd = itemsStr.indexOf('}', itemStart);
              if (itemEnd === -1) break;
              
              const itemStr = itemsStr.substring(itemStart, itemEnd + 1);
              const item = parseItem(itemStr);
              if (item) items.push(item);
              
              itemStart = itemsStr.indexOf('{title:', itemEnd + 1);
            }
          }
          
          // If no items found with the above method, try alternative approach
          if (items.length === 0) {
            // Look for title patterns in the entire section
            const titleMatches = section.match(/\{title:[^}]+\}/g);
            if (titleMatches) {
              titleMatches.forEach(itemStr => {
                const item = parseItem(itemStr);
                if (item) items.push(item);
              });
            }
          }
          
          return {
            header,
            checkboxOptions,
            checkboxValues,
            items
          };
        } catch (error) {
          return null;
        }
      }).filter(Boolean);
      
      return parsedSections;
    } catch (error) {
          return [];
        }
  };
  
  // Parse individual item - EXACT COPY FROM PPMReportPage.js
  const parseItem = (itemStr) => {
    try {
      // Extract title
      const titleMatch = itemStr.match(/title:\s*([^,}]+)/);
      const title = titleMatch ? titleMatch[1].trim().replace(/['"]/g, '') : '';
      
      // Extract options
      const optionsMatch = itemStr.match(/options:\s*\[([^\]]*)\]/);
      const options = optionsMatch ? 
        optionsMatch[1].split(',').map(opt => opt.trim().replace(/['"]/g, '')).filter(opt => opt) : [];
      
      // Extract value
      const valueMatch = itemStr.match(/value:\s*([^,}]+)/);
      const value = valueMatch ? valueMatch[1].trim().replace(/['"]/g, '') : '';
      
      // Extract hasCheckbox
      const hasCheckboxMatch = itemStr.match(/hasCheckbox:\s*([^,}]+)/);
      const hasCheckbox = hasCheckboxMatch ? hasCheckboxMatch[1].trim() === 'true' : false;
      
      // Extract textField
      const textFieldMatch = itemStr.match(/textField:\s*\{([^}]*)\}/);
      const textField = {};
      if (textFieldMatch) {
        const tfStr = textFieldMatch[1];
        const isTextBoxMatch = tfStr.match(/isTextBox:\s*([^,}]+)/);
        textField.isTextBox = isTextBoxMatch ? isTextBoxMatch[1].trim() === 'true' : false;
        
        const hintTextMatch = tfStr.match(/hintText:\s*([^,}]+)/);
        textField.hintText = hintTextMatch ? hintTextMatch[1].trim().replace(/['"]/g, '') : '';
        
        const valueTypeMatch = tfStr.match(/valueType:\s*([^,}]+)/);
        textField.valueType = valueTypeMatch ? valueTypeMatch[1].trim().replace(/['"]/g, '') : '';
        
        const naMatch = tfStr.match(/n\/a:\s*([^,}]+)/);
        textField['n/a'] = naMatch ? naMatch[1].trim() === 'true' : false;
      }
      
      // Extract textFieldValue
      const textFieldValueMatch = itemStr.match(/textFieldValue:\s*([^,}]+)/);
      const textFieldValue = textFieldValueMatch ? textFieldValueMatch[1].trim().replace(/['"]/g, '') : '';
      
      const result = {
        title,
        options,
        value,
        hasCheckbox,
        textField,
        textFieldValue
      };
      return result;
    } catch (error) {
      return null;
    }
  };

  // Manual parser for the specific format - returns empty array to avoid static data
  const parseManualFormat = (str) => {
    try {
      // Try a more aggressive approach for the specific format
      let cleaned = str;
      
      // Remove any leading/trailing whitespace
      cleaned = cleaned.trim();
      
      // Handle the specific pattern: [{'title':'Electricity',...}]
      // Replace single quotes with double quotes more carefully
      cleaned = cleaned
        .replace(/'/g, '"')  // Replace all single quotes with double quotes
        .replace(/""/g, '"') // Fix any double double quotes
        .replace(/"true"/g, 'true')  // Fix boolean values
        .replace(/"false"/g, 'false')
        .replace(/"null"/g, 'null')
        .replace(/\\"/g, '"')  // Fix escaped quotes
        .replace(/\\\\/g, '\\') // Fix double backslashes
        .replace(/,\s*}/g, '}') // Remove trailing commas before closing braces
        .replace(/,\s*]/g, ']'); // Remove trailing commas before closing brackets
      
      const result = JSON.parse(cleaned);
      return result;
    } catch (e) {
      // Last resort: Try to extract basic structure using regex
      try {
        const titleMatches = str.match(/'title':\s*'([^']+)'/g);
        if (titleMatches && titleMatches.length > 0) {
          const titles = titleMatches.map(match => {
            const titleMatch = match.match(/'title':\s*'([^']+)'/);
            return titleMatch ? titleMatch[1] : null;
          }).filter(Boolean);
          
          // Return empty array instead of hardcoded structure
          return [];
        }
      } catch (regexError) {
      }
      
      return [];
    }
  };

  // Normalize MCQ data coming from API (strings, booleans, etc.)
  const normalizeMcq = (sections) => {
    if (!Array.isArray(sections)) return [];
    return sections.map(section => {
      const checkboxValues = section.checkboxValues || {};
      const normalizedCheckboxValues = Object.fromEntries(
        Object.entries(checkboxValues).map(([k, v]) => [k, v === true || v === 'true'])
      );
      const items = Array.isArray(section.items) ? section.items.map(item => {
        const raw = item.value;
        let booleanValue = (raw === true || raw === 'true');
        let value = raw;
        if (Array.isArray(item.options) && item.options.length && typeof raw === 'string') {
          const lower = raw.toLowerCase();
          if (lower === 'true') value = 'YES';
          else if (lower === 'false') value = 'NO';
        }
        const textField = item.textField || {};
        const naFlag = (textField['n/a'] === true || textField['n/a'] === 'true' || textField.n_a === true || textField.n_a === 'true');
        return {
          ...item,
          value,
          booleanValue,
          checkboxValue: item.checkboxValue !== undefined ? item.checkboxValue : booleanValue,
          textFieldValue: (() => {
            // If user has interacted with the field (item.textFieldValue is defined), use it (even if empty)
            // This allows user to clear the field
            if (item.textFieldValue !== undefined) {
              // Filter out "string" if it somehow got in there
              return item.textFieldValue === 'string' ? '' : item.textFieldValue;
            }
            // Otherwise, use the actual value from API (textField.value, not valueType)
            const apiValue = textField.value;
            if (apiValue !== undefined && apiValue !== '' && apiValue !== 'string') {
              return apiValue;
            }
            // Fallback to empty string (never use valueType)
            return '';
          })(),
          textField: {
            isTextBox: !!textField.isTextBox,
            hintText: textField.hintText || '',
            valueType: textField.valueType || '',
            value: textField.value || '',
            na: naFlag
          }
        };
      }) : [];
      return {
        ...section,
        checkboxValues: normalizedCheckboxValues,
        items
      };
    });
  };

  const handleMcqNAChange = (sectionIdx, itemIdx, checked, isEdit = false) => {
    if (isEdit) {
      const updated = [...editRecord.mcqQuestionsList];
      const it = updated[sectionIdx].items[itemIdx];
      updated[sectionIdx].items[itemIdx] = { ...it, textField: { ...it.textField, na: !!checked } };
      setEditRecord({ ...editRecord, mcqQuestionsList: updated });
    } else {
      const updated = [...newRecord.mcqQuestionsList];
      const it = updated[sectionIdx].items[itemIdx];
      updated[sectionIdx].items[itemIdx] = { ...it, textField: { ...it.textField, na: !!checked } };
      setNewRecord({ ...newRecord, mcqQuestionsList: updated });
    }
  };

  // MCQ Questions List handlers
  const handleMcqChange = (sectionIdx, itemIdx, value, isEdit = false) => {
    if (isEdit) {
      const updatedSections = [...editRecord.mcqQuestionsList];
      const item = updatedSections[sectionIdx].items[itemIdx];
      updatedSections[sectionIdx].items[itemIdx] = {
        ...item,
        value,
        booleanValue: value === 'YES' ? true : value === 'NO' ? false : item.booleanValue
      };
      setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
    } else {
      const updatedSections = [...newRecord.mcqQuestionsList];
      const item = updatedSections[sectionIdx].items[itemIdx];
      updatedSections[sectionIdx].items[itemIdx] = {
        ...item,
        value,
        booleanValue: value === 'YES' ? true : value === 'NO' ? false : item.booleanValue
      };
      setNewRecord({ ...newRecord, mcqQuestionsList: updatedSections });
    }
  };

  const handleMcqCheckboxChange = (sectionIdx, checkboxKey, value, isEdit = false) => {
    const apply = (sections) => {
      const updatedSections = [...sections];
      const section = { ...updatedSections[sectionIdx] };
      const checkboxValues = { ...(section.checkboxValues || {}) };
      const isNA = String(checkboxKey).trim().toLowerCase() === 'na';
      if (value) {
        if (isNA) {
          // Selecting NA auto-unchecks every other option in this section.
          Object.keys(checkboxValues).forEach((k) => { checkboxValues[k] = false; });
        } else {
          // Selecting any other option auto-unchecks NA.
          Object.keys(checkboxValues).forEach((k) => {
            if (String(k).trim().toLowerCase() === 'na') checkboxValues[k] = false;
          });
        }
      }
      checkboxValues[checkboxKey] = value;
      section.checkboxValues = checkboxValues;
      updatedSections[sectionIdx] = section;
      return updatedSections;
    };
    if (isEdit) {
      setEditRecord({ ...editRecord, mcqQuestionsList: apply(editRecord.mcqQuestionsList) });
    } else {
      setNewRecord({ ...newRecord, mcqQuestionsList: apply(newRecord.mcqQuestionsList) });
    }
  };

  const handleMcqTextFieldChange = (sectionIdx, itemIdx, value, isEdit = false) => {
    if (isEdit) {
      const updatedSections = [...editRecord.mcqQuestionsList];
      updatedSections[sectionIdx].items[itemIdx].textFieldValue = value;
      setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
    } else {
      const updatedSections = [...newRecord.mcqQuestionsList];
      updatedSections[sectionIdx].items[itemIdx].textFieldValue = value;
      setNewRecord({ ...newRecord, mcqQuestionsList: updatedSections });
    }
  };

  const handleMcqItemCheckboxChange = (sectionIdx, itemIdx, checked, isEdit = false) => {
    const apply = (sections) => {
      const updatedSections = [...sections];
      const section = { ...updatedSections[sectionIdx] };
      const items = (section.items || []).map((it) => ({ ...it }));
      const targetIsNA = String(items[itemIdx]?.title || '').trim().toLowerCase() === 'na';
      if (checked) {
        if (targetIsNA) {
          // Selecting NA auto-unchecks every other option in this section.
          items.forEach((it, i) => { if (i !== itemIdx) { it.checkboxValue = false; it.booleanValue = false; } });
        } else {
          // Selecting any other option auto-unchecks NA.
          items.forEach((it) => {
            if (String(it.title || '').trim().toLowerCase() === 'na') { it.checkboxValue = false; it.booleanValue = false; }
          });
        }
      }
      items[itemIdx].checkboxValue = checked;
      section.items = items;
      updatedSections[sectionIdx] = section;
      return updatedSections;
    };
    if (isEdit) {
      setEditRecord({ ...editRecord, mcqQuestionsList: apply(editRecord.mcqQuestionsList) });
    } else {
      setNewRecord({ ...newRecord, mcqQuestionsList: apply(newRecord.mcqQuestionsList) });
    }
  };

  useEffect(() => {
    async function fetchRecords() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/pow-assessment", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.data)) {
          setRecords(data.data);
        } else {
          setRecords([]);
        }
      } catch {
        setRecords([]);
      }
    }
    async function fetchAppUsers() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/app-users", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.data)) {
          setAppUsers(data.data);
        } else {
          setAppUsers([]);
        }
      } catch {
        setAppUsers([]);
      }
    }
    fetchRecords();
    fetchAppUsers();
  }, []);

  // Function to get MCQ structure from API for new records
  const getMcqStructureFromApi = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + "/api/pow-assessment", {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success && Array.isArray(data.data) && data.data.length > 0) {
        // Use the first record to get the structure
        const firstRecord = data.data[0];
        if (firstRecord.mcqQuestionsList) {
          // If it has the new format, use it
          return firstRecord.mcqQuestionsList;
        } else {
          // If it has the old format, convert it
          return convertOldFormatToNew(firstRecord);
        }
      }
    } catch (error) {
    }
    return [];
  };

  // New simplified function to parse stepsList data correctly - with aggressive parsing
  const parseStepsListData = (stepsListString) => {
    try {
      let stepsData = [];
      
      // Try to parse the string as JSON first
      if (typeof stepsListString === 'string') {
        try {
          stepsData = JSON.parse(stepsListString);
    } catch (e) {
          // If JSON parsing fails, try robust conversion like in parseCustomMcqFormat
          let s = stepsListString.trim();
          try {
            // 1) Quote unquoted keys (allowing / - _ ( ) in names)
            s = s.replace(/([,{]\s*)([A-Za-z0-9_\/\-\(\)]+)(\s*:)/g, '$1"$2"$3');
            // 2) Convert single-quoted strings to double-quoted safely
            s = s.replace(/'((?:\\'|[^'])*)'/g, (m, inner) => {
              const unescaped = inner.replace(/\\'/g, "'");
              const escapedDouble = unescaped.replace(/"/g, '\\"');
              return '"' + escapedDouble + '"';
            });
            // 3) Fix booleans in known places
            s = s.replace(/(\"value\"\s*:\s*)\"true\"/g, '$1true');
            s = s.replace(/(\"value\"\s*:\s*)\"false\"/g, '$1false');
            // 4) Remove trailing commas
            s = s.replace(/,\s*([}\]])/g, '$1');
            // 5) Fix the specific format issue with missing quotes around property names
            s = s.replace(/([A-Za-z0-9_\/\-\(\)]+):/g, '"$1":');
            // 6) Fix the specific issue with "n/a" property
            s = s.replace(/"n\/a":/g, '"n/a":');
            
            stepsData = JSON.parse(s);
          } catch (e2) {
            // Last resort: simple quote replacement
            let cleaned = stepsListString
              .replace(/'/g, '"')
              .replace(/"true"/g, 'true')
              .replace(/"false"/g, 'false');
            try {
              stepsData = JSON.parse(cleaned);
            } catch (e3) {
              // Final fallback: extract data using regex patterns
              return parseStepsListWithRegex(stepsListString);
            }
          }
        }
      } else if (Array.isArray(stepsListString)) {
        stepsData = stepsListString;
      }
      
      if (!Array.isArray(stepsData)) {
        return parseStepsListWithRegex(stepsListString);
      }
      
      let items = [];
      
      // Process each step in the data
            stepsData.forEach(step => {
        if (!step || !step.title) return;
        
        const stepTitle = step.title;
        const stepImg = step.img || null; // Preserve img property from step level
        
        // Add main title
        items.push({
                title: stepTitle,
                options: [],
                value: '',
                hasCheckbox: false,
                textField: { isTextBox: false, hintText: '', valueType: '', n_a: false },
          isMainTitle: true,
          img: stepImg // Preserve img for main title
        });
              
        // Process all steps dynamically (any key that's not 'title' or 'img')
        const stepKeys = Object.keys(step).filter(key => key !== 'title' && key !== 'img');
        stepKeys.forEach((stepKey, index) => {
          const stepData = step[stepKey];
          if (stepData && stepData.header) {
            // Check if we should show the step header
            const hasCheckboxes = stepData.items && stepData.items.checkBox && stepData.items.checkBox.isCheckBoxShow && stepData.items.checkBox.itemList;
            const hasTextBox = stepData.items && stepData.items.textBox && stepData.items.textBox.isTextBoxShow;
            
            // Only show step header if there are checkboxes OR textbox is enabled
            if (hasCheckboxes || hasTextBox) {
              // Add step header
              items.push({
                title: `${stepKey}: ${stepData.header.toUpperCase()}`,
                    options: [],
                    value: '',
                    hasCheckbox: false,
                    textField: { isTextBox: false, hintText: '', valueType: '', n_a: false },
                isStepHeader: true,
                img: stepImg // Preserve img for step header
              });
            }
            
            // Add checkbox items if they exist and are enabled
            if (hasCheckboxes) {
              stepData.items.checkBox.itemList.forEach(checkboxItem => {
                items.push({
                  title: checkboxItem.title,
                    options: [],
                    value: '',
                    hasCheckbox: true,
                  checkboxValue: checkboxItem.value || false,
                    textField: { isTextBox: false, hintText: '', valueType: '', n_a: false },
                  img: stepImg // Preserve img for checkbox items
                });
              });
            }
            
            // Add text box if it exists and is enabled
            if (hasTextBox) {
              items.push({
                title: stepData.items.textBox.hintText || '',
                    options: [],
                    value: '',
                    hasCheckbox: false,
                stepHeader: stepData.header || '', // Store the step header
                stepName: stepKey, // Store the step name
                    textField: { 
                      isTextBox: true, 
                  hintText: stepData.items.textBox.hintText || '', 
                  valueType: stepData.items.textBox.value || '', 
                      n_a: false,
                  value: stepData.items.textBox.value || ''
                    },
                  img: stepImg // Preserve img for text box items
                  });
                }
          }
        });
      });
      
      return items;
    } catch (error) {
      return parseStepsListWithRegex(stepsListString);
    }
  };

  // Parse all main sections from the stepsList array using a simpler approach
  const parseStepsListWithRegex = (stepsListString) => {
    try {
      
      let items = [];
      
      // Extract all main section titles (the ones that start with {'title':'SectionName','anykey':{...})
      const mainSectionPattern = /\{'title':\s*'([^']+)',\s*'[^']+':\s*\{/g;
      let mainSectionMatch;
      
      while ((mainSectionMatch = mainSectionPattern.exec(stepsListString)) !== null) {
        const mainTitle = mainSectionMatch[1];
        const sectionStartIndex = mainSectionMatch.index;
        
        
        // Find the end of this section (next main section or end of string)
        let sectionEndIndex = stepsListString.length;
        const nextMainSectionPattern = /\{'title':\s*'([^']+)',\s*'[^']+':\s*\{/g;
        nextMainSectionPattern.lastIndex = sectionStartIndex + 1;
        const nextMatch = nextMainSectionPattern.exec(stepsListString);
        if (nextMatch) {
          sectionEndIndex = nextMatch.index;
        }
        
        const sectionStr = stepsListString.substring(sectionStartIndex, sectionEndIndex);
        
        // Add main title
        items.push({
          title: mainTitle,
                  options: [],
                  value: '',
                  hasCheckbox: false,
                  textField: { isTextBox: false, hintText: '', valueType: '', n_a: false },
          isMainTitle: true
        });
        
        // Process all steps dynamically using regex (any step name, not just step1, step2, etc.)
        const stepPattern = /'([^']+)':\s*\{'header':\s*'([^']+)'[^}]*\}/g;
        let stepMatch;
        const steps = [];
        
        // Extract all steps from this section
        while ((stepMatch = stepPattern.exec(sectionStr)) !== null) {
          const stepName = stepMatch[1];
          const stepHeader = stepMatch[2];
          steps.push({ stepName: stepName, header: stepHeader });
        }
        
        // Process each step (no need to sort since we process in order found)
        steps.forEach((step, index) => {
          // Extract checkbox items from this step's itemList using a more robust approach
          let itemListStr = null;
          
          // Find the step section first
          const stepStartPattern = new RegExp(`'${step.stepName}':\\s*\\{`);
          const stepStartMatch = sectionStr.match(stepStartPattern);
          
          if (stepStartMatch) {
            const stepStartIndex = stepStartMatch.index + stepStartMatch[0].length;
            
            // Find the matching closing brace for this step
            let braceCount = 1;
            let stepEndIndex = stepStartIndex;
            
            while (braceCount > 0 && stepEndIndex < sectionStr.length) {
              if (sectionStr[stepEndIndex] === '{') {
                braceCount++;
              } else if (sectionStr[stepEndIndex] === '}') {
                braceCount--;
              }
              stepEndIndex++;
            }
            
            if (braceCount === 0) {
              const stepContent = sectionStr.substring(stepStartIndex, stepEndIndex - 1);
              
              // Look for itemList in the step content
              const itemListPattern = /'itemList':\s*\[([^\]]+)\]/;
              const itemListMatch = stepContent.match(itemListPattern);
              
              if (itemListMatch) {
                itemListStr = itemListMatch[1];
              } else {
              }
            }
          } else {
          }
          
          // Check if we should show the step header
          const hasCheckboxes = itemListStr !== null;
          const stepTextBoxPattern = new RegExp(`'${step.stepName}':\\s*\\{[^}]*'textBox':\\s*\\{[^}]*'isTextBoxShow':\\s*true[^}]*\\}`);
          const stepTextBoxMatch = sectionStr.match(stepTextBoxPattern);
          const hasTextBox = stepTextBoxMatch !== null;
          
          // Only show step header if there are checkboxes OR textbox is enabled
          if (hasCheckboxes || hasTextBox) {
            // Add step header
            items.push({
              title: `${step.stepName}: ${step.header.toUpperCase()}`,
                       options: [],
                       value: '',
                       hasCheckbox: false,
              textField: { isTextBox: false, hintText: '', valueType: '', n_a: false },
              isStepHeader: true
            });
          }
          
          if (itemListStr) {
            
            // Extract all checkbox items from this step's itemList
            const checkboxPattern = /\{'title':\s*'([^']+)',\s*'value':\s*(true|false)\}/g;
            let checkboxMatch;
            
            while ((checkboxMatch = checkboxPattern.exec(itemListStr)) !== null) {
              const checkboxTitle = checkboxMatch[1];
              const checkboxValue = checkboxMatch[2] === 'true';
              
              items.push({
                title: checkboxTitle,
                  options: [],
                  value: '',
                hasCheckbox: true,
                checkboxValue: checkboxValue,
                textField: { isTextBox: false, hintText: '', valueType: '', n_a: false }
              });
            }
          }
          
          // Extract textBox from this step if isTextBoxShow is true
          if (hasTextBox) {
            const textBoxStr = stepTextBoxMatch[0];
            
            const hintTextMatch = textBoxStr.match(/'hintText':\s*'([^']+)'/);
            const hintText = hintTextMatch ? hintTextMatch[1] : '';
            
            const valueMatch = textBoxStr.match(/'value':\s*'([^']*)'/);
            const textBoxValue = valueMatch ? valueMatch[1] : '';
            
            items.push({
              title: hintText,
                    options: [],
                    value: '',
                    hasCheckbox: false,
              stepHeader: step.header, // Store the step header
              stepName: step.stepName, // Store the step name
                    textField: { 
                      isTextBox: true, 
                hintText: hintText, 
                valueType: textBoxValue, 
                      n_a: false,
                value: textBoxValue
                    }
                  });
                }
        });
        
      }
      
      return items;
    } catch (error) {
      return [];
    }
  };

  // Function to convert old format to new format
  const convertOldFormatToNew = (rec) => {
    let mcq = [];
    
    // Parse MCQ Questions
    let mcqQuestionsItems = [];
    try {
      if (rec.mcqQuestions) {
        const mcqData = parseSingleQuotedJSON(rec.mcqQuestions) || [];
        mcqQuestionsItems = mcqData.map(item => ({
          title: item.text || item.title || 'Question',
          options: item.options || [],
          value: item.value || '',
          hasCheckbox: false,
          textField: { isTextBox: false, hintText: '', valueType: '', n_a: false }
        }));
      }
    } catch (e) {
      mcqQuestionsItems = [];
    }

    // Parse Steps List using the new simplified function
    let stepsListItems = [];
    try {
      if (rec.stepsList) {
        stepsListItems = parseStepsListData(rec.stepsList);
      }
    } catch (e) {
      stepsListItems = [];
    }

    // Parse Safe Check List
    let safeCheckListItems = [];
    try {
      if (rec.safeCheckList) {
          const safeData = parseSingleQuotedJSON(rec.safeCheckList) || { items: [] };
        if (safeData.items && Array.isArray(safeData.items)) {
          safeCheckListItems = safeData.items.map(item => ({
            title: item.title || '',
              img: item.img || '',
            options: ['YES', 'NO', 'N/A'],
            value: '',
            hasCheckbox: true,
            checkboxValue: item.value || false,
            textField: { isTextBox: false, hintText: '', valueType: '', n_a: false }
          }));
        }
          
          // Add text boxes if present - dynamically from API keys
          Object.keys(safeData).forEach(key => {
            if (key !== 'header' && key !== 'items' && key !== 'isOptional' && safeData[key] && safeData[key].textBox && safeData[key].textBox.isTextBoxShow) {
              safeCheckListItems.push({
                title: key, // Use the API key as title
                options: [],
                value: '',
                hasCheckbox: false,
                textField: { 
                  isTextBox: true, 
                  hintText: safeData[key].textBox.hintText || '', 
                  valueType: safeData[key].textBox.value || '', 
                  n_a: false,
                  value: safeData[key].textBox.value || ''
                }
              });
            }
          });
      }
    } catch (e) {
      safeCheckListItems = [];
    }

    // Parse MCQ Questions HSS
    let hssItems = [];
    try {
      if (rec.mcqQuestionsHss) {
          const hssData = parseSingleQuotedJSON(rec.mcqQuestionsHss) || { header: 'HSS Question', options: [], selected: '', reasonTextBox: {} };
        hssItems = [{
          title: hssData.header || 'HSS Question',
          options: hssData.options || [],
          value: hssData.selected || '',
          hasCheckbox: false,
          textField: { 
            isTextBox: hssData.reasonTextBox && hssData.reasonTextBox.isTextBoxShow || false, 
            hintText: hssData.reasonTextBox && hssData.reasonTextBox.hintText || '', 
            valueType: hssData.reasonTextBox && hssData.reasonTextBox.value || '', 
            n_a: false,
            value: hssData.reasonTextBox && hssData.reasonTextBox.value || ''
          }
        }];
      }
    } catch (e) {
      hssItems = [];
    }

    mcq = [
      { 
        header: 'MCQ Questions', 
        checkboxOptions: [], 
        checkboxValues: {}, 
        items: mcqQuestionsItems
      },
      { 
        header: 'Steps List', 
        checkboxOptions: [], 
        checkboxValues: {}, 
        items: stepsListItems,
        mainTitle: 'Steps List'
      },
      { 
        header: 'The Safe 6', 
        checkboxOptions: [], 
        checkboxValues: {}, 
        items: safeCheckListItems
      },
      { 
        header: 'MCQ Questions HSS', 
        checkboxOptions: [], 
        checkboxValues: {}, 
        items: hssItems
      }
    ];

    return mcq;
  };

  // Helper function to clean display values (remove brackets)
  const cleanDisplayValue = (value) => {
    if (!value) return '';
    if (typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) {
      return value.slice(1, -1);
    }
    return value;
  };

  // Helper function to convert date format from DD/MM/YYYY to YYYY-MM-DD for HTML date input
  const convertDateForInput = (dateString) => {
    if (!dateString) return '';
    // Check if it's already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // Convert from DD/MM/YYYY to YYYY-MM-DD
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month}-${day}`;
    }
    return dateString;
  };

  // Helper function to convert date format from YYYY-MM-DD to DD/MM/YYYY for saving
  const convertDateForSave = (dateString) => {
    if (!dateString) return '';
    // Check if it's already in DD/MM/YYYY format
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      return dateString;
    }
    // Convert from YYYY-MM-DD to DD/MM/YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    return dateString;
  };

  // Step 4 handlers
  const handleStep4NameChange = (nameIdx, value, isEdit = false) => {
    const setRecord = isEdit ? setEditRecord : setNewRecord;
    
    setRecord(prev => ({
      ...prev,
      step4Data: {
        ...prev.step4Data,
        name: prev.step4Data?.name?.map((name, idx) => idx === nameIdx ? value : name) || [value]
      }
    }));
  };

  const handleStep4AddName = (isEdit = false) => {
    const setRecord = isEdit ? setEditRecord : setNewRecord;
    
    setRecord(prev => ({
      ...prev,
      step4Data: {
        ...prev.step4Data,
        name: [...(prev.step4Data?.name || ['']), ''],
        signature: [...(prev.step4Data?.signature || ['']), '']
      }
    }));
  };

  // Assessment Review handlers
  const handleAssessmentReviewTimeChange = (timeIdx, value, isEdit = false) => {
    const setRecord = isEdit ? setEditRecord : setNewRecord;
    setRecord(prev => {
      const currentTime = prev.assessmentReviewData?.time || [''];
      const currentSssbReissue = prev.assessmentReviewData?.sssbReissue || [''];
      
      // Ensure both arrays are long enough and synchronized
      const newTime = [...currentTime];
      const newSssbReissue = [...currentSssbReissue];
      
      while (newTime.length <= timeIdx) {
        newTime.push('');
      }
      while (newSssbReissue.length <= timeIdx) {
        newSssbReissue.push('');
      }
      
      newTime[timeIdx] = value;
      
      return {
        ...prev,
        assessmentReviewData: {
          ...prev.assessmentReviewData,
          time: newTime,
          sssbReissue: newSssbReissue
        }
      };
    });
  };

  const handleAssessmentReviewSssbReissueChange = (sssbIdx, value, isEdit = false) => {
    const setRecord = isEdit ? setEditRecord : setNewRecord;
    setRecord(prev => {
      const currentSssbReissue = prev.assessmentReviewData?.sssbReissue || [''];
      // Ensure the array is long enough
      const newSssbReissue = [...currentSssbReissue];
      while (newSssbReissue.length <= sssbIdx) {
        newSssbReissue.push('');
      }
      newSssbReissue[sssbIdx] = value;
      
      return {
        ...prev,
        assessmentReviewData: {
          ...prev.assessmentReviewData,
          sssbReissue: newSssbReissue
        }
      };
    });
  };

  const handleAssessmentReviewAddTime = (isEdit = false) => {
    const setRecord = isEdit ? setEditRecord : setNewRecord;
    setRecord(prev => ({
      ...prev,
      assessmentReviewData: {
        ...prev.assessmentReviewData,
        time: [...(prev.assessmentReviewData?.time || ['']), ''],
        sssbReissue: [...(prev.assessmentReviewData?.sssbReissue || ['']), '']
      }
    }));
  };

  const handleAssessmentReviewRemoveTime = (isEdit = false) => {
    const setRecord = isEdit ? setEditRecord : setNewRecord;
    setRecord(prev => ({
      ...prev,
      assessmentReviewData: {
        ...prev.assessmentReviewData,
        time: prev.assessmentReviewData?.time?.filter((_, idx) => idx !== prev.assessmentReviewData.time.length - 1) || [''],
        sssbReissue: prev.assessmentReviewData?.sssbReissue?.filter((_, idx) => idx !== prev.assessmentReviewData.sssbReissue.length - 1) || ['']
      }
    }));
  };

  const handleAssessmentReviewAddSssbReissue = (isEdit = false) => {
    const setRecord = isEdit ? setEditRecord : setNewRecord;
    setRecord(prev => ({
      ...prev,
      assessmentReviewData: {
        ...prev.assessmentReviewData,
        sssbReissue: [...(prev.assessmentReviewData?.sssbReissue || ['']), '']
      }
    }));
  };

  const handleAssessmentReviewRemoveSssbReissue = (isEdit = false) => {
    const setRecord = isEdit ? setEditRecord : setNewRecord;
    setRecord(prev => ({
      ...prev,
      assessmentReviewData: {
        ...prev.assessmentReviewData,
        sssbReissue: prev.assessmentReviewData?.sssbReissue?.filter((_, idx) => idx !== prev.assessmentReviewData.sssbReissue.length - 1) || ['']
      }
    }));
  };

  const handleStep4RemoveName = (isEdit = false) => {
    const setRecord = isEdit ? setEditRecord : setNewRecord;
    
    setRecord(prev => ({
      ...prev,
      step4Data: {
        ...prev.step4Data,
        name: prev.step4Data?.name?.filter((_, idx) => idx !== prev.step4Data.name.length - 1) || [''],
        signature: prev.step4Data?.signature?.filter((_, idx) => idx !== prev.step4Data.signature.length - 1) || ['']
      }
    }));
  };

  const handleStep4ClearSignature = (nameIdx, isEdit = false) => {
    const setRecord = isEdit ? setEditRecord : setNewRecord;
    
  // Clear the canvas if we have a ref
  const record = isEdit ? editRecord : newRecord;
  const canvasRef = record?.step4SignatureRefs?.[nameIdx];
  if (canvasRef && canvasRef.clear) {
    canvasRef.clear();
  }

  setRecord(prev => ({
      ...prev,
      step4Data: {
        ...prev.step4Data,
        signature: prev.step4Data?.signature?.map((sig, idx) => idx === nameIdx ? '' : sig) || ['']
      }
    }));
  };

  const handleStep4SignatureEnd = (nameIdx, isEdit = false) => {
    const setRecord = isEdit ? setEditRecord : setNewRecord;
    const record = isEdit ? editRecord : newRecord;
    
  // Get the signature canvas ref - using same logic as Site Register
  const canvasRef = record.step4SignatureRefs?.[nameIdx];
  if (canvasRef && !canvasRef.isEmpty()) {
    // Use getCanvas() instead of getTrimmedCanvas() to maintain proper size (same as Site Register)
    const canvas = canvasRef.getCanvas();
      const signatureData = canvas.toDataURL("image/png");
      
      setRecord(prev => ({
        ...prev,
        step4Data: {
          ...prev.step4Data,
          signature: prev.step4Data?.signature?.map((sig, idx) => idx === nameIdx ? signatureData : sig) || [signatureData]
        }
      }));
    }
  };

  // Ensure existing signatures are drawn into canvases after data loads (robust load like Site Register)
  useEffect(() => {
    if (!editRecord || !editRecord.step4Data || !Array.isArray(editRecord.step4Data.signature)) return;
    const refs = editRecord.step4SignatureRefs || [];
    editRecord.step4Data.signature.forEach((sig, idx) => {
      if (!sig) return;
      const canvasRef = refs[idx];
      if (!canvasRef || !canvasRef.getCanvas) return;
      let src = String(sig);
      if (!src.startsWith('data:image')) {
        src = 'data:image/png;base64,' + src;
      }
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = canvasRef.getCanvas();
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          const canvasAspect = canvas.width / canvas.height;
          const imgAspect = img.width / img.height;
          let drawWidth, drawHeight, drawX, drawY;
          if (imgAspect > canvasAspect) {
            drawWidth = canvas.width;
            drawHeight = canvas.width / imgAspect;
            drawX = 0;
            drawY = (canvas.height - drawHeight) / 2;
          } else {
            drawHeight = canvas.height;
            drawWidth = canvas.height * imgAspect;
            drawX = (canvas.width - drawWidth) / 2;
            drawY = 0;
          }
          ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
        } catch (e) {
          console.error('Failed to draw existing step4 signature', e);
        }
      };
      img.onerror = () => console.error('Failed to load existing step4 signature image');
      img.src = src;
    });
  }, [editRecord && editRecord.step4Data && editRecord.step4Data.signature]);

  // Search logic
  const filtered = records.filter(r => {
    const fields = [
      r.location || r.locationDescription, 
      r.descriptionOfWorks,
      r.jobNumber
    ].map(val => (val || '').toLowerCase());
    const searchWords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (searchWords.length === 0) return true;
    return searchWords.every(word => fields.some(field => field.includes(word)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().slice((page-1)*pageSize, page*pageSize);
  const formatDateForDisplay = (d) => {
    if (!d) return "—";
    const x = new Date(d);
    return isNaN(x.getTime()) ? d : x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };
  
  // Reset page to 1 if current page is beyond available pages
  if (filtered.length === 0 && page > 1) {
    setPage(1);
  }

  // Add Record
  const handleAdd = async () => {
    if (!newRecord.userId || !newRecord.locationDescription || !newRecord.date || !newRecord.jobNumber || !newRecord.personInCharge) {
      toast.error("All required fields must be filled");
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      
      const mcqForSave = (newRecord.mcqQuestionsList || []).map(sec => {
        // Aggregate checkbox values from individual items
        const aggregatedCheckboxValues = {};
        (sec.items || []).forEach(item => {
          if (item.hasCheckbox && item.title) {
            aggregatedCheckboxValues[item.title] = item.checkboxValue || false;
          }
        });
        
        return {
        ...sec,
          // Include aggregated checkbox values in the saved data
          checkboxValues: { ...(sec.checkboxValues || {}), ...aggregatedCheckboxValues },
        items: (sec.items || []).map(it => {
          const tf = it.textField || {};
          const savedTF = { ...tf };
          if ('na' in savedTF) { savedTF['n/a'] = !!savedTF.na; delete savedTF.na; }
          
          // Include textFieldValue in the saved data as valueType
          if (it.textFieldValue !== undefined) {
            savedTF.valueType = it.textFieldValue;
          }
          
          // Handle checkbox values
          let value = it.value;
          if (it.hasCheckbox) {
            value = it.checkboxValue ? 'YES' : 'NO';
          } else if (it.booleanValue !== undefined) {
            value = it.booleanValue === true ? 'YES' : it.booleanValue === false ? 'NO' : it.value;
          }
          
          return {
            ...it,
            value: value,
            textField: savedTF
          };
        })
        };
      });
      
      // Convert to string format matching the original API structure
      const mcqQuestionsString = mcqForSave.find(sec => sec.header === 'MCQ Questions')?.items?.map(item => {
        const text = item.title || '';
        const options = item.options || [];
        const value = item.value || '';
        const isOptional = false; // Default value
        return `{'text':'${text.replace(/'/g, "\\'")}','options':[${options.map(opt => `'${opt}'`).join(',')}],'value':'${value}','isOptional':${isOptional}}`;
      }).join(',') || '';
      
      // Generate stepsListString using the new dynamic function
      const stepsListString = generateStepsListString(mcqForSave);
      
      // Generate safeCheckListString - should be a single object, not an array
      const safeCheckListSection = mcqForSave.find(sec => sec.header === 'The Safe 6');
      let safeCheckListString = '';
      if (safeCheckListSection) {
        const checkboxValues = safeCheckListSection.checkboxValues || {};
        
        // Get the items from the section (these are the PPE items)
        const itemsWithValues = safeCheckListSection.items?.map(item => ({
          ...item,
          value: item.checkboxValue || false
        })) || [];
        
        // Parse the original safeCheckList to preserve all structure
        const originalSafeData = parseSingleQuotedJSON(newRecord.safeCheckList) || {};
        
        // Update only the values while preserving all other data
        const updatedSafeData = { ...originalSafeData };
        
        // Update items with new checkbox values
        if (updatedSafeData.items && Array.isArray(updatedSafeData.items)) {
          updatedSafeData.items = updatedSafeData.items.map(item => {
            const matchingUIItem = safeCheckListSection.items?.find(ui => ui.title === item.title);
            const newValue = matchingUIItem ? matchingUIItem.checkboxValue : item.value;
            return {
              ...item,
              value: newValue
            };
          });
        }
        
        // Update text box values while preserving all other properties
        const textBoxItems = safeCheckListSection.items?.filter(item => item.textField?.isTextBox) || [];
        textBoxItems.forEach(item => {
          if (updatedSafeData[item.title] && updatedSafeData[item.title].textBox) {
            // Use the current textFieldValue if it exists (including empty string), otherwise fall back to original
            const currentValue = item.textFieldValue !== undefined ? item.textFieldValue : 
                                (item.textField?.value !== undefined ? item.textField.value : 
                                updatedSafeData[item.title].textBox.value);
            
            updatedSafeData[item.title] = {
              ...updatedSafeData[item.title],
              textBox: {
                ...updatedSafeData[item.title].textBox,
                value: currentValue
              }
            };
          }
        });
        
        safeCheckListString = JSON.stringify(updatedSafeData).replace(/"/g, "'");
      }
      
      const hssString = mcqForSave.find(sec => sec.header === 'MCQ Questions HSS')?.items?.map(item => {
        // Convert HSS to the expected format
        return `{'header':'${item.title || ''}','options':${JSON.stringify(item.options || [])},'selected':'${item.value || ''}','reasonTextBox':{'isTextBoxShow':${item.textField?.isTextBox || false},'hintText':'${item.textField?.hintText || ''}','valuetype':'string','isRequired':false,'value':'${item.textField?.valueType || ''}'}}`;
      }).join(',') || '';
      
      // Use the reconstructed strings with updated checkbox values
      let updatedStepsList = `[${stepsListString}]`;
      let updatedSafeCheckList = safeCheckListString;
      
      // Get user details for saving firstName and lastName
      const user = appUsers.find(u => u._id === newRecord.userId);
      const addedByFirstName = user ? user.firstName : '';
      const addedByLastName = user ? user.lastName : '';
      
      // Convert date to original format for saving
      const recordToSave = {
        ...newRecord,
        formUuid: ensureFormUuid(newRecord.formUuid),
        addedByFirstName,
        addedByLastName,
        date: convertDateForSave(newRecord.date),
        mcqQuestions: `[${mcqQuestionsString}]`,
        stepsList: updatedStepsList,
        safeCheckList: updatedSafeCheckList,
        mcqQuestionsHss: hssString,
        // Save the complete MCQ structure as mcqQuestionsList for UI state
        mcqQuestionsList: JSON.stringify(mcqForSave),
        // Save step4 data
        step4: JSON.stringify(newRecord.step4Data)
      };
      
      
      const res = await fetch(baseUrl + "/api/pow-assessment", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(recordToSave)
      });
      
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error('Add API Error Response:', errorText);
        toast.error(`Failed to create record: ${res.status} ${res.statusText}`);
        return;
      }
      
      const data = await res.json();
      if (res.ok && data.success && noopCreateSuccess(data)) {
        toast.warn(data.message || "This record already exists. No new record was added.");
        return;
      }
      if (res.ok && data.success) {
        setRecords([...records, data.data]);
        setShowAddModal(false);
        setNewRecord({
          formUuid: "",
          userId: "",
          mcqQuestionsList: [],
          locationDescription: "",
          date: "",
          jobNumber: "",
          personInCharge: "",
          recodedOfMultiSiteVisit: "",
          step4: "",
          assessmentReview: "",
          step4Data: {
            header: "",
            descriptions: "",
            name: [''],
            nameHint: "",
            signature: ['']
          }
        });
        toast.success("Record added!");
      } else {
        toast.error(data.message || "Failed to add");
      }
    } catch {
      toast.error("Failed to add");
    }
  };


  // Helper function to reorder sections to match PDF sequence
  const reorderSectionsForPDF = (sections) => {
    if (!Array.isArray(sections)) return sections;
    
    // Define the order based on PDF sequence (matching exact PDF section order)
    const orderMap = {
      'mcqDetailedPreWorkList': 1,      // Section 1: Location & Description
      'mcqControlsPreWorkList': 2,       // Section 1: Location & Description
      'checklist': 3,                    // Section 2: Identify Site Hazards
      'detailsofEmergencyProcedures': 4, // Section 3: Details of Emergency Procedures
      'safeCheckList': 5,                // Section 4: PPE tick as appropriate
      'personInChargeofSafety': 6,      // Section 6: Signatories
      'PersonTakingOverAsPICOS': 7,      // Section 6: Signatories
      'nameUkpnCompany': 8,              // Section 6: Signatories
      'recordofAssessmentReview': 9,     // Section 7: Record of assessment review
      'stateReasonForReviewRequest': 10, // Section 8: Further Attention Required
      'mcqQuestionsHss': 11,             // Section 8: Further Attention Required (reviewer details)
      'mcqQuestions': 12,
      'Steps List': 13
    };
    
    // Helper function to get section order
    const getSectionOrder = (section) => {
      // First try by header
      if (orderMap[section.header] !== undefined) {
        return orderMap[section.header];
      }
      
      // Handle stateReasonForReviewRequest which has empty header but specific item title
      if (!section.header && section.items && section.items.length > 0) {
        const firstItemTitle = section.items[0]?.title || '';
        if (firstItemTitle === 'State reason for review request') {
          return orderMap['stateReasonForReviewRequest'] || 999;
        }
      }
      
      return 999;
    };
    
    // Sort sections based on order
    return [...sections].sort((a, b) => {
      const orderA = getSectionOrder(a);
      const orderB = getSectionOrder(b);
      return orderA - orderB;
    });
  };

  // Helper function to get image source from path
  const getImageSrcFromPath = (imgPath) => {
    if (!imgPath) return null;
    
    // If it's already a data URL or base64, return as is
    if (typeof imgPath === 'string' && (imgPath.startsWith('data:image') || imgPath.startsWith('http'))) {
      return imgPath;
    }
    
    // Try to get image from context
    try {
      const filename = imgPath.split('/').pop();
      const imageKey = iconsContext.keys().find(key => {
        const keyFilename = key.split('/').pop().toLowerCase();
        return keyFilename === filename.toLowerCase();
      });
      
      if (imageKey) {
        return iconsContext(imageKey);
      }
    } catch (error) {
      console.warn('Failed to load image from path:', imgPath, error);
    }
    
    // Fallback to getImageSrc function
    return getImageSrc(imgPath);
  };

  // Edit Record - Updated to handle actual API response structure
  const handleEdit = (rec) => {
    let mcq = [];
    
    // Check if record has the new mcqQuestionsList field
    if (rec.mcqQuestionsList) {
      try {
        let str = rec.mcqQuestionsList;
        if (typeof str === 'string') {
          let s = str.trim();
          try {
            mcq = JSON.parse(s);
          } catch {
            // robust conversion for non-JSON-like strings
            // 1) Quote unquoted keys (allowing / - _ ( ) in names)
            s = s.replace(/([,{]\s*)([A-Za-z0-9_\/\-\(\)]+)(\s*:)/g, '$1"$2"$3');
            // 2) Convert single-quoted strings to double-quoted safely
            s = s.replace(/'((?:\\'|[^'])*)'/g, (m, inner) => {
              const unescaped = inner.replace(/\\'/g, "'");
              const escapedDouble = unescaped.replace(/"/g, '\\"');
              return '"' + escapedDouble + '"';
            });
            // 3) Fix booleans in known places
            s = s.replace(/(\"value\"\s*:\s*)\"true\"/g, '$1true');
            s = s.replace(/(\"value\"\s*:\s*)\"false\"/g, '$1false');
            // 4) Remove trailing commas
            s = s.replace(/,\s*([}\]])/g, '$1');
            // 5) Fix the specific format issue with missing quotes around property names
            s = s.replace(/([A-Za-z0-9_\/\-\(\)]+):/g, '"$1":');
            // 6) Fix the specific issue with "n/a" property
            s = s.replace(/"n\/a":/g, '"n/a":');
            try {
              mcq = JSON.parse(s);
            } catch (parseError) {
              // If still fails, try to manually parse the structure
              try {
                mcq = parseCustomMcqFormat(str);
              } catch (customError) {
                mcq = [];
              }
            }
          }
        } else if (Array.isArray(str)) {
          mcq = str;
        }
      } catch {
        mcq = [];
      }
    } else {
      // Handle old format - convert old text fields to new structured format
      
      // Parse MCQ Questions - check for mcqDetailedPreWorkList and mcqControlsPreWorkList
      // Note: These will be added as separate sections with their field names as headers

      // Parse Steps List - check for checklist field
      let stepsListItems = [];
      try {
        if (rec.checklist) {
          stepsListItems = parseStepsListData(rec.checklist);
        } else if (rec.stepsList) {
          stepsListItems = parseStepsListData(rec.stepsList);
        }
      } catch (e) {
        stepsListItems = [];
      }

      // Parse Safe Check List - preserve structure and sequence to match PDF
      let safeCheckListItems = [];
      try {
        if (rec.safeCheckList) {
          let safeData;
          try {
            const safeStr = typeof rec.safeCheckList === 'string' ? rec.safeCheckList : JSON.stringify(rec.safeCheckList);
            safeData = JSON.parse(safeStr);
          } catch (e) {
            safeData = parseSingleQuotedJSON(rec.safeCheckList) || { items: [] };
          }
          
          // Process items in exact order as they appear in API to match PDF sequence
          if (safeData.items && Array.isArray(safeData.items)) {
            safeData.items.forEach((item, itemIndex) => {
              // Process checkboxes first (in order) - these appear as sub-header and data rows in PDF
              if (item.checkBox && item.checkBox.isCheckBoxShow && item.checkBox.itemList && Array.isArray(item.checkBox.itemList)) {
                item.checkBox.itemList.forEach((checkboxItem, checkboxIndex) => {
                  safeCheckListItems.push({
                    title: checkboxItem.title || '',
                    img: item.img || '',
                    options: [],
                    value: '',
                    hasCheckbox: true,
                    checkboxValue: checkboxItem.value || false,
                    textField: { isTextBox: false, hintText: '', valueType: '', n_a: false },
                    // Store original item index and checkbox index for proper grouping
                    originalItemIndex: itemIndex,
                    checkboxIndex: checkboxIndex,
                    // Mark first checkbox of each item as main title for display
                    isMainTitle: checkboxIndex === 0
                  });
                });
              }
              
              // Add text box for "Other PPE required" after checkboxes for this item
              if (item.textBox && item.textBox.isTextBoxShow) {
                safeCheckListItems.push({
                  title: item.textBox.hintText || 'Other PPE required',
                  img: item.img || '',
                  options: [],
                  value: '',
                  hasCheckbox: false,
                  checkboxValue: false,
                  textField: { 
                    isTextBox: true, 
                    hintText: item.textBox.hintText || 'Other PPE required', 
                    valueType: item.textBox.valuetype || 'string', 
                    n_a: false,
                    value: item.textBox.value || ''
                  },
                  originalItemIndex: itemIndex
                });
              }
            });
          }
          
          // Add detailPpeRequired text box at the end (appears as justification row in PDF)
          if (safeData.detailPpeRequired && safeData.detailPpeRequired.textBox && safeData.detailPpeRequired.textBox.isTextBoxShow) {
            safeCheckListItems.push({
              title: safeData.detailPpeRequired.textBox.hintText || 'Justification if Safe 6 item not worn and reason if PPE only for specific tasks',
              options: [],
              value: '',
              hasCheckbox: false,
              textField: { 
                isTextBox: true, 
                hintText: safeData.detailPpeRequired.textBox.hintText || 'Justification if Safe 6 item not worn and reason if PPE only for specific tasks', 
                valueType: safeData.detailPpeRequired.textBox.valuetype || 'string', 
                n_a: false,
                value: safeData.detailPpeRequired.textBox.value || ''
              }
            });
          }
        }
      } catch (e) {
        console.error('Error parsing safeCheckList:', e);
        safeCheckListItems = [];
      }

      // Parse MCQ Questions HSS
      let hssItems = [];
      try {
        if (rec.mcqQuestionsHss) {
          let hssData;
          try {
            const hssStr = typeof rec.mcqQuestionsHss === 'string' ? rec.mcqQuestionsHss : JSON.stringify(rec.mcqQuestionsHss);
            hssData = JSON.parse(hssStr);
          } catch (e) {
            hssData = parseSingleQuotedJSON(rec.mcqQuestionsHss) || { header: 'HSS Question', options: [], selected: '', reasonTextBox: {} };
          }
          hssItems = [{
            title: hssData.header || 'HSS Question',
            options: hssData.options || [],
            value: hssData.selected || '',
            hasCheckbox: false,
            textField: { 
              isTextBox: hssData.reasonTextBox && hssData.reasonTextBox.isTextBoxShow || false, 
              hintText: hssData.reasonTextBox && hssData.reasonTextBox.hintText || '', 
              valueType: hssData.reasonTextBox && hssData.reasonTextBox.value || '', 
              n_a: false,
              value: hssData.reasonTextBox && hssData.reasonTextBox.value || ''
            }
          }];
        }
      } catch (e) {
        hssItems = [];
      }

      mcq = [];
      
      // Add mcqDetailedPreWorkList section if it exists
      if (rec.mcqDetailedPreWorkList) {
        try {
          const detailedStr = typeof rec.mcqDetailedPreWorkList === 'string' ? rec.mcqDetailedPreWorkList : JSON.stringify(rec.mcqDetailedPreWorkList);
          const detailedData = JSON.parse(detailedStr);
          if (Array.isArray(detailedData) && detailedData.length > 0) {
            const detailedItems = detailedData.map(item => ({
              title: item.text || item.title || 'Question',
              options: item.options || [],
              value: item.value || '',
              hasCheckbox: false,
              textField: { isTextBox: false, hintText: '', valueType: '', n_a: false }
            }));
            mcq.push({
              header: 'mcqDetailedPreWorkList',
              checkboxOptions: [],
              checkboxValues: {},
              items: detailedItems
            });
          }
        } catch (e) {
          console.error('Error parsing mcqDetailedPreWorkList:', e);
        }
      }
      
      // Add mcqControlsPreWorkList section if it exists
      if (rec.mcqControlsPreWorkList) {
        try {
          const controlsStr = typeof rec.mcqControlsPreWorkList === 'string' ? rec.mcqControlsPreWorkList : JSON.stringify(rec.mcqControlsPreWorkList);
          const controlsData = JSON.parse(controlsStr);
          if (Array.isArray(controlsData) && controlsData.length > 0) {
            const controlsItems = controlsData.map(item => ({
              title: item.text || item.title || 'Question',
              options: item.options || [],
              value: item.value || '',
              hasCheckbox: false,
              textField: { isTextBox: false, hintText: '', valueType: '', n_a: false }
            }));
            mcq.push({
              header: 'mcqControlsPreWorkList',
              checkboxOptions: [],
              checkboxValues: {},
              items: controlsItems
            });
          }
        } catch (e) {
          console.error('Error parsing mcqControlsPreWorkList:', e);
        }
      }
      
      // Fallback to old mcqQuestions field if no new fields exist
      if (mcq.length === 0 && rec.mcqQuestions) {
        try {
          const mcqData = parseSingleQuotedJSON(rec.mcqQuestions) || [];
          const mcqQuestionsItems = mcqData.map(item => ({
            title: item.text || item.title || 'Question',
            options: item.options || [],
            value: item.value || '',
            hasCheckbox: false,
            textField: { isTextBox: false, hintText: '', valueType: '', n_a: false }
          }));
          if (mcqQuestionsItems.length > 0) {
            mcq.push({
              header: 'mcqQuestions',
          checkboxOptions: [], 
          checkboxValues: {}, 
          items: mcqQuestionsItems
            });
          }
        } catch (e) {}
      }
      
      // Add checklist section if it exists
      if (rec.checklist && stepsListItems.length > 0) {
        mcq.push({
          header: 'checklist',
          checkboxOptions: [], 
          checkboxValues: {}, 
          items: stepsListItems
        });
      }
      
      // Add safeCheckList section if it exists
      if (rec.safeCheckList && safeCheckListItems.length > 0) {
        mcq.push({
          header: 'safeCheckList',
          checkboxOptions: [], 
          checkboxValues: {}, 
          items: safeCheckListItems
        });
      }
      
      // Add mcqQuestionsHss section if it exists
      if (rec.mcqQuestionsHss && hssItems.length > 0) {
        mcq.push({
          header: 'mcqQuestionsHss',
          checkboxOptions: [], 
          checkboxValues: {}, 
          items: hssItems
        });
      }
      
      // Parse detailsofEmergencyProcedures
      if (rec.detailsofEmergencyProcedures) {
        try {
          const emergencyStr = typeof rec.detailsofEmergencyProcedures === 'string' ? rec.detailsofEmergencyProcedures : JSON.stringify(rec.detailsofEmergencyProcedures);
          const emergencyData = JSON.parse(emergencyStr);
          if (emergencyData.items && Array.isArray(emergencyData.items)) {
            const emergencyItems = emergencyData.items.map(item => {
              // Handle multipleTextBox (What 3 Words)
              if (item.multipleTextBox && item.multipleTextBox.isMultipleTextBoxShow) {
                return {
                  title: item.title || '',
                  options: [],
                  value: '',
                  hasCheckbox: false,
                  textField: {
                    isTextBox: true,
                    hintText: item.multipleTextBox.textFields?.map(tf => tf.hintText).join(', ') || '',
                    valueType: item.multipleTextBox.textFields?.map(tf => tf.value).join(', ') || '',
                    n_a: false,
                    value: item.multipleTextBox.textFields?.map(tf => tf.value).join(', ') || ''
                  }
                };
              }
              // Handle textBox
              if (item.textBox && item.textBox.isTextBoxShow) {
                return {
                  title: item.title || '',
                  options: [],
                  value: '',
                  hasCheckbox: false,
                  textField: {
                    isTextBox: true,
                    hintText: item.textBox.hintText || '',
                    valueType: item.textBox.value || '',
                    n_a: false,
                    value: item.textBox.value || ''
                  }
                };
              }
              // Handle radioBox
              if (item.radioBox && item.radioBox.isRadioBoxShow) {
                return {
                  title: item.title || '',
                  options: item.radioBox.itemList?.map(opt => opt.value) || [],
                  value: item.radioBox.selectedValue || '',
                  hasCheckbox: false,
                  textField: { isTextBox: false, hintText: '', valueType: '', n_a: false }
                };
              }
              return null;
            }).filter(Boolean);
            
            if (emergencyItems.length > 0) {
              mcq.push({
                header: 'detailsofEmergencyProcedures',
                descriptiveHeader: emergencyData.header || 'Details of Emergency Procedures',
                checkboxOptions: [],
                checkboxValues: {},
                items: emergencyItems
              });
            }
          }
        } catch (e) {
          console.error('Error parsing detailsofEmergencyProcedures:', e);
        }
      }
      
      // Parse personInChargeofSafety - Store as table data
      if (rec.personInChargeofSafety) {
        try {
          const picosStr = typeof rec.personInChargeofSafety === 'string' ? rec.personInChargeofSafety : JSON.stringify(rec.personInChargeofSafety);
          const picosData = JSON.parse(picosStr);
          
          // Helper function to parse string arrays like "[12:07]" or "[name1,name2]"
          const parseStringArray = (str) => {
            if (!str) return [];
            if (Array.isArray(str)) return str;
            if (typeof str === 'string') {
              try {
                const parsed = JSON.parse(str);
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                const cleaned = str.replace(/^\[|\]$/g, '');
                return cleaned.split(',').map(s => s.trim()).filter(Boolean);
              }
            }
            return [];
          };
          
          const names = parseStringArray(picosData.name);
          const times = parseStringArray(picosData.time);
          const dates = parseStringArray(picosData.date);
          const signatures = parseStringArray(picosData.signature);
          
          // Create table rows - combine data by index
          const maxLength = Math.max(names.length, times.length, dates.length, signatures.length);
          const tableRows = [];
          for (let i = 0; i < maxLength; i++) {
            tableRows.push({
              name: names[i] || '',
              time: times[i] || '',
              date: dates[i] || '',
              signature: signatures[i] || ''
            });
          }
          
          if (tableRows.length > 0 || picosData.header) {
            mcq.push({
              header: 'personInChargeofSafety',
              checkboxOptions: [],
              checkboxValues: {},
              items: [],
              isTable: true,
              tableHeader: picosData.header || 'Person in Charge of Safety (PICOS)',
              tableData: tableRows,
              nameHint: picosData.nameHint || 'Name',
              timeHint: picosData.timeHint || 'Time',
              dateHint: picosData.dateHint || 'Date'
            });
          }
        } catch (e) {
          console.error('Error parsing personInChargeofSafety:', e);
        }
      }
      
      // Parse PersonTakingOverAsPICOS - Store as table data
      if (rec.PersonTakingOverAsPICOS) {
        try {
          const takingOverStr = typeof rec.PersonTakingOverAsPICOS === 'string' ? rec.PersonTakingOverAsPICOS : JSON.stringify(rec.PersonTakingOverAsPICOS);
          const takingOverData = JSON.parse(takingOverStr);
          
          // Helper function to parse string arrays
          const parseStringArray = (str) => {
            if (!str) return [];
            if (Array.isArray(str)) return str;
            if (typeof str === 'string') {
              try {
                const parsed = JSON.parse(str);
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                const cleaned = str.replace(/^\[|\]$/g, '');
                return cleaned.split(',').map(s => s.trim()).filter(Boolean);
              }
            }
            return [];
          };
          
          const names = parseStringArray(takingOverData.name);
          const times = parseStringArray(takingOverData.time);
          const dates = parseStringArray(takingOverData.date);
          const signatures = parseStringArray(takingOverData.signature);
          
          // Create table rows - combine data by index
          const maxLength = Math.max(names.length, times.length, dates.length, signatures.length);
          const tableRows = [];
          for (let i = 0; i < maxLength; i++) {
            tableRows.push({
              name: names[i] || '',
              time: times[i] || '',
              date: dates[i] || '',
              signature: signatures[i] || ''
            });
          }
          
          if (tableRows.length > 0 || takingOverData.header) {
            mcq.push({
              header: 'PersonTakingOverAsPICOS',
              checkboxOptions: [],
              checkboxValues: {},
              items: [],
              isTable: true,
              tableHeader: takingOverData.header || 'Person taking over as PICOS',
              tableData: tableRows,
              nameHint: takingOverData.nameHint || 'Name',
              timeHint: takingOverData.timeHint || 'Time',
              dateHint: takingOverData.dateHint || 'Date'
            });
          }
        } catch (e) {
          console.error('Error parsing PersonTakingOverAsPICOS:', e);
        }
      }
      
      // Parse nameUkpnCompany - Store as table data
      if (rec.nameUkpnCompany) {
        try {
          const nameUkpnStr = typeof rec.nameUkpnCompany === 'string' ? rec.nameUkpnCompany : JSON.stringify(rec.nameUkpnCompany);
          const nameUkpnData = JSON.parse(nameUkpnStr);
          
          // Helper function to parse string arrays
          const parseStringArray = (str) => {
            if (!str) return [];
            if (Array.isArray(str)) return str;
            if (typeof str === 'string') {
              try {
                const parsed = JSON.parse(str);
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                const cleaned = str.replace(/^\[|\]$/g, '');
                return cleaned.split(',').map(s => s.trim()).filter(Boolean);
              }
            }
            return [];
          };
          
          const names = parseStringArray(nameUkpnData.name);
          const times = parseStringArray(nameUkpnData.time);
          const signatures = parseStringArray(nameUkpnData.signature);
          
          // Create table rows - combine data by index
          const maxLength = Math.max(names.length, times.length, signatures.length);
          const tableRows = [];
          for (let i = 0; i < maxLength; i++) {
            tableRows.push({
              name: names[i] || '',
              time: times[i] || '',
              signature: signatures[i] || ''
            });
          }
          
          if (tableRows.length > 0 || nameUkpnData.header) {
            mcq.push({
              header: 'nameUkpnCompany',
              checkboxOptions: [],
              checkboxValues: {},
              items: [],
              isTable: true,
              tableHeader: nameUkpnData.header || 'Name (and company if not UKPN)',
              tableData: tableRows,
              nameHint: nameUkpnData.nameHint || 'Name',
              timeHint: nameUkpnData.timeHint || 'Time'
            });
          }
        } catch (e) {
          console.error('Error parsing nameUkpnCompany:', e);
        }
      }
      
      // Parse recordofAssessmentReview - Store as table data
      if (rec.recordofAssessmentReview) {
        try {
          const reviewStr = typeof rec.recordofAssessmentReview === 'string' ? rec.recordofAssessmentReview : JSON.stringify(rec.recordofAssessmentReview);
          const reviewData = JSON.parse(reviewStr);
          
          // Helper function to parse string arrays like "[12:07]" or "[jjk]"
          const parseStringArray = (str) => {
            if (!str) return [];
            if (Array.isArray(str)) return str;
            if (typeof str === 'string') {
              try {
                const parsed = JSON.parse(str);
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                const cleaned = str.replace(/^\[|\]$/g, '');
                return cleaned.split(',').map(s => s.trim()).filter(Boolean);
              }
            }
            return [];
          };
          
          const times = parseStringArray(reviewData.time);
          const sssbReissues = parseStringArray(reviewData.sssbReissue);
          const reasonsForReview = parseStringArray(reviewData.reasonforReview);

          // Create table rows - combine data by index
          const maxLength = Math.max(times.length, sssbReissues.length, reasonsForReview.length);
          const tableRows = [];
          for (let i = 0; i < maxLength; i++) {
            tableRows.push({
              time: times[i] || '',
              sssbReissue: sssbReissues[i] || '',
              reasonforReview: reasonsForReview[i] || ''
            });
          }
          
          if (tableRows.length > 0 || reviewData.header) {
            mcq.push({
              header: 'recordofAssessmentReview',
              checkboxOptions: [],
              checkboxValues: {},
              items: [],
              isTable: true,
              tableHeader: reviewData.header || 'Record of Assessment Review - if site hazards change (weather/working party changes)',
              tableData: tableRows,
              timeHint: reviewData.timeHint || 'Time',
              sssbReissueHint: reviewData.sssbReissueHint || 'POWA re-issued (PICOS to initial that working party briefed)',
              reasonforReviewHint: reviewData.reasonforReviewHint || 'Reason for review'
            });
          }
        } catch (e) {
          console.error('Error parsing recordofAssessmentReview:', e);
        }
      }
      
      // Parse stateReasonForReviewRequest
      if (rec.stateReasonForReviewRequest) {
        mcq.push({
          header: '',
          checkboxOptions: [],
          checkboxValues: {},
          items: [{
            title: 'State reason for review request',
            options: [],
            value: '',
            hasCheckbox: false,
            textField: {
              isTextBox: true,
              hintText: 'State reason for review request',
              valueType: rec.stateReasonForReviewRequest || '',
              n_a: false,
              value: rec.stateReasonForReviewRequest || ''
            }
          }]
        });
      }
    }
    
      // Parse step4 data
      let step4Data = { 
        header: "",
        descriptions: "",
        name: [''],
        nameHint: "",
        signature: [''] 
      };
      try {
        if (rec.step4) {
          let step4Parsed;
          
         // Handle both object and string formats
         if (typeof rec.step4 === 'string') {
           // Try strict JSON first (your latest payload is valid JSON string)
           try {
             const obj = JSON.parse(rec.step4);
             const normalizeToArray = (val) => {
               if (Array.isArray(val)) return val;
               if (typeof val === 'string') {
                 // Try to parse JSON-like array string (e.g. "[shweta , sunil]" or "[\"...\",\"...\"]")
                 try {
                   // Ensure quotes for simple name lists without quotes
                   const fixed = val.trim().startsWith('[') ? val : `[${val}]`;
                   // Replace single quotes with double quotes and wrap bare words in quotes
                   const quoted = fixed
                     .replace(/'/g, '"')
                     .replace(/\[(\s*[a-zA-Z0-9_\- ]+\s*)(,|\])/g, (m) => {
                       // Wrap bare words with quotes (fallback)
                       return m.replace(/([a-zA-Z0-9_\- ]+)/g, '"$1"');
                     });
                   const arr = JSON.parse(quoted);
                   return Array.isArray(arr) ? arr.map(v => (typeof v === 'string' ? v.trim() : v)) : [];
                 } catch {
                   // Fallback: split by comma
                   return val
                     .replace(/^\[/, '')
                     .replace(/\]$/, '')
                     .split(',')
                     .map(s => s.trim().replace(/^"|"$/g, ''))
                     .filter(Boolean);
                 }
               }
               return [];
             };
             step4Parsed = {
               header: obj.header || '',
               descriptions: obj.descriptions || '',
               name: normalizeToArray(obj.name),
               nameHint: obj.nameHint || '',
               signature: normalizeToArray(obj.signature)
             };
           } catch {
             // The string contains malformed JSON, let's parse it manually
             const step4Str = rec.step4;
             
             step4Parsed = {};
             
             // Parse the malformed JSON string manually
             try {
              // Extract header: "Step 4: COMMUNICATION & REVIEW"
              const headerMatch = step4Str.match(/header:([^,]+?)(?=,descriptions)/);
              if (headerMatch) {
                step4Parsed.header = headerMatch[1].trim();
              }
              
              // Extract descriptions: "We have participated & understood this assessment & received the Site-Specific Safety Briefing (SSSB)"
              const descriptionsMatch = step4Str.match(/descriptions:([^,]+?)(?=,name)/);
              if (descriptionsMatch) {
                step4Parsed.descriptions = descriptionsMatch[1].trim();
              }
              
              // Extract name: [sunil]
              const nameMatch = step4Str.match(/name:\[([^\]]+?)\]/);
              if (nameMatch) {
                const nameStr = nameMatch[1].trim();
                step4Parsed.name = nameStr ? [nameStr] : [''];
              }
              
              // Extract nameHint: "Name"
              const nameHintMatch = step4Str.match(/nameHint:([^,]+?)(?=,signature)/);
              if (nameHintMatch) {
                step4Parsed.nameHint = nameHintMatch[1].trim();
              }
              
              // Extract signature: [base64 string]
              const signatureMatch = step4Str.match(/signature:\[([^\]]+?)\]/);
              if (signatureMatch) {
                const sigStr = signatureMatch[1].trim();
                step4Parsed.signature = sigStr ? [sigStr] : [''];
              }
              
          } catch (e) {
              }
           }
          } else if (typeof rec.step4 === 'object') {
            // The object is malformed, let's parse it as a string manually
            const step4Str = rec.step4.toString();            
            step4Parsed = {};
            
            // Parse the malformed object string manually with more precise patterns
            try {
              // Extract header: "Step 4: COMMUNICATION & REVIEW"
              const headerMatch = step4Str.match(/header:([^,]+?)(?=,descriptions)/);
              if (headerMatch) {
                step4Parsed.header = headerMatch[1].trim();
              } else {
                // Try alternative pattern
                const headerMatch2 = step4Str.match(/header:([^,}]+)/);
                if (headerMatch2) {
                  step4Parsed.header = headerMatch2[1].trim();                }
              }
              
              // Extract descriptions: "We have participated & understood this assessment & received the Site-Specific Safety Briefing (SSSB)"
              const descriptionsMatch = step4Str.match(/descriptions:([^,]+?)(?=,name)/);
              if (descriptionsMatch) {
                step4Parsed.descriptions = descriptionsMatch[1].trim();
              } else {
                // Try alternative pattern
                const descriptionsMatch2 = step4Str.match(/descriptions:([^,}]+)/);
                if (descriptionsMatch2) {
                  step4Parsed.descriptions = descriptionsMatch2[1].trim();                }
              }
              
              // Extract name: [sunil]
              const nameMatch = step4Str.match(/name:\[([^\]]+?)\]/);
              if (nameMatch) {
                const nameStr = nameMatch[1].trim();
                step4Parsed.name = nameStr ? [nameStr] : [''];
              } else {
                // Try alternative pattern
                const nameMatch2 = step4Str.match(/name:\[([^\]]+)\]/);
                if (nameMatch2) {
                  const nameStr = nameMatch2[1].trim();
                  step4Parsed.name = nameStr ? [nameStr] : [''];                }
              }
              
              // Extract nameHint: "Name"
              const nameHintMatch = step4Str.match(/nameHint:([^,]+?)(?=,signature)/);
              if (nameHintMatch) {
                step4Parsed.nameHint = nameHintMatch[1].trim();
              } else {
                // Try alternative pattern
                const nameHintMatch2 = step4Str.match(/nameHint:([^,}]+)/);
                if (nameHintMatch2) {
                  step4Parsed.nameHint = nameHintMatch2[1].trim();                }
              }
              
              // Extract signature: [base64 string]
              const signatureMatch = step4Str.match(/signature:\[([^\]]+?)\]/);
              if (signatureMatch) {
                const sigStr = signatureMatch[1].trim();
                step4Parsed.signature = sigStr ? [sigStr] : [''];
              } else {
                // Try alternative pattern
                const signatureMatch2 = step4Str.match(/signature:\[([^\]]+)\]/);
                if (signatureMatch2) {
                  const sigStr = signatureMatch2[1].trim();
                  step4Parsed.signature = sigStr ? [sigStr] : [''];                }
              }
              
            } catch (e) {
            }
          }
          
          
          // Extract values from the parsed data
          if (step4Parsed) {
            if (step4Parsed.header) step4Data.header = step4Parsed.header;
            if (step4Parsed.descriptions) step4Data.descriptions = step4Parsed.descriptions;
            if (step4Parsed.nameHint) step4Data.nameHint = step4Parsed.nameHint;
          }
          
          // Parse name array - handle both string and array formats
          if (step4Parsed.name) {
            if (typeof step4Parsed.name === 'string') {
              // Handle string format like "[sunil, rhyshdhhdhb, revieww]"
              const nameStr = step4Parsed.name.replace(/[\[\]]/g, '');
              step4Data.name = nameStr.split(',').map(n => n.trim()).filter(n => n);
            } else if (Array.isArray(step4Parsed.name)) {
              step4Data.name = step4Parsed.name;
            }
          }
          
          // Parse signature array - handle both string and array formats
          if (step4Parsed.signature) {
            if (typeof step4Parsed.signature === 'string') {
              // Handle string format like "[\"base64_1\", \"base64_2\"]"
              try {
                step4Data.signature = JSON.parse(step4Parsed.signature);
          } catch (e) {
                step4Data.signature = [step4Parsed.signature];
              }
            } else if (Array.isArray(step4Parsed.signature)) {
              step4Data.signature = step4Parsed.signature;
            }
          }
        }
      } catch (e) {
      }

      // Parse assessmentReview data
      let assessmentReviewParsed = { 
        header: "",
        descriptions: "",
        time: [''],
        timeHint: "",
        sssbReissue: [''],
        sssbReissueHint: ""
      };
      try {
        if (rec.assessmentReview) {
          
          // Try strict JSON first
          try {
            const obj = JSON.parse(rec.assessmentReview);
            const normalizeToArray = (val) => {
              if (Array.isArray(val)) return val;
              if (typeof val === 'string') {
                try {
                  const fixed = val.trim().startsWith('[') ? val : `[${val}]`;
                  const quoted = fixed
                    .replace(/'/g, '"')
                    .replace(/\[(\s*[a-zA-Z0-9_\- :]+\s*)(,|\])/g, (m) => {
                      return m.replace(/([a-zA-Z0-9_\- :]+)/g, '"$1"');
                    });
                  const arr = JSON.parse(quoted);
                  return Array.isArray(arr) ? arr.map(v => (typeof v === 'string' ? v.trim() : v)) : [];
                } catch {
                  return val
                    .replace(/^\[/, '')
                    .replace(/\]$/, '')
                    .split(',')
                    .map(s => s.trim().replace(/^"|"$/g, ''))
                    .filter(Boolean);
                }
              }
              return [];
            };
            const timeArray = normalizeToArray(obj.time);
            const sssbReissueArray = normalizeToArray(obj.sssbReissue);
            
            // Ensure both arrays are the same length
            const maxLength = Math.max(timeArray.length, sssbReissueArray.length);
            while (timeArray.length < maxLength) {
              timeArray.push('');
            }
            while (sssbReissueArray.length < maxLength) {
              sssbReissueArray.push('');
            }
            
            assessmentReviewParsed = {
              header: obj.header || '',
              descriptions: obj.descriptions || '',
              time: timeArray,
              timeHint: obj.timeHint || '',
              sssbReissue: sssbReissueArray,
              sssbReissueHint: obj.sssbReissueHint || ''
            };
          } catch {
          }
        }
      } catch (e) {
      }

      // Do NOT fallback to static; use API only in edit flow
      const normalized = normalizeMcq(Array.isArray(mcq) ? mcq : []);
      // Reorder sections to match PDF sequence
      const reorderedSections = reorderSectionsForPDF(normalized);
      setEditRecord({ 
        ...rec,
        formUuid: ensureFormUuid(rec?.formUuid),
        // Map model field names to frontend field names
        // Model uses 'location' but frontend uses 'locationDescription'
        locationDescription: rec.locationDescription || rec.location || '',
        // Model uses 'personInChargeofSafety' but frontend uses 'personInCharge'
        personInCharge: rec.personInCharge || rec.personInChargeofSafety || '',
        mcqQuestionsList: reorderedSections,
        date: rec.date ? rec.date.trim() : rec.date,
        step4Data: step4Data,
        assessmentReviewData: assessmentReviewParsed
      });
    setShowEditModal(true);
  };

  // Fallback function to reconstruct stepsList from form data
  const reconstructStepsListFromFormData = (mcqForSave) => {
    // Check for both 'checklist' and 'Steps List' headers
    const stepsListSection = mcqForSave.find(sec => sec.header === 'checklist' || sec.header === 'Steps List');
    const stepsListItems = stepsListSection?.items || [];
    
    // Group items by main title dynamically
    const groupedByTitle = {};
    let currentMainTitle = '';
    let currentStepData = {
      checkboxes: [],
      textBoxes: []
    };
    
    stepsListItems.forEach(item => {
      if (item.isMainTitle) {
        if (currentMainTitle) {
          groupedByTitle[currentMainTitle] = currentStepData;
        }
        currentMainTitle = item.title;
        currentStepData = {
          checkboxes: [],
          textBoxes: []
        };
      } else if (item.hasCheckbox && !item.textField?.isTextBox) {
        currentStepData.checkboxes.push({
          title: item.title,
          value: !!item.checkboxValue
        });
      } else if (item.textField?.isTextBox) {
        currentStepData.textBoxes.push({
          isTextBoxShow: true,
          hintText: item.title,
          value: item.textFieldValue || item.textField?.value || '',
          header: item.stepHeader || '',
          stepName: item.stepName || '' // Preserve original step name
        });
      }
    });
    
    if (currentMainTitle) {
      groupedByTitle[currentMainTitle] = currentStepData;
    }
    
    // Convert to original API format dynamically
    const stepsListString = Object.entries(groupedByTitle).map(([mainTitle, data]) => {
      const steps = [];
      
      // Generate steps dynamically based on available data
      data.textBoxes.forEach((textBox, index) => {
        // Use the preserved step name or fallback to generic name
        const stepName = textBox.stepName || `step${index + 1}`;
        const stepText = `{'header':'${textBox.header || ''}','items':{'textBox':{'isTextBoxShow':true,'hintText':'${textBox.hintText}','valuetype':'string','isRequired':false,'value':'${textBox.value}'}}}`;
        steps.push(`'${stepName}':${stepText}`);
      });                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                          
      
      // Add checkboxes to first step if they exist
      if (data.checkboxes.length > 0 && data.textBoxes.length > 0 && steps.length > 0) {
        const checkboxList = data.checkboxes.map(cb => `{'title':'${cb.title}','value':${cb.value}}`).join(',');
        const firstStepWithCheckboxes = `{'header':'${data.textBoxes[0]?.header || ''}','items':{'textBox':${data.textBoxes[0] ? `{'isTextBoxShow':true,'hintText':'${data.textBoxes[0].hintText}','valuetype':'string','isRequired':false,'value':'${data.textBoxes[0].value}'}` : `{'isTextBoxShow':false,'hintText':'','valuetype':'string','isRequired':false,'value':''}`},'checkBox':{'isCheckBoxShow':true,'isCheckBoxRequired':false,'itemList':[${checkboxList}]}}}`;
        // Get the actual step key from the first step (could be 'step1', 'newone', 'customstep', etc.)
        const firstStepKey = steps[0].split(':')[0].replace(/'/g, ''); // Extract key from 'step1':{...}
        steps[0] = `'${firstStepKey}':${firstStepWithCheckboxes}`;
      }
      
      return `{'title':'${mainTitle}',${steps.join(',')}}`;
    }).join(',');
    
    return stepsListString;
  };

  // Dynamic stepsList save functionality - preserve original structure
  const generateStepsListString = (mcqForSave, originalChecklistString = null) => {    
    // Get the original stepsList from the edit record or use provided string
    const originalStepsList = originalChecklistString || editRecord.stepsList || editRecord.checklist;
    if (!originalStepsList) {      return reconstructStepsListFromFormData(mcqForSave);
    }
    
    // Parse the original stepsList to get the structure
    let originalData;
    try {
      originalData = JSON.parse(originalStepsList);    } catch (e) {
      // If JSON parsing fails, try robust string conversion
      let s = originalStepsList.trim();
      try {
        s = s.replace(/([,{]\s*)([A-Za-z0-9_\/\-\(\)]+)(\s*:)/g, '$1"$2"$3');
        s = s.replace(/'((?:\\'|[^'])*)'/g, (m, inner) => {
          const unescaped = inner.replace(/\\'/g, "'");
          const escapedDouble = unescaped.replace(/"/g, '\\"');
          return '"' + escapedDouble + '"';
        });
        s = s.replace(/(\"value\"\s*:\s*)\"true\"/g, '$1true');
        s = s.replace(/(\"value\"\s*:\s*)\"false\"/g, '$1false');
        s = s.replace(/,\s*([}\]])/g, '$1');
        s = s.replace(/([A-Za-z0-9_\/\-\(\)]+):/g, '"$1":');
        s = s.replace(/"n\/a":/g, '"n/a":');
        originalData = JSON.parse(s);
      } catch (e2) {
        // Last resort: simple quote replacement
        let cleaned = originalStepsList
          .replace(/'/g, '"')
          .replace(/"true"/g, 'true')
          .replace(/"false"/g, 'false');
        try {
          originalData = JSON.parse(cleaned);
        } catch (e3) {
          // If all parsing fails, fall back to form data reconstruction
          return reconstructStepsListFromFormData(mcqForSave);
        }
      }
    }
    
    if (!Array.isArray(originalData)) {
      // Fallback: reconstruct from form data
      return reconstructStepsListFromFormData(mcqForSave);
    }
    
    // Get the current form data - check for both 'checklist' and 'Steps List' headers
    const stepsListSection = mcqForSave.find(sec => sec.header === 'checklist' || sec.header === 'Steps List');
    const stepsListItems = stepsListSection?.items || [];
        
    // Create a map of current form data by main title dynamically
    const currentDataMap = {};
    let currentMainTitle = '';
    let currentStepData = {
      checkboxes: [],
      textBoxes: []
    };
    
    stepsListItems.forEach(item => {
      if (item.isMainTitle) {
        if (currentMainTitle) {
          currentDataMap[currentMainTitle] = currentStepData;
        }
        currentMainTitle = item.title;
        currentStepData = {
          checkboxes: [],
          textBoxes: []
        };
      } else if (item.hasCheckbox && !item.textField?.isTextBox) {
        currentStepData.checkboxes.push({
          title: item.title,
          value: !!item.checkboxValue
        });
      } else if (item.textField?.isTextBox) {
        const textBoxValue = item.textFieldValue || item.textField?.value || '';
        currentStepData.textBoxes.push({
          isTextBoxShow: true,
          hintText: item.title || item.textField?.hintText || '',
          value: textBoxValue,
          header: item.stepHeader || '',
          stepName: item.stepName || '' // Preserve stepName for better matching
        });
      }
    });
    
    if (currentMainTitle) {
      currentDataMap[currentMainTitle] = currentStepData;
    }
        
    // Update the original data with current form values dynamically
    const updatedData = originalData.map(originalSection => {
      const mainTitle = originalSection.title;
      const currentData = currentDataMap[mainTitle];
      
      if (!currentData) {        return originalSection; // Keep original if no current data
      }
            
      // Update all steps dynamically (any key that's not 'title')
      const stepKeys = Object.keys(originalSection).filter(key => key !== 'title');
      stepKeys.forEach(stepKey => {
        const stepData = originalSection[stepKey];
        if (stepData && stepData.items) {          
          // Update checkboxes if they exist
          if (stepData.items.checkBox && stepData.items.checkBox.itemList && currentData.checkboxes.length > 0) {
            stepData.items.checkBox.itemList = stepData.items.checkBox.itemList.map(originalCheckbox => {
              const currentCheckbox = currentData.checkboxes.find(cb => cb.title === originalCheckbox.title);
              if (currentCheckbox) {
                return { ...originalCheckbox, value: currentCheckbox.value };
              }
              return originalCheckbox;
            });
          }
          
          // Update textBox if it exists
          if (stepData.items.textBox) {
            // Match by hintText or by step header to identify the textBox
            // First try to match by hintText, then by header
            let currentTextBox = currentData.textBoxes.find(tb => 
              tb.hintText === stepData.items.textBox.hintText
            );
            
            // If no match by hintText, try matching by header
            if (!currentTextBox && stepData.header) {
              currentTextBox = currentData.textBoxes.find(tb => 
                tb.header === stepData.header
              );
            }
            
            if (currentTextBox) {
              stepData.items.textBox.value = currentTextBox.value;
              stepData.items.textBox.isTextBoxShow = true;
            }
          }
        }
      });
      
      return originalSection;
    });
    
    // Convert back to string format matching the original API structure
    const resultString = JSON.stringify(updatedData).replace(/"/g, "'");    return resultString;
  };

  // Function to update only value fields using string manipulation (avoiding complex JSON parsing)
  const updateOnlyValueFields = (originalData, formData) => {
    try {
      // Deep clone the original data to avoid mutations
      const updatedData = JSON.parse(JSON.stringify(originalData));
      
      // Get form data from mcqQuestionsList
      const formMcqData = formData.mcqQuestionsList || [];
      const stepsListSection = formMcqData.find(sec => sec.header === 'Steps List');
      const stepsListItems = stepsListSection?.items || [];
      
      // Create a map of form data by main title and step
      const formDataMap = {};
      let currentMainTitle = '';
      let currentStepData = {
        checkboxes: [],
        textBoxes: []
      };
      
      stepsListItems.forEach(item => {
        if (item.isMainTitle) {
          if (currentMainTitle) {
            formDataMap[currentMainTitle] = currentStepData;
          }
          currentMainTitle = item.title;
          currentStepData = {
            checkboxes: [],
            textBoxes: []
          };
        } else if (item.hasCheckbox && !item.textField?.isTextBox) {
          // Debug log for checkbox collection
          currentStepData.checkboxes.push({
            title: item.title,
            value: !!item.checkboxValue
          });
        } else if (item.textField?.isTextBox) {
          currentStepData.textBoxes.push({
            hintText: item.title,
            value: item.textFieldValue || item.textField?.value || '',
            header: item.stepHeader || '',
            stepName: item.stepName || ''
          });
        }
      });
      
      if (currentMainTitle) {
        formDataMap[currentMainTitle] = currentStepData;
      }
      
      // Update the stepsList string directly using string manipulation
      let updatedStepsList = originalData.stepsList;
      
      // Collect all checkboxes from all sections and group them by title
      const allCheckboxes = [];
      Object.keys(formDataMap).forEach(mainTitle => {
        const formData = formDataMap[mainTitle];
        allCheckboxes.push(...formData.checkboxes);
      });
      
      // Group checkboxes by title to handle multiple instances of the same title
      const checkboxGroups = {};
      allCheckboxes.forEach(checkbox => {
        if (!checkboxGroups[checkbox.title]) {
          checkboxGroups[checkbox.title] = [];
        }
        checkboxGroups[checkbox.title].push(checkbox);
      });
      
      
      // Process each group of checkboxes with the same title
      Object.keys(checkboxGroups).forEach(title => {
        const checkboxes = checkboxGroups[title];
        
        // Use regex to find all checkboxes with this title and replace them in order
        const escapedTitle = title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const checkboxPattern = new RegExp(`(\\{'title':'${escapedTitle}','value':)(true|false)(\\})`, 'g');
        let matchIndex = 0;
        
        updatedStepsList = updatedStepsList.replace(checkboxPattern, (match, prefix, currentValue, suffix) => {
          if (matchIndex < checkboxes.length) {
            const checkbox = checkboxes[matchIndex];
            const newValue = checkbox.value ? 'true' : 'false';
            matchIndex++;
            return `${prefix}${newValue}${suffix}`;
          }
          return match; // Keep original if no more checkboxes
        });
        });
        
        // Update textBox values
      Object.keys(formDataMap).forEach(mainTitle => {
        const formData = formDataMap[mainTitle];
        
        formData.textBoxes.forEach(textBox => {
          const stepName = textBox.stepName;
          const value = textBox.value;
          
          if (stepName && value !== undefined) {
            // Create regex pattern to find and replace textBox values for specific step
            const textBoxPattern = new RegExp(
              `('${stepName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}':\\{[^}]*'textBox':\\{[^}]*'value':')([^']*)('\\}[^}]*\\})`,
              'g'
            );
            
            updatedStepsList = updatedStepsList.replace(textBoxPattern, `$1${value}$3`);
          }
        });
      });
      
      // Update the stepsList in the result
      updatedData.stepsList = updatedStepsList;
      
      // Update other form fields - Map frontend field names to model field names
      // Model uses 'location' but frontend uses 'locationDescription'
      updatedData.location = formData.locationDescription;
      updatedData.locationDescription = formData.locationDescription; // Also keep for compatibility
      
      updatedData.jobNumber = formData.jobNumber;
      
      // Model uses 'personInChargeofSafety' but frontend uses 'personInCharge'
      updatedData.personInChargeofSafety = formData.personInCharge;
      updatedData.personInCharge = formData.personInCharge; // Also keep for compatibility
      
      updatedData.date = formData.date;
      updatedData.recodedOfMultiSiteVisit = formData.recodedOfMultiSiteVisit;
      updatedData.step4 = formData.step4;
      updatedData.assessmentReview = formData.assessmentReview;
      
      
      return updatedData;
    } catch (error) {
      console.error('Error updating value fields:', error);
      return originalData; // Return original data if update fails
    }
  };

  const handleEditSave = async () => {
    // Prevent duplicate save calls
    if (isSavingRef.current) {
      return;
    }
    
    if (!editRecord.locationDescription || !editRecord.date || !editRecord.jobNumber || !editRecord.personInCharge) {
      toast.error("All required fields must be filled");
      return;
    }
    
    isSavingRef.current = true; // Set saving flag
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      
      // Get the original record data to preserve structure
      const originalRecord = records.find(r => r._id === editRecord._id);
      if (!originalRecord) {
        toast.error("Original record not found");
        isSavingRef.current = false; // Reset saving flag
        return;
      }
      
      // Create form data structure that matches the original API response format
      const formData = {
        ...editRecord,
        date: convertDateForSave(editRecord.date)
      };
      
      // Also update the other MCQ fields with current form data
      const editFormMcqData = formData.mcqQuestionsList || [];
      
      // Start with the original record and update specific fields
      const recordToSave = { ...originalRecord };
      
      // Update userId and get user details for saving firstName and lastName
      if (formData.userId) {
        recordToSave.userId = formData.userId;
        const user = appUsers.find(u => u._id === formData.userId);
        recordToSave.addedByFirstName = user ? user.firstName : '';
        recordToSave.addedByLastName = user ? user.lastName : '';
      }
      
      // Update basic fields - Map frontend field names to model field names
      // Model uses 'location' but frontend uses 'locationDescription'
      recordToSave.location = formData.location || formData.locationDescription;
      recordToSave.locationDescription = formData.location || formData.locationDescription; // Also keep for compatibility
      
      recordToSave.date = formData.date;
      recordToSave.jobNumber = formData.jobNumber;
      recordToSave.descriptionOfWorks = formData.descriptionOfWorks;
      
      // Model uses 'personInChargeofSafety' but frontend uses 'personInCharge'
      recordToSave.personInChargeofSafety = formData.personInCharge;
      recordToSave.personInCharge = formData.personInCharge; // Also keep for compatibility
      
      recordToSave.recodedOfMultiSiteVisit = formData.recodedOfMultiSiteVisit;
      
      // Update mcqDetailedPreWorkList
      const mcqDetailedSection = editFormMcqData.find(sec => sec.header === 'mcqDetailedPreWorkList');
      if (mcqDetailedSection && mcqDetailedSection.items) {
        const mcqDetailedItems = mcqDetailedSection.items.map(item => ({
          text: item.title || '',
          options: item.options || [],
          extraOptionsText: item.options?.map(() => '') || [],
          value: item.value || '',
          isOptional: false
        }));
        recordToSave.mcqDetailedPreWorkList = JSON.stringify(mcqDetailedItems);
      }
      
      // Update mcqControlsPreWorkList
      const mcqControlsSection = editFormMcqData.find(sec => sec.header === 'mcqControlsPreWorkList');
      if (mcqControlsSection && mcqControlsSection.items) {
        const mcqControlsItems = mcqControlsSection.items.map(item => ({
          text: item.title || '',
          options: item.options || [],
          extraOptionsText: item.options?.map(() => '') || [],
          value: item.value || '',
          isOptional: false
        }));
        recordToSave.mcqControlsPreWorkList = JSON.stringify(mcqControlsItems);
      }
      
      // Update checklist
      const checklistSection = editFormMcqData.find(sec => sec.header === 'checklist');
      if (checklistSection) {
        // Get the original checklist from the original record
        const originalChecklist = originalRecord.checklist || '';
        
        if (originalChecklist) {
          // Pass the original checklist string directly to the function
          const stepsListString = generateStepsListString(editFormMcqData, originalChecklist);
          recordToSave.checklist = stepsListString;
        } else {
          // If no original checklist, reconstruct from form data
          const stepsListString = reconstructStepsListFromFormData(editFormMcqData);
          recordToSave.checklist = stepsListString;
        }
      }
      
      // Update detailsofEmergencyProcedures
      const emergencySection = editFormMcqData.find(sec => sec.header === 'detailsofEmergencyProcedures');
      if (emergencySection && emergencySection.items) {
        const emergencyData = {
          header: 'Details of Emergency Procedures',
          items: emergencySection.items.map(item => {
            const result = { title: item.title || '' };
            
            // Handle multipleTextBox (What 3 Words)
            if (item.textField?.isTextBox && item.textField.hintText?.includes('Word')) {
              const values = (item.textFieldValue || item.textField?.value || '').split(',').map(v => v.trim());
              result.multipleTextBox = {
                isMultipleTextBoxShow: true,
                textFields: [
                  { hintText: 'Word 1', valuetype: 'string', isRequired: false, value: values[0] || '' },
                  { hintText: 'Word 2', valuetype: 'string', isRequired: false, value: values[1] || '' },
                  { hintText: 'Word 3', valuetype: 'string', isRequired: false, value: values[2] || '' }
                ]
              };
            }
            // Handle textBox
            else if (item.textField?.isTextBox) {
              result.textBox = {
                isTextBoxShow: true,
                hintText: item.textField.hintText || '',
                valuetype: 'string',
                isRequired: false,
                value: item.textFieldValue || item.textField?.value || ''
              };
            }
            // Handle radioBox
            else if (item.options && item.options.length > 0) {
              result.radioBox = {
                isRadioBoxShow: true,
                isRadioBoxRequired: false,
                selectedValue: item.value || '',
                itemList: item.options.map(opt => ({ title: opt, value: opt }))
              };
              // Add textBox for "Other" option if needed
              if (item.value === 'Other') {
                result.textBox = {
                  isTextBoxShow: false,
                  hintText: 'Please specify other location',
                  valuetype: 'string',
                  isRequired: false,
                  value: ''
                };
              }
            }
            
            return result;
          })
        };
        recordToSave.detailsofEmergencyProcedures = JSON.stringify(emergencyData);
      }
      
      // Update mcqQuestions (fallback for old format)
      const mcqQuestionsSection = editFormMcqData.find(sec => sec.header === 'MCQ Questions' || sec.header === 'mcqQuestions');
      if (mcqQuestionsSection) {
        const mcqQuestionsString = mcqQuestionsSection.items?.map(item => {
          const text = item.title || '';
          const options = item.options || [];
          const value = item.value || '';
          const isOptional = false;
          return `{'text':'${text.replace(/'/g, "\\'")}','options':[${options.map(opt => `'${opt}'`).join(',')}],'value':'${value}','isOptional':${isOptional}}`;
        }).join(',') || '';
        recordToSave.mcqQuestions = `[${mcqQuestionsString}]`;
      }
      
      // Update safeCheckList
      const safeCheckListSection = editFormMcqData.find(sec => sec.header === 'safeCheckList');
      if (safeCheckListSection) {
        // Parse the original safeCheckList to preserve all structure
        const originalSafeData = parseSingleQuotedJSON(editRecord.safeCheckList) || {};
        
        // Update only the values while preserving all other data
        const updatedSafeData = { ...originalSafeData };
        
        // Get all UI items
        const uiItems = safeCheckListSection.items || [];
        
        // Update items array - each item has checkBox.itemList and textBox
        if (updatedSafeData.items && Array.isArray(updatedSafeData.items)) {
          updatedSafeData.items = updatedSafeData.items.map(apiItem => {
            const updatedItem = { ...apiItem };
            
            // Update checkBox.itemList values
            if (updatedItem.checkBox && updatedItem.checkBox.itemList && Array.isArray(updatedItem.checkBox.itemList)) {
              updatedItem.checkBox = {
                ...updatedItem.checkBox,
                itemList: updatedItem.checkBox.itemList.map(checkboxItem => {
                  // Find matching UI item by checkbox title
                  const matchingUIItem = uiItems.find(ui => 
                    ui.hasCheckbox && 
                    ui.title === checkboxItem.title &&
                    ui.img === apiItem.img // Match by image to ensure correct item group
                  );
                  
                  if (matchingUIItem) {
                    const newValue = matchingUIItem.checkboxValue !== undefined 
                      ? matchingUIItem.checkboxValue 
                      : (matchingUIItem.booleanValue !== undefined ? matchingUIItem.booleanValue : checkboxItem.value);
            return {
                      ...checkboxItem,
              value: newValue
            };
                  }
                  return checkboxItem;
                })
              };
            }
            
            // Update textBox value for "Other PPE required"
            if (updatedItem.textBox && updatedItem.textBox.isTextBoxShow) {
              // Find matching UI item by hintText and image (for "Other PPE required")
              // The UI item title might be the item title or "Other PPE required", but hintText should match
              const matchingUIItem = uiItems.find(ui => 
                ui.textField?.isTextBox && 
                (ui.textField.hintText === 'Other PPE required' || ui.textField.hintText === updatedItem.textBox.hintText) &&
                ui.img === apiItem.img // Match by image to ensure correct item
              );
              
              if (matchingUIItem) {
                const currentValue = matchingUIItem.textFieldValue !== undefined 
                  ? matchingUIItem.textFieldValue 
                  : (matchingUIItem.textField?.value !== undefined ? matchingUIItem.textField.value : updatedItem.textBox.value);
                
                updatedItem.textBox = {
                  ...updatedItem.textBox,
                  value: currentValue
                };
              }
            }
            
            return updatedItem;
          });
        }
        
        // Update detailPpeRequired textBox value (root level)
        // Find the item that doesn't have an img (detailPpeRequired is at root level, not part of items array)
        if (updatedSafeData.detailPpeRequired && updatedSafeData.detailPpeRequired.textBox) {
          const detailPpeItem = uiItems.find(ui => 
            ui.textField?.isTextBox && 
            !ui.img && // detailPpeRequired doesn't have an image
            (ui.textField.hintText === updatedSafeData.detailPpeRequired.textBox.hintText || 
             ui.title === updatedSafeData.detailPpeRequired.textBox.hintText)
          );
          
          if (detailPpeItem) {
            const currentValue = detailPpeItem.textFieldValue !== undefined 
              ? detailPpeItem.textFieldValue 
              : (detailPpeItem.textField?.value !== undefined ? detailPpeItem.textField.value : updatedSafeData.detailPpeRequired.textBox.value);
            
            updatedSafeData.detailPpeRequired = {
              ...updatedSafeData.detailPpeRequired,
              textBox: {
                ...updatedSafeData.detailPpeRequired.textBox,
                value: currentValue
              }
            };
          }
        }
        
        const safeCheckListString = JSON.stringify(updatedSafeData).replace(/"/g, "'");
        
        recordToSave.safeCheckList = safeCheckListString;
      }
      
      // Update mcqQuestionsHss
      const hssSection = editFormMcqData.find(sec => sec.header === 'mcqQuestionsHss');
      if (hssSection) {
        const hssString = hssSection.items?.map(item => {
          // Get the current text box value (prioritize textFieldValue, then textField.value, then empty string)
          const textBoxValue = item.textFieldValue !== undefined ? item.textFieldValue : 
                              (item.textField?.value !== undefined ? item.textField.value : '');
          return `{'header':'${item.title || ''}','options':${JSON.stringify(item.options || [])},'selected':'${item.value || ''}','reasonTextBox':{'isTextBoxShow':${item.textField?.isTextBox || false},'hintText':'${item.textField?.hintText || ''}','valuetype':'string','isRequired':false,'value':'${textBoxValue}'}}`;
        }).join(',') || '';
        recordToSave.mcqQuestionsHss = hssString;
      }
      
      // Update step4 data
      recordToSave.step4 = JSON.stringify(editRecord.step4Data);
      
      // Update assessmentReview data
      recordToSave.assessmentReview = JSON.stringify(editRecord.assessmentReviewData);
      
      // Update personInChargeofSafety from table data
      const picosSection = editFormMcqData.find(sec => sec.header === 'personInChargeofSafety' && sec.isTable);
      if (picosSection && picosSection.tableData) {
        const picosData = {
          header: picosSection.tableHeader || 'Person in Charge of Safety (PICOS)',
          descriptions: originalRecord.personInChargeofSafety ? (() => {
            try {
              const parsed = JSON.parse(originalRecord.personInChargeofSafety);
              return parsed.descriptions || '-';
            } catch { return '-'; }
          })() : '-',
          name: picosSection.tableData.map(row => row.name || ''),
          nameHint: picosSection.nameHint || 'Name',
          time: picosSection.tableData.map(row => row.time || ''),
          timeHint: picosSection.timeHint || 'Time',
          date: picosSection.tableData.map(row => row.date || ''),
          dateHint: picosSection.dateHint || 'Date',
          signature: picosSection.tableData.map(row => row.signature || '')
        };
        recordToSave.personInChargeofSafety = JSON.stringify(picosData);
      }
      
      // Update PersonTakingOverAsPICOS from table data
      const takingOverSection = editFormMcqData.find(sec => sec.header === 'PersonTakingOverAsPICOS' && sec.isTable);
      if (takingOverSection && takingOverSection.tableData) {
        const takingOverData = {
          header: takingOverSection.tableHeader || 'Person taking over as PICOS',
          descriptions: originalRecord.PersonTakingOverAsPICOS ? (() => {
            try {
              const parsed = JSON.parse(originalRecord.PersonTakingOverAsPICOS);
              return parsed.descriptions || '-';
            } catch { return '-'; }
          })() : '-',
          name: takingOverSection.tableData.map(row => row.name || ''),
          nameHint: takingOverSection.nameHint || 'Name',
          time: takingOverSection.tableData.map(row => row.time || ''),
          timeHint: takingOverSection.timeHint || 'Time',
          date: takingOverSection.tableData.map(row => row.date || ''),
          dateHint: takingOverSection.dateHint || 'Date',
          signature: takingOverSection.tableData.map(row => row.signature || '')
        };
        recordToSave.PersonTakingOverAsPICOS = JSON.stringify(takingOverData);
      }
      
      // Update nameUkpnCompany from table data
      const nameUkpnSection = editFormMcqData.find(sec => sec.header === 'nameUkpnCompany' && sec.isTable);
      if (nameUkpnSection && nameUkpnSection.tableData) {
        const nameUkpnData = {
          header: nameUkpnSection.tableHeader || 'Name (and company if not UKPN)',
          descriptions: originalRecord.nameUkpnCompany ? (() => {
            try {
              const parsed = JSON.parse(originalRecord.nameUkpnCompany);
              return parsed.descriptions || '';
            } catch { return ''; }
          })() : '',
          name: nameUkpnSection.tableData.map(row => row.name || ''),
          nameHint: nameUkpnSection.nameHint || 'Name (and company if not UKPN)',
          time: nameUkpnSection.tableData.map(row => row.time || ''),
          timeHint: nameUkpnSection.timeHint || null,
          signature: nameUkpnSection.tableData.map(row => row.signature || '')
        };
        recordToSave.nameUkpnCompany = JSON.stringify(nameUkpnData);
      }
      
      // Update recordofAssessmentReview from table data
      const reviewSection = editFormMcqData.find(sec => sec.header === 'recordofAssessmentReview' && sec.isTable);
      if (reviewSection && reviewSection.tableData) {
        const reviewData = {
          header: reviewSection.tableHeader || 'Record of Assessment Review - if site hazards change (weather/working party changes)',
          descriptions: originalRecord.recordofAssessmentReview ? (() => {
            try {
              const parsed = JSON.parse(originalRecord.recordofAssessmentReview);
              return parsed.descriptions || '';
            } catch { return ''; }
          })() : '',
          time: reviewSection.tableData.map(row => row.time || ''),
          timeHint: reviewSection.timeHint || 'Time',
          sssbReissue: reviewSection.tableData.map(row => row.sssbReissue || ''),
          sssbReissueHint: reviewSection.sssbReissueHint || 'POWA re-issued (PICOS to initial that working party briefed)',
          reasonforReview: reviewSection.tableData.map(row => row.reasonforReview || ''),
          reasonforReviewHint: reviewSection.reasonforReviewHint || 'Reason for review'
        };
        recordToSave.recordofAssessmentReview = JSON.stringify(reviewData);
      }
      
      // Update stateReasonForReviewRequest
      const stateReasonSection = editFormMcqData.find(sec => (sec.header === 'stateReasonForReviewRequest' || sec.header === '') && sec.items && sec.items.length > 0);
      if (stateReasonSection && stateReasonSection.items && stateReasonSection.items.length > 0) {
        const stateReasonItem = stateReasonSection.items.find(item => item.textField?.isTextBox && item.textField.hintText === 'State reason for review request') || stateReasonSection.items[0];
        if (stateReasonItem && stateReasonItem.textField?.isTextBox) {
          recordToSave.stateReasonForReviewRequest = stateReasonItem.textFieldValue || stateReasonItem.textField?.value || '';
        }
      }
      
      // Update stepsList using the debug function
      const stepsListString = generateStepsListString(editFormMcqData);
      recordToSave.stepsList = stepsListString;
      
      // Debug logging
      // Debug: Check all checkbox values in the final result
      const allCheckboxMatches = recordToSave.stepsList.match(/'title':'[^']+','value':(true|false)/g) || [];
      
      // Debug: Check form data for all checkboxes
      const formMcqData = formData.mcqQuestionsList || [];
      const stepsListSection = formMcqData.find(sec => sec.header === 'Steps List');
      const allCheckboxItems = stepsListSection?.items?.filter(item => item.hasCheckbox) || [];
      
      // Validate the data before sending
      if (!editRecord._id) {
        console.error('Missing record ID');
        toast.error('Missing record ID');
        isSavingRef.current = false; // Reset saving flag
        return;
      }
      
      recordToSave.formUuid = ensureFormUuid(editRecord.formUuid);
      const res = await fetch(baseUrl + `/api/pow-assessment/${editRecord._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(recordToSave)
      });
        
        if (!res.ok) {
          const errorText = await res.text();
          console.error('Edit API Error Response:', errorText);
          toast.error(`Failed to update record: ${res.status} ${res.statusText}`);
          isSavingRef.current = false; // Reset saving flag
          return;
        }
        
        const data = await res.json();
      
      if (res.ok && data.success) {
        // Update records with the API response data
        const updatedRecord = data.data.data || data.data;
        if (updatedRecord) {
          setRecords(records.map(r => r._id === editRecord._id ? updatedRecord : r));
        }
        
        // Refetch all records to ensure data consistency
        try {
          const refreshRes = await fetch(baseUrl + "/api/pow-assessment", {
            headers: { "Authorization": `Bearer ${token}` }
          });
          const refreshData = await refreshRes.json();
          if (refreshRes.ok && refreshData.success && Array.isArray(refreshData.data)) {
            setRecords(refreshData.data);
          }
        } catch (refreshError) {
          console.warn('Failed to refresh records:', refreshError);
        }
        
        setShowEditModal(false);
        toast.success("Record updated!");
      } else {
        console.error('Edit failed:', { status: res.status, data });
        toast.error(data.message || `Failed to update (Status: ${res.status})`);
      }
    } catch (error) {
      console.error("Error updating record:", error);
      console.error("Error details:", error.message, error.stack);
      toast.error(`Failed to update record: ${error.message}`);
    } finally {
      isSavingRef.current = false; // Reset saving flag
    }
  };

  // Delete Record
  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };
  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + `/api/pow-assessment/${deleteId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRecords(records.filter(r => r._id !== deleteId));
        toast.success("Deleted successfully!");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  // Text handler
  const handleTextChange = (field, value, isEdit = false) => {
    if (isEdit) {
      setEditRecord({ ...editRecord, [field]: value });
    } else {
      setNewRecord({ ...newRecord, [field]: value });
    }
  }; 

  // Helper function to remove white background from CheckTick image
  const processCheckTickImage = (imageSrc) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function() {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;
          
          // Remove white background (make white pixels transparent)
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            // Check if pixel is white (or very close to white)
            if (r > 240 && g > 240 && b > 240) {
              data[i + 3] = 0; // Make transparent
            }
          }
          
          ctx.putImageData(imageData, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } catch (e) {
          console.warn("Failed to process CheckTick image:", e);
          resolve(imageSrc); // Return original if processing fails
        }
      };
      img.onerror = () => {
        resolve(imageSrc); // Return original if load fails
      };
      img.src = imageSrc;
    });
  };

  // SharePoint Upload Handler
  const handleSharePointUpload = async () => {
    const locationForUpload = (sharePointUploadRecord?.location || sharePointUploadRecord?.locationDescription || '').trim();

    if (!locationForUpload) {
      toast.error('Location is missing for this record.');
      return;
    }

    if (sharePointPhaseType === 'phase' && (!sharePointNetwork || sharePointNetwork.trim() === '')) {
      toast.error('Please select an area (EPN, LPN, or SPN)');
      return;
    }

    const payloadPhaseType = sharePointPhaseType === 'phase' ? 'phase' : 'non-phase';
    const payloadNetwork = sharePointPhaseType === 'phase' ? sharePointNetwork : '';
    const payloadPhaseNumber = sharePointPhaseType === 'phase' ? sharePointPhaseNumber : '';

    setShowSharePointUploadModal(false);

    await generatePDF(sharePointUploadRecord, {
      uploadOnly: true,
      phaseType: payloadPhaseType,
      phaseNumber: payloadPhaseNumber,
      network: payloadNetwork,
      siteName: locationForUpload,
      onSuccess: () => {
        setSharePointUploadRecord(null);
        setSharePointPhaseType('phase');
        setSharePointPhaseNumber('15');
        setSharePointNetwork('');
      },
      onError: () => {}
    });
  };

  const generatePDF = async (record, options = {}) => {
      const shouldUploadToSharePoint = options.uploadOnly === true;
      const uploadConfig = shouldUploadToSharePoint ? {
        phaseType: options.phaseType || 'non-phase',
        phaseNumber: options.phaseNumber || '',
        network: options.network || '',
        siteName: options.siteName || '',
        onSuccess: options.onSuccess,
        onError: options.onError
      } : null;
      
      // Require a valid job number before proceeding
      if (!record?.jobNumber || String(record.jobNumber).trim() === "") {
        toast.error("Job Number is required before generating/uploading PDF.");
        return;
      }

      // Process CheckTick image to remove white background
      const processedCheckTick = await processCheckTickImage(CheckTick);

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Modern color scheme
      const primaryColor = [139, 22, 52]; // #8b1634 - Match SiteRegisterPage.js
      const secondaryColor = [233, 207, 214]; // #e9cfd6 - Match SiteRegisterPage.js
      const accentColor = [16, 185, 129]; // Modern green #10b981
      const headerColor = [30, 41, 59]; // Dark slate #1e293b
      const dangerColor = [239, 68, 68]; // Modern red #ef4444
      const warningColor = [245, 158, 11]; // Modern amber #f59e0b
      const darkGray = [51, 65, 85]; // Slate 700
      const lightGray = [148, 163, 184]; // Slate 400
      const backgroundColor = [248, 250, 252]; // Slate 50
      const grayColor = [90, 90, 90];
      
      
      // Helper function to draw modern rounded rectangle with shadow
      const drawRoundedRect = (x, y, width, height, radius, fillColor, strokeColor, hasShadow = false) => {
        if (hasShadow) {
          // Draw shadow
          doc.setFillColor(0, 0, 0, 0.1);
          doc.roundedRect(x + 1, y + 1, width, height, radius, radius, 'F');
        }
        
        doc.setFillColor(...fillColor);
        doc.setDrawColor(...strokeColor);
        doc.setLineWidth(0.5);
        doc.roundedRect(x, y, width, height, radius, radius, 'FD');
      };
      
// ---- HEADER SECTION ----
const headerHeight = 30; // increased height for spacing & clarity
drawRoundedRect(0, 0, pageWidth, headerHeight, 0, primaryColor, primaryColor);

// ---- ADD HIGH-RES LOGO ----
const addLogoToPDF = () => {
  return new Promise((resolve) => {
    const logoImg = new Image();
    logoImg.crossOrigin = 'anonymous';
    logoImg.onload = function () {
      try {
        const logoWidth = 70; // balanced width
        const aspectRatio = logoImg.height / logoImg.width;
        const logoHeight = logoWidth * aspectRatio;

        const logoX = 10; // left margin
        const logoY = (headerHeight - logoHeight) / 2; // perfectly center vertically

        // high-resolution scaling
        const canvas = document.createElement('canvas');
        const scale = 2.5;
        canvas.width = logoImg.width * scale;
        canvas.height = logoImg.height * scale;
        const ctx = canvas.getContext('2d');
        ctx.scale(scale, scale);
        ctx.drawImage(logoImg, 0, 0);

        const highResDataUrl = canvas.toDataURL('image/png', 1.0);
        doc.addImage(highResDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight, '', 'NONE');
      } catch (error) {
        // fallback text logo
        doc.setFillColor(255, 255, 255);
        doc.rect(10, 6, 60, 15, 'F');
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text("TAYLOR TECHNOLOGY", 15, 12);
        doc.text("SYSTEMS LIMITED", 15, 17);
      }
      resolve();
    };

    logoImg.onerror = function () {
      // fallback if logo fails
      doc.setFillColor(255, 255, 255);
      doc.rect(10, 6, 60, 15, 'F');
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text("TAYLOR TECHNOLOGY", 15, 12);
      doc.text("SYSTEMS LIMITED", 15, 17);
      resolve();
    };

    logoImg.src = onsiteproLogo;
  });
};

// ---- USE LOGO ----
await addLogoToPDF();

// ---- HEADER TEXT ----
const titleX = 90; // shifted right for spacing from logo
doc.setFont("helvetica", "bold");
doc.setFontSize(16);
doc.setTextColor(255, 255, 255);
doc.text("Point of Work Assessment", titleX, 13);

doc.setFont("helvetica", "normal");
doc.setFontSize(8);
doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", titleX, 19);

let yPosition = headerHeight + 10; // next content starts below header



      // ---- Section 1 : Location & Description of Works ----
      doc.setFillColor(139, 22, 52); // dark magenta header
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      
      const section1Left = 10;
      const section1TableW = pageWidth - 20;

      doc.rect(section1Left, yPosition, section1TableW, 8, "F");
      doc.text("1", section1Left + 4, yPosition + 5.5);
      doc.text("Location / Description of Works", section1Left + 12, yPosition + 5.5);
      yPosition += 8;
      
      // Parse dynamic MCQ data
      let detailedPreWorkData = null;
      let controlsPreWorkData = null;
      
      try {
        if (record.mcqDetailedPreWorkList) {
          const parsed = typeof record.mcqDetailedPreWorkList === 'string' 
            ? JSON.parse(record.mcqDetailedPreWorkList) 
            : record.mcqDetailedPreWorkList;
          detailedPreWorkData = Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : null;
        }
      } catch (e) {
        console.error('Failed to parse mcqDetailedPreWorkList:', e);
      }
      
      try {
        if (record.mcqControlsPreWorkList) {
          const parsed = typeof record.mcqControlsPreWorkList === 'string' 
            ? JSON.parse(record.mcqControlsPreWorkList) 
            : record.mcqControlsPreWorkList;
          controlsPreWorkData = Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : null;
        }
      } catch (e) {
        console.error('Failed to parse mcqControlsPreWorkList:', e);
      }
      
      const detailedPreWorkText = detailedPreWorkData?.text || "Detailed pre-work\nassessment available";
      const detailedPreWorkValue = detailedPreWorkData?.value;
      const detailedOptions = detailedPreWorkData?.options?.length ? detailedPreWorkData.options : ["No", "Yes"];

      const controlsPreWorkText = controlsPreWorkData?.text || "Controls in pre-work\nassessment fully applied";
      const controlsPreWorkValue = controlsPreWorkData?.value;
      const controlsOptions = controlsPreWorkData?.options?.length ? controlsPreWorkData.options : ["Yes", "No"];
      const controlsExtra = controlsPreWorkData?.extraOptionsText || [];

      const normPowOpt = (v) => String(v ?? "").trim().toLowerCase();
      const matchPowOption = (value, option) => {
        if (value === undefined || value === null || value === "") return false;
        const v = normPowOpt(value);
        const o = normPowOpt(option);
        return v === o || v.startsWith(`${o} `) || v.startsWith(o);
      };

      const findPowOptionIndex = (options, target) => {
        const idx = options.findIndex((opt) => normPowOpt(opt) === target);
        return idx >= 0 ? idx : -1;
      };

      const detailedNoIdx = findPowOptionIndex(detailedOptions, "no");
      const detailedYesIdx = findPowOptionIndex(detailedOptions, "yes");
      const controlsYesIdx = findPowOptionIndex(controlsOptions, "yes");
      const controlsNoIdx = findPowOptionIndex(controlsOptions, "no");

      const controlsYesExtra = (controlsExtra[controlsYesIdx >= 0 ? controlsYesIdx : 0] || "continue with POWA")
        .replace(/\\n/g, "\n")
        .trim();
      const controlsNoExtra = (controlsExtra[controlsNoIdx >= 0 ? controlsNoIdx : 1] || "apply pre-work controls first")
        .replace(/\\n/g, "\n")
        .trim();

      const labelCellStyle = { fillColor: [230, 230, 230], fontStyle: "bold", valign: "middle" };
      const optionCellStyle = { halign: "center", fontStyle: "bold", valign: "middle", fillColor: [255, 255, 255] };
      const s1Col = (units) => section1TableW * (units / 6);

      autoTable(doc, {
        startY: yPosition,
        body: [
          [
            { content: "Location:", styles: labelCellStyle },
            { content: record.locationDescription || record.location || "", colSpan: 2, styles: { halign: "center" } },
            { content: "Date:\nValid only on date / shift detailed", styles: { ...labelCellStyle, halign: "center" } },
            { content: record.date || "", colSpan: 2, styles: { halign: "center" } },
          ],
          [
            { content: "Description of Works:", styles: labelCellStyle },
            { content: record.descriptionOfWorks || record.locationDescription || "", colSpan: 2, styles: { halign: "center" } },
            { content: "Job Number:", styles: { ...labelCellStyle, halign: "center" } },
            { content: record.jobNumber || "", colSpan: 2, styles: { halign: "center" } },
          ],
          [
            { content: detailedPreWorkText, styles: labelCellStyle },
            { content: "No:", styles: optionCellStyle },
            { content: "Yes:", styles: optionCellStyle },
            { content: controlsPreWorkText, styles: labelCellStyle },
            { content: `Yes\n${controlsYesExtra}`, styles: optionCellStyle },
            { content: `No\n${controlsNoExtra}`, styles: optionCellStyle },
          ],
        ],
        columnStyles: {
          0: { cellWidth: s1Col(1.35) },
          1: { cellWidth: s1Col(0.65) },
          2: { cellWidth: s1Col(0.65) },
          3: { cellWidth: s1Col(1.35) },
          4: { cellWidth: s1Col(1.0) },
          5: { cellWidth: s1Col(1.0) },
        },
        styles: {
          font: "helvetica",
          fontSize: 9,
          cellPadding: 3,
          overflow: "linebreak",
          valign: "middle",
        },
        theme: "grid",
        tableWidth: section1TableW,
        margin: { left: section1Left },
        bodyStyles: {
          lineWidth: 0.2,
          textColor: [0, 0, 0],
          minCellHeight: 12,
        },
        didParseCell: (data) => {
          if (data.section === "body" && data.row.index === 2) {
            data.cell.styles.fontSize = 8;
          }
        },
        didDrawCell: (data) => {
          if (data.section !== "body" || data.row.index !== 2) return;

          const drawPowTickBesideText = (cell, labelText) => {
            doc.setFont("helvetica", "bold");
            doc.setFontSize(8);
            const lines = String(labelText).split("\n").filter((ln) => ln.length > 0);
            if (!lines.length) return;
            const lineHeight = 3.6;
            const blockH = lines.length * lineHeight;
            let maxW = 0;
            lines.forEach((line) => {
              maxW = Math.max(maxW, doc.getTextWidth(line));
            });
            const blockStartX = cell.x + (cell.width - maxW) / 2;
            const blockCenterY = cell.y + (cell.height - blockH) / 2 + blockH / 2;
            const tickSize = 3.5;
            let tickX = blockStartX + maxW + 1.5;
            tickX = Math.min(tickX, cell.x + cell.width - tickSize - 1);
            doc.addImage(processedCheckTick, "PNG", tickX, blockCenterY - tickSize / 2, tickSize, tickSize);
          };

          if (data.column.index === 1 && matchPowOption(detailedPreWorkValue, detailedOptions[detailedNoIdx >= 0 ? detailedNoIdx : 0])) {
            drawPowTickBesideText(data.cell, "No:");
          }
          if (data.column.index === 2 && matchPowOption(detailedPreWorkValue, detailedOptions[detailedYesIdx >= 0 ? detailedYesIdx : 1])) {
            drawPowTickBesideText(data.cell, "Yes:");
          }
          if (data.column.index === 4 && matchPowOption(controlsPreWorkValue, controlsOptions[controlsYesIdx >= 0 ? controlsYesIdx : 0])) {
            drawPowTickBesideText(data.cell, `Yes\n${controlsYesExtra}`);
          }
          if (data.column.index === 5 && matchPowOption(controlsPreWorkValue, controlsOptions[controlsNoIdx >= 0 ? controlsNoIdx : 1])) {
            drawPowTickBesideText(data.cell, `No\n${controlsNoExtra}`);
          }

          if (data.column.index === 3) {
            const text = String(controlsPreWorkText || "");
            if (!/fully/i.test(text)) return;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(8);
            const lines = doc.splitTextToSize(text, data.cell.width - 6);
            const lineHeight = 3.6;
            const blockH = lines.length * lineHeight;
            let textY = data.cell.y + (data.cell.height - blockH) / 2 + 2.8;
            const padX = data.cell.x + 3;
            lines.forEach((line) => {
              const lowerLine = String(line).toLowerCase();
              const fullyPos = lowerLine.indexOf("fully");
              if (fullyPos >= 0) {
                const before = line.slice(0, fullyPos);
                const wordW = doc.getTextWidth(line.slice(fullyPos, fullyPos + 5));
                const x = padX + (before ? doc.getTextWidth(before) : 0);
                doc.setLineWidth(0.2);
                doc.line(x, textY + 0.6, x + wordW, textY + 0.6);
              }
              textY += lineHeight;
            });
          }
        },
      });

      yPosition = doc.lastAutoTable.finalY + 2;
      
// ---- Section 2 : Identify Site Hazards ----
doc.setFont("helvetica", "bold");
doc.setTextColor(255, 255, 255);

// 🔹 Parse checklist data from API response
let checklistData = [];
try {
  if (record.checklist) {
    checklistData = parseSingleQuotedJSON(record.checklist) || [];
    if (!Array.isArray(checklistData)) {
      checklistData = [];
    }
  }
} catch (e) {
  console.error("Error parsing checklist:", e);
  checklistData = [];
}

// 🔹 Dynamic icon loading for hazard icons
const getHazardIcon = (imgPath) => {
  if (!imgPath) {
    // Return null if no path provided
    return null;
  }
  
  try {
    // Extract filename from API path (e.g., "assets/icons/s1.png" -> "s1.png")
    const filename = imgPath.split('/').pop();
    
    // Try to find the image in the context using the filename
    // The context keys are relative paths like "./s1.png" or "./icons/s1.png"
    // Match by filename (case-insensitive) to handle different path formats
    const imageKey = iconsContext.keys().find(key => {
      const keyFilename = key.split('/').pop().toLowerCase();
      return keyFilename === filename.toLowerCase();
    });
    
    if (imageKey) {
      // Return the image from the context
      return iconsContext(imageKey);
    } else {
      console.warn(`Icon not found for path: ${imgPath}, filename: ${filename}`);
      return null;
    }
  } catch (error) {
    console.warn(`Failed to load icon from path: ${imgPath}`, error);
    // Return null if image cannot be loaded
    return null;
  }
};

// 🔹 Get dynamic headers from first checklist item (if available)
let identifyHeader = "IDENTIFY: Site Hazards not managed in pre-work assessment";
let specifyHeader = "SPECIFY: Additional control measures required to manage the site hazards (include those on adjacent work areas)\n(use continuation sheet if required (must be noted/attached))";
let timeHeader = "TIME:\nControls in place";

if (checklistData.length > 0) {
  const firstItem = checklistData[0];
  if (firstItem.step1?.header) {
    identifyHeader = firstItem.step1.header;
  }
  if (firstItem.step2?.header && firstItem.step2?.items?.textBox?.hintText) {
    specifyHeader = `${firstItem.step2.header}: ${firstItem.step2.items.textBox.hintText}`;
  } else if (firstItem.step2?.header) {
    specifyHeader = firstItem.step2.header;
  }
  if (firstItem.step3?.header && firstItem.step3?.items?.textBox?.hintText) {
    timeHeader = `${firstItem.step3.header}:\n${firstItem.step3.items.textBox.hintText}`;
  } else if (firstItem.step3?.header) {
    timeHeader = `${firstItem.step3.header}:\nControls in place`;
  }
}

// ✅ Header row
autoTable(doc, {
  startY: yPosition,
  head: [
    [
      { content: "2", styles: { halign: "center", fontStyle: "bold", fontSize: 9, fillColor: [139, 22, 52], textColor: [255, 255, 255] } },
      { content: identifyHeader, styles: { halign: "center", fontStyle: "bold", fontSize: 9, fillColor: [139, 22, 52], textColor: [255, 255, 255] } },
      { content: specifyHeader, styles: { halign: "center", fontStyle: "bold", fontSize: 8, fillColor: [139, 22, 52], textColor: [255, 255, 255] } },
      { content: timeHeader, styles: { halign: "center", fontStyle: "bold", fontSize: 8, fillColor: [139, 22, 52], textColor: [255, 255, 255] } },
    ],
  ],
  theme: "plain",
  styles: { font: "helvetica" },
  tableWidth: pageWidth - 20,
  margin: { left: 10 },
  columnStyles: {
    0: { cellWidth: 10 },  // #
    1: { cellWidth: 28 },  // IDENTIFY
    2: { cellWidth: 120 }, // SPECIFY
    3: { cellWidth: 30 },  // TIME
  },
});

yPosition = doc.lastAutoTable.finalY;

// 🔹 Build hazards data from API response
const hazards = checklistData.map((item) => {
  const checkboxItems = item.step1?.items?.checkBox?.itemList || [];
  const allCheckboxItems = checkboxItems.map(cb => ({
    title: cb.title,
    value: cb.value === true
  }));
  const otherText = item.step1?.items?.textBox?.value || '';
  const otherHint = item.step1?.items?.textBox?.hintText || 'Other:';
  if (item.step1?.items?.textBox?.isTextBoxShow) {
    allCheckboxItems.push({
      title: otherText ? `${otherHint} : ${otherText}` : otherHint,
      value: otherText && otherText.trim() ? true : false,
      isTextBox: true  // Mark as textbox item - don't show checkTick
    });
  }
  const specifyValue = item.step2?.items?.textBox?.value || '';
  const timeValue = item.step3?.items?.textBox?.value || '';
  return {
    title: item.title || '',
    icon: getHazardIcon(item.img),
    checkboxItems: allCheckboxItems.length > 0 ? allCheckboxItems : [{ title: '', value: false }],
    specifyValue: specifyValue,
    timeValue: timeValue,
  };
});

// ✅ Build table body
const tableBody = hazards.map((h) => [
  { content: "", styles: { halign: "center", valign: "middle" } },
  { content: "", styles: { valign: "top" } },
  { content: h.specifyValue || "", styles: { valign: "top" } },
  { content: h.timeValue || "", styles: { valign: "top" } },
]);

// ✅ Draw hazard table
autoTable(doc, {
  startY: yPosition + 0.5, // ✅ reduced spacing between header and first row
  body: tableBody,
  willDrawCell: (data) => {
    if (data.section === "body") {
      const hazard = hazards[data.row.index];
      
      // Initialize height if not already calculated for this row
      if (!data.row.height || data.row.height === 28) {
        let calculatedHeight = 28; // default height
        
        // Calculate height for column 0 (icon + title)
        if (hazard && hazard.title) {
          doc.setFontSize(6.5);
          const col0Width = 26; // Column 0 width from columnStyles
          const maxTextWidth = col0Width - 4; // Leave padding
          const textLines = doc.splitTextToSize(hazard.title, maxTextWidth);
          const iconHeight = 8; // icon size
          const iconToTextSpacing = 4;
          const textLineHeight = 3.5;
          const titleHeight = iconHeight + iconToTextSpacing + (textLines.length * textLineHeight);
          calculatedHeight = Math.max(calculatedHeight, titleHeight + 8); // Add padding
        }
        
        // Calculate height for column 1 (checkbox items)
        if (hazard && hazard.checkboxItems) {
          doc.setFontSize(8.2);
          const col1Width = 50; // Column 1 width from columnStyles
          const maxTextWidth = col1Width - 12;
    
          // 🔹 Slightly increase base padding
          let totalHeight = 8; 
          const lineHeight = 3.8;       // increased spacing
          const textLineHeight = 4.0;   // increased for larger font
    
          hazard.checkboxItems.forEach((item, index) => {
            if (item.title && item.title.trim()) {
              const textLines = doc.splitTextToSize(item.title, maxTextWidth);
              totalHeight += textLines.length * textLineHeight;
    
              // 🔹 Add space only if next item exists and has text
              const nextItem = hazard.checkboxItems[index + 1];
              if (nextItem && nextItem.title && nextItem.title.trim()) {
                totalHeight += lineHeight;
              }
            }
          });
    
          calculatedHeight = Math.max(calculatedHeight, totalHeight);
        }
        
        data.row.height = calculatedHeight;
      }
    }
  },
  
  
  didDrawCell: (data) => {
    const { cell, row } = data;
    const hazard = hazards[row.index];
    if (data.column.index === 0 && hazard && hazard.icon) {
      const iconSize = 8;
      const iconX = cell.x + (cell.width - iconSize) / 2;
      const iconY = cell.y + 4;
      // Only add image if icon exists (not null)
      if (hazard.icon) {
        doc.addImage(hazard.icon, "PNG", iconX, iconY, iconSize, iconSize);
      }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.5);
      doc.setTextColor(0, 0, 0);
      const text = hazard.title;
      // Wrap text to fit within cell width (with padding)
      const maxTextWidth = cell.width - 4; // Leave 2px padding on each side
      const textLines = doc.splitTextToSize(text, maxTextWidth);
      const lineHeight = 3.5;
      const startY = iconY + iconSize + 4;
      // Center each line of text
      textLines.forEach((line, index) => {
        const textWidth = doc.getTextWidth(line);
        const textX = cell.x + (cell.width - textWidth) / 2;
        const textY = startY + (index * lineHeight);
        doc.text(line, textX, textY);
      });
    }
    if (data.column.index === 1 && hazard && hazard.checkboxItems) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.2);
      doc.setTextColor(0, 0, 0);
      const startY = cell.y + 3;
      const checkmarkSize = 3;
      const textStartX = cell.x + 3;
      const maxTextWidth = cell.width - 12;
      const lineHeight = 3.3;  // increased spacing between items
      const textLineHeight = 3.5;
      let currentY = startY;
      hazard.checkboxItems.forEach((item, index) => {
        if (item.title && item.title.trim()) {
          const textLines = doc.splitTextToSize(item.title, maxTextWidth);
          textLines.forEach((line, lineIndex) => {
            doc.text(line, textStartX, currentY);
            // Only show checkTick for checkbox items, not for textbox items
            if (lineIndex === 0 && item.value === true && !item.isTextBox) {
              const textWidth = doc.getTextWidth(line);
              const checkmarkX = textStartX + textWidth + 2;
              // Align checkmark center with text visual center (font size 8.2)
              // Text baseline is at currentY, visual center is approximately at currentY - fontSize * 0.3
              // Checkmark center should align with text center, so top-left is at center - half size
              const fontSize = 8.2;
              const textCenterY = currentY - (fontSize * 0.3);
              const checkmarkY = textCenterY - (checkmarkSize / 2) + 1.0;
              doc.addImage(processedCheckTick, "PNG", checkmarkX, checkmarkY, checkmarkSize, checkmarkSize);
            }
            if (lineIndex < textLines.length - 1) currentY += textLineHeight;
          });
          currentY += lineHeight;
        }
      });
    }
  },
  styles: {
    font: "helvetica",
    fontSize: 8.2,
    cellPadding: 2,
    lineWidth: 0.2,
    textColor: [0, 0, 0],
    valign: "middle",
  },
  theme: "grid",
  tableWidth: pageWidth - 20,
  margin: { left: 10 },
  columnStyles: {
    0: { cellWidth: 26 },
    1: { cellWidth: 50 },
    2: { cellWidth: 85 },
    3: { cellWidth: 29 },
  },
  bodyStyles: { minCellHeight: 30 },
  rowPageBreak: "avoid",
});

// ✅ Reduced space between sections so rows join perfectly
yPosition = doc.lastAutoTable.finalY; 

     // ---- Section 3 : Details of Emergency Procedures ----
// 🔹 Parse detailsofEmergencyProcedures data from API response
let emergencyProceduresData = { header: "", items: [] };
try {
  if (record.detailsofEmergencyProcedures) {
    emergencyProceduresData = parseSingleQuotedJSON(record.detailsofEmergencyProcedures) || { header: "", items: [] };
    if (!emergencyProceduresData.header) {
      emergencyProceduresData.header = "";
    }
    if (!Array.isArray(emergencyProceduresData.items)) {
      emergencyProceduresData.items = [];
    }
  }
} catch (e) {
  console.error("Error parsing detailsofEmergencyProcedures:", e);
  emergencyProceduresData = { header: "", items: [] };
}

doc.setFillColor(139, 22, 52); // header color same as others
doc.setTextColor(255, 255, 255);
doc.setFont("helvetica", "bold");

// 🔹 Header bar
doc.rect(10, yPosition, pageWidth - 20, 8, "F");
doc.text("3", 14, yPosition + 5.5);
// Show header dynamically from API, only if available
if (emergencyProceduresData.header) {
  doc.text(emergencyProceduresData.header, 22, yPosition + 5.5);
}

// 🔹 Helper function to get cell content for an item
const getItemContent = (item) => {
  let content = "";
  
  // Handle multipleTextBox - show hintText and value for each textField
  if (item.multipleTextBox && item.multipleTextBox.isMultipleTextBoxShow && item.multipleTextBox.textFields) {
    const textFields = item.multipleTextBox.textFields || [];
    content = textFields.map((field) => {
      const hintText = field.hintText || "";
      const value = field.value || "";
      return `${hintText}: ${value}`;
    }).filter(item => item.trim() !== "").join("\n");
  }
  // Handle textBox - show hintText and value if hintText is available
  else if (item.textBox && item.textBox.isTextBoxShow) {
    const hintText = item.textBox.hintText || "";
    const value = item.textBox.value || "";
    if (hintText) {
      content = `${hintText}: ${value}`;
    } else {
      content = value;
    }
  }
  // Handle radioBox - show all options (CheckTick will be added after table rendering)
  else if (item.radioBox && item.radioBox.isRadioBoxShow) {
    const itemList = item.radioBox.itemList || [];
    const radioOptions = itemList.map(opt => {
      const optionText = opt.title || opt.value || "";
      return optionText;
    }).join("\n\n"); // Add extra line break between items for more spacing
    content = radioOptions;
    
    // If there's a textBox attached to radioBox (for "Other" option)
    if (item.textBox && item.textBox.isTextBoxShow) {
      const hintText = item.textBox.hintText || "";
      const value = item.textBox.value || "";
      if (hintText) {
        content += `\n\n${hintText}: ${value}`;
      } else if (value) {
        content += `\n\n${value}`;
      }
    }
  }
  
  return content || "";
};

// 🔹 Build dynamic tables from items array
yPosition += 8;

if (emergencyProceduresData.items.length > 0) {
  // Group items into rows of 3 columns
  const itemsPerRow = 3;
  const rows = [];
  const radioBoxItems = []; // Track radioBox items for CheckTick rendering
  
  for (let i = 0; i < emergencyProceduresData.items.length; i += itemsPerRow) {
    const rowItems = emergencyProceduresData.items.slice(i, i + itemsPerRow);
    const headRow = [];
    const bodyRow = [];
    
    rowItems.forEach((item, colIndex) => {
      const title = item.title || "";
      const content = getItemContent(item);
      
      // Track radioBox items for CheckTick rendering
      if (item.radioBox && item.radioBox.isRadioBoxShow && item.radioBox.selectedValue) {
        radioBoxItems.push({
          rowIndex: rows.length,
          colIndex: colIndex,
          selectedValue: item.radioBox.selectedValue,
          itemList: item.radioBox.itemList || []
        });
      }
      
      headRow.push({
        content: title,
        styles: { halign: "center", fillColor: [240, 240, 240], fontStyle: "bold" }
      });
      bodyRow.push({
        content: content,
        styles: { valign: "top" }
      });
    });
    
    // Fill remaining columns if less than 3 items
    while (headRow.length < itemsPerRow) {
      headRow.push({
        content: "",
        styles: { halign: "center", fillColor: [240, 240, 240], fontStyle: "bold" }
      });
      bodyRow.push({
        content: "",
        styles: { valign: "top" }
      });
    }
    
    rows.push({ head: headRow, body: bodyRow });
  }
  
  // Render each row as a separate table
  rows.forEach((row, rowIndex) => {
    autoTable(doc, {
      startY: yPosition,
      head: [row.head],
      body: [row.body],
      styles: {
        font: "helvetica",
        fontSize: 9,
        cellPadding: 4,
        halign: "left",
        valign: "middle",
      },
      theme: "grid",
      tableWidth: pageWidth - 20,
      margin: { left: 10 },
      headStyles: {
        textColor: [0, 0, 0],
        fillColor: [245, 245, 245],
        lineWidth: 0.2,
      },
      bodyStyles: {
        lineWidth: 0.2,
      },
    });
    
    // Add CheckTick images for radioBox items in this row
    const tableData = doc.lastAutoTable;
    radioBoxItems.forEach((radioBoxItem) => {
      if (radioBoxItem.rowIndex === rowIndex && tableData.body && tableData.body[0]) {
        const cell = tableData.body[0].cells[radioBoxItem.colIndex];
        if (cell) {
          const itemList = radioBoxItem.itemList;
          const selectedValue = radioBoxItem.selectedValue;
          
          // Find the index of the selected option in the itemList
          let selectedIndex = -1;
          itemList.forEach((opt, idx) => {
            const optionText = opt.title || opt.value || "";
            const optionValue = opt.value || optionText;
            if (optionText === selectedValue || optionValue === selectedValue) {
              selectedIndex = idx;
            }
          });
          
          if (selectedIndex >= 0) {
            // Calculate position for CheckTick next to the selected option text
            doc.setFontSize(9);
            doc.setFont("helvetica", "normal");
            
            const fontSize = 9;
            const padding = 4; // cellPadding
            const checkTickSize = 4;
            const totalLines = itemList.length;
            
            // Calculate line height based on actual cell dimensions
            // With double newlines (\n\n), spacing between items is increased
            // This gives us the actual spacing autoTable uses between items
            const cellHeight = cell.height;
            const availableHeight = cellHeight - (padding * 2); // Subtract top and bottom padding
            // Since we're using double newlines, each item has more spacing
            // We calculate the spacing between items (not individual lines)
            const itemSpacing = availableHeight / totalLines; // Spacing between each item
            
            // Calculate Y position for the selected option
            // For autoTable with valign: "top" and fontSize 9:
            // First item text baseline is at: cell.y + padding + fontSize * 0.65
            // Text center (middle of text) is at: baseline - fontSize * 0.35
            // Adjusting to move up more to align with text
            const firstItemBaseline = cell.y + padding + (fontSize * 0.65);
            const firstItemCenterY = firstItemBaseline - (fontSize * 0.35);
            
            // Calculate selected item center Y
            // For selectedIndex 0: first item ("At point of work")
            // For selectedIndex 1: second item ("In Vehicle") - this is the selected one
            // For selectedIndex 2: third item ("Other")
            // Each item is separated by itemSpacing (which accounts for double newlines)
            // Adjusting to move up more to align with "In Vehicle"
            const selectedItemCenterY = firstItemCenterY + (selectedIndex * itemSpacing) - 2;
            
            // Position CheckTick on the right side of the text
            // Get the text content for the selected option to calculate width
            const selectedOptionText = itemList[selectedIndex]?.title || itemList[selectedIndex]?.value || "";
            const textWidth = doc.getTextWidth(selectedOptionText);
            
            // Position CheckTick to the right of the text
            const checkTickX = cell.x + padding + textWidth + 3; // 3px spacing after text
            const checkTickY = selectedItemCenterY - (checkTickSize / 2) + 1; // Center vertically on text line, slightly down
            
            // Add CheckTick image
            doc.addImage(processedCheckTick, "PNG", checkTickX, checkTickY, checkTickSize, checkTickSize);
          }
        }
      }
    });
    
    yPosition = doc.lastAutoTable.finalY;
  });
} else {
  // Fallback: Show empty table if no data
  autoTable(doc, {
    startY: yPosition,
    head: [
      [
        { content: "", styles: { halign: "center", fillColor: [240, 240, 240], fontStyle: "bold" } },
        { content: "", styles: { halign: "center", fillColor: [240, 240, 240], fontStyle: "bold" } },
        { content: "", styles: { halign: "center", fillColor: [240, 240, 240], fontStyle: "bold" } },
      ],
    ],
    body: [["", "", ""]],
    styles: {
      font: "helvetica",
      fontSize: 9,
      cellPadding: 4,
      halign: "left",
      valign: "middle",
    },
    theme: "grid",
    tableWidth: pageWidth - 20,
    margin: { left: 10 },
    headStyles: {
      textColor: [0, 0, 0],
      fillColor: [245, 245, 245],
      lineWidth: 0.2,
    },
    bodyStyles: {
      lineWidth: 0.2,
    },
  });
  yPosition = doc.lastAutoTable.finalY;
}

yPosition = doc.lastAutoTable.finalY + 2;

const margin = 10;     // 10 mm margin left and right
const usableWidth = pageWidth - margin * 2; // 190 mm max table width

// Parse safeCheckList from API response
let safeCheckListData = null;
try {
  if (record.safeCheckList) {
    safeCheckListData = parseSingleQuotedJSON(record.safeCheckList) || {};
  }
} catch (e) {
  safeCheckListData = {};
}

// Get header from API or use default
const section4Header = safeCheckListData?.header || "PPE tick as appropriate";
const section4Items = safeCheckListData?.items || [];
const detailPpeRequired = safeCheckListData?.detailPpeRequired || {};

// Helper function to get image for PDF
const getPdfImage = (imagePath) => {
  if (!imagePath) return null;
  const imageMap = {
    'assets/icons/1.png': icon1,
    'assets/icons/2.png': icon2,
    'assets/icons/3.png': icon3,
    'assets/icons/4.png': icon4,
    'assets/icons/5.png': icon5,
    'assets/icons/6.png': icon6,
    'assets/icons/7.png': icon7,
    'assets/icons/8.png': icon8,
    'assets/icons/9.jpg': icon9,
    'assets/icons/9.png': icon9,
  };
  return imageMap[imagePath] || null;
};

// Build dynamic table structure from API data
const numColumns = Math.max(section4Items.length, 9); // At least 9 columns
const leftColWidth = 30;
const otherColsWidth = (usableWidth - leftColWidth) / numColumns;

// Build column styles dynamically
const columnStyles = {
  0: { cellWidth: leftColWidth, fillColor: [107, 44, 92], textColor: [255, 255, 255], fontStyle: "bold" }
};
for (let i = 1; i <= numColumns; i++) {
  columnStyles[i] = { cellWidth: otherColsWidth };
}

// Build icon row (grey placeholders) - ensure enough height for icons
const iconRow = [""];
for (let i = 0; i < numColumns; i++) {
  iconRow.push({ 
    content: " ", // Space placeholder - icon will be drawn in didDrawCell
    styles: { 
      fillColor: [230, 230, 230],
      cellPadding: { top: 3, bottom: 3, left: 2, right: 2 }, // Add padding for icon space
      minCellHeight: 12 // Try to set minimum height
    } 
  });
}

// Build red sub-header row from first checkbox item of each column
// Also store checkbox values for check-tick drawing
const subHeaderCheckboxMap = {}; // Store sub-header checkbox values: {colIndex: {title, value}}
const subHeaderRow = [""];
for (let i = 0; i < numColumns; i++) {
  const item = section4Items[i];
  if (item && item.checkBox && item.checkBox.itemList && item.checkBox.itemList.length > 0) {
    const firstCheckbox = item.checkBox.itemList[0];
    subHeaderRow.push({
      content: firstCheckbox.title || "",
      styles: { fillColor: [255, 0, 0], textColor: [255, 255, 255], fontStyle: "bold" }
    });
    // Store checkbox data for check-tick
    subHeaderCheckboxMap[i + 1] = {
      title: firstCheckbox.title,
      value: firstCheckbox.value === true
    };
  } else {
    subHeaderRow.push({ content: "", styles: { fillColor: [255, 0, 0], textColor: [255, 255, 255], fontStyle: "bold" } });
  }
}

// Build data rows from checkbox items (max 3 rows per column)
// First, find the maximum number of checkbox items per column (excluding first item which is in header)
let maxDataRows = 0;
for (let i = 0; i < section4Items.length; i++) {
  const item = section4Items[i];
  if (item && item.checkBox && item.checkBox.itemList) {
    const checkboxItems = item.checkBox.itemList;
    // Exclude first item (it's in header), count remaining
    const remainingItems = Math.max(0, checkboxItems.length - 1);
    maxDataRows = Math.max(maxDataRows, remainingItems);
  }
}
maxDataRows = Math.min(maxDataRows, 3); // Limit to 3 rows as per original design

// Initialize data rows
const dataRows = [];
for (let rowIdx = 0; rowIdx < maxDataRows; rowIdx++) {
  dataRows[rowIdx] = [""]; // First column is empty
}

// Fill data rows column by column - store both title and value for check-tick
const checkboxDataMap = {}; // Store checkbox data: {rowIndex_colIndex: {title, value}}
for (let colIdx = 0; colIdx < numColumns; colIdx++) {
  const item = section4Items[colIdx];
  if (item && item.checkBox && item.checkBox.itemList) {
    const checkboxItems = item.checkBox.itemList;
    // Skip first item (already in header), get remaining items
    for (let rowIdx = 0; rowIdx < maxDataRows; rowIdx++) {
      const checkboxItem = checkboxItems[rowIdx + 1]; // Skip first item
      if (checkboxItem) {
        dataRows[rowIdx].push(checkboxItem.title || "");
        // Store checkbox data for check-tick drawing
        // Key format: rowIndex_colIndex (both 1-based to match table structure)
        checkboxDataMap[`${rowIdx + 1}_${colIdx + 1}`] = {
          title: checkboxItem.title,
          value: checkboxItem.value === true
        };
      } else {
        dataRows[rowIdx].push("");
      }
    }
  } else {
    // Fill with empty strings if no data
    for (let rowIdx = 0; rowIdx < maxDataRows; rowIdx++) {
      dataRows[rowIdx].push("");
    }
  }
}

// Ensure we have at least 3 rows for consistency (fill with empty if needed)
while (dataRows.length < 3) {
  const newRow = [""];
  for (let i = 0; i < numColumns; i++) {
    newRow.push("");
  }
  dataRows.push(newRow);
}

// Empty row
const emptyRow = [""];
for (let i = 0; i < numColumns; i++) {
  emptyRow.push("");
}

// Other PPE required row - get hint text from first item with textBox
let otherPpeText = "Other PPE required";
if (section4Items.length > 0 && section4Items[0].textBox && section4Items[0].textBox.hintText) {
  otherPpeText = section4Items[0].textBox.hintText;
}
const otherPpeRow = [
  {
    content: otherPpeText,
    styles: { halign: "left", fillColor: [230, 230, 230], textColor: [0, 0, 0], fontSize: 8, fontStyle: "bold" },
  }
];
for (let i = 0; i < numColumns; i++) {
  otherPpeRow.push("");
}

// Justification row - get from detailPpeRequired API (no static fallback)
let justificationText = "";
if (detailPpeRequired && detailPpeRequired.textBox && detailPpeRequired.textBox.hintText) {
  justificationText = detailPpeRequired.textBox.hintText;
}
const justificationRow = [
  {
    content: justificationText,
    styles: { halign: "left", fillColor: [255, 0, 0], textColor: [255, 255, 255], fontSize: 8, fontStyle: "bold" },
  },
  {
    content: "",
    colSpan: numColumns,
    styles: { fillColor: [255, 0, 0], textColor: [255, 255, 255] },
  },
];

// Build header row with dynamic header (after calculating maxDataRows)
const headerContent = `4\n${section4Header.split(' ')[0] || 'PPE'}\n${section4Header.split(' ').slice(1).join(' ') || 'tick as appropriate'}`;
// Calculate rowSpan: icon row (1) + sub-header row (1) + data rows (maxDataRows) + empty row (1) + otherPpeRow (1) + justificationRow (1) = 5 + maxDataRows
const totalBodyRows = 1 + 1 + maxDataRows + 1 + 1 + 1; // icon + subheader + data + empty + otherPpe + justification
const headerRow = [
  { content: headerContent, rowSpan: totalBodyRows, styles: { fontSize: 8, halign: "center", valign: "middle", lineWidth: 0.2, fillColor: [107, 44, 92], textColor: [255, 255, 255] } }
];
for (let i = 0; i < numColumns; i++) {
  headerRow.push("");
}

// Build table body for Section 4
const section4TableBody = [
  iconRow,
  subHeaderRow,
  ...dataRows,
  emptyRow,
  otherPpeRow,
  justificationRow,
];

// PPE Table
autoTable(doc, {
  startY: yPosition,
  theme: "grid",
  tableWidth: pageWidth - 20,
  margin: { left: 10 },
  styles: {
    font: "helvetica",
    fontSize: 7,
    halign: "center",
    valign: "middle",
    cellPadding: 2,
    lineWidth: 0.1,
  },
  columnStyles: columnStyles,
  head: [headerRow],
  body: section4TableBody,
  didParseCell: (data) => {
    // Increase height for icon row (first body row) to accommodate icons
    if (data.section === "body" && data.row.index === 0) {
      // Set minimum row height for icon row
      if (!data.row.height || data.row.height < 12) {
        data.row.height = 12; // Minimum 12mm height for icons
      }
      // Also ensure cell has enough height
      if (data.cell && (!data.cell.height || data.cell.height < 12)) {
        data.cell.height = 12;
      }
    }
  },
  didDrawCell: (data) => {
    const cell = data.cell;
    const rowIndex = data.row.index;
    const colIndex = data.column.index;
    
    // Draw PPE icons in icon row (first body row, index 0)
    if (data.section === "body" && rowIndex === 0 && colIndex > 0) {
      const itemColIndex = colIndex - 1;
      const item = section4Items[itemColIndex];
      if (item && item.img) {
        const pdfImage = getPdfImage(item.img);
        if (pdfImage) {
          try {
            // Calculate image size based on available cell space (with padding)
            const availableHeight = cell.height - 4; // Account for padding
            const availableWidth = cell.width - 4;
            const imgSize = Math.min(8, Math.min(availableHeight, availableWidth)); // Use smaller size to fit properly
            
            // Center image in cell, accounting for padding
            const centerX = cell.x + cell.width / 2;
            const centerY = cell.y + cell.height / 2;
            
            // Draw image centered in cell
            doc.addImage(pdfImage, "PNG", centerX - imgSize / 2, centerY - imgSize / 2, imgSize, imgSize);
          } catch (e) {
            // Image load failed, skip
            console.warn("Failed to draw image:", e);
          }
        }
      }
    }
    
    // Draw check-tick in sub-header row (second body row, index 1) if value is true
    if (data.section === "body" && rowIndex === 1 && colIndex > 0) {
      const checkboxData = subHeaderCheckboxMap[colIndex];
      if (checkboxData && checkboxData.value === true) {
        try {
          const tickSize = 3; // Small check-tick size
          const centerX = cell.x + cell.width / 2;
          const topY = cell.y + 2; // Position near top, above text
          doc.addImage(processedCheckTick, "PNG", centerX - tickSize / 2, topY, tickSize, tickSize);
        } catch (e) {
          console.warn("Failed to draw check-tick:", e);
        }
      }
    }
    
    // Draw check-tick in data rows (rows after sub-header) if value is true
    // Data rows start from index 2 (after icon row at 0 and sub-header row at 1)
    if (data.section === "body" && rowIndex >= 2 && colIndex > 0) {
      const dataRowIndex = rowIndex - 2; // Convert to data row index (0-based)
      // Match the key format: rowIndex_colIndex (both 1-based)
      const checkboxKey = `${dataRowIndex + 1}_${colIndex}`;
      const checkboxData = checkboxDataMap[checkboxKey];
      if (checkboxData && checkboxData.value === true) {
        try {
          const tickSize = 3; // Small check-tick size
          const centerX = cell.x + cell.width / 2;
          const topY = cell.y + 2; // Position near top, above text
          doc.addImage(processedCheckTick, "PNG", centerX - tickSize / 2, topY, tickSize, tickSize);
        } catch (e) {
          console.warn("Failed to draw check-tick:", e);
        }
      }
    }
  },
});

yPosition = doc.lastAutoTable.finalY + 4;

// Section 5: Communication - Parse from API
let communicationList = [];
try {
  if (record.communicationList) {
    // communicationList is a JSON string array
    if (typeof record.communicationList === 'string') {
      communicationList = JSON.parse(record.communicationList);
    } else if (Array.isArray(record.communicationList)) {
      communicationList = record.communicationList;
    }
  }
} catch (e) {
  console.warn("Failed to parse communicationList:", e);
  communicationList = [];
}

// Build communication data dynamically from API
const communicationData = [
  [
    { content: "5", styles: { fontStyle: 'bold', fontSize: 10, halign: 'center', fillColor: [220, 220, 220], textColor: [0, 0, 0], cellPadding: 3 } },
    { content: "Communication", styles: { fontStyle: 'bold', fontSize: 10, halign: 'left', fillColor: primaryColor, textColor: [255, 255, 255], cellPadding: 3 } }
  ],
  // Add items from API dynamically
  ...communicationList.map((item, index) => [
    `${index + 1}.`,
    item || "" // Use API item or empty string
  ])
];

autoTable(doc, {
  startY: yPosition,
  body: communicationData,
  theme: 'grid',
  styles: { fontSize: 9, cellPadding: 3, lineColor: [180, 180, 180], lineWidth: 0.4, textColor: [0, 0, 0], fontStyle: 'normal' },
  columnStyles: {
    0: { halign: 'center', cellWidth: 15 },
    1: { halign: 'left', cellWidth: 175 } // 🔹 Adjusted to match total width ~190
  },
  tableWidth: 190, // 🔹 Match Section 6 width
  margin: { left: 10, right: 10 },
  didParseCell: (data) => {
    if (data.row.index === 0) {
      data.cell.styles.fillColor = primaryColor;
      data.cell.styles.textColor = [255, 255, 255];
      data.cell.styles.fontStyle = 'bold';
    }
  }
});

yPosition = doc.lastAutoTable.finalY + 2;

      
     // ---- Section 6 : Signatories ----

// 🔹 Full-width header bar
doc.setFillColor(139, 22, 52); // Dark magenta
doc.setTextColor(255, 255, 255);
doc.setFont("helvetica", "bold");
doc.rect(10, yPosition, pageWidth - 20, 8, "F");
doc.text("6", 14, yPosition + 5.5);
doc.text("Signatories", 22, yPosition + 5.5);

// Helper function to parse array values and return as array
const parseArrayToItems = (value) => {
  if (!value) return [''];
  if (typeof value !== 'string') return [String(value)];
  
  // Remove brackets and split by comma
  const cleaned = value.replace(/^\[|\]$/g, '').trim();
  if (!cleaned) return [''];
  
  // Split by comma and clean each item
  const items = cleaned.split(',').map(item => item.trim()).filter(item => item);
  return items.length > 0 ? items : [''];
};

// Helper function to extract signature array and return base64 data
const parseSignatureArray = (signatureData) => {
  if (!signatureData) return [''];
  try {
    let parsed = signatureData;
    
    // Handle array format like "[base64,...]" or escaped JSON like "[\\\"base64\\\"]"
    if (typeof parsed === 'string' && parsed.trim().startsWith('[')) {
      try {
        // Try to parse as JSON array first (handles escaped JSON like "[\\\"base64\\\"]")
        const jsonParsed = JSON.parse(parsed);
        if (Array.isArray(jsonParsed) && jsonParsed.length > 0) {
          return jsonParsed.map(sig => {
            // Return the actual base64 data
            if (sig && typeof sig === 'string') {
              // Remove any escaped quotes or surrounding quotes
              let cleanSig = sig.replace(/^\\?"|\\?"$/g, '');
              cleanSig = cleanSig.replace(/^"|"$/g, ''); // Remove any remaining quotes
              return cleanSig || '';
            }
            return sig || '';
          });
        } else {
          // Empty array
          return [''];
        }
      } catch (e) {
        // If JSON parsing fails, try to parse manually
        // Handle format like "[\\\"base64...\\\",\\\"base64...]\""
        // First, try to extract the array content
        const match = parsed.match(/^\[(.*)\]$/);
        if (match) {
          const content = match[1];
          // Split by comma but handle escaped quotes
          const items = [];
          let current = '';
          let inQuotes = false;
          let escapeNext = false;
          
          for (let i = 0; i < content.length; i++) {
            const char = content[i];
            
            if (escapeNext) {
              current += char;
              escapeNext = false;
            } else if (char === '\\') {
              escapeNext = true;
              current += char;
            } else if (char === '"') {
              inQuotes = !inQuotes;
              // Don't add quote to current
            } else if (char === ',' && !inQuotes) {
              items.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          if (current.trim()) {
            items.push(current.trim());
          }
          
          return items.map(item => {
            // Remove escaped quotes and regular quotes
            let cleanItem = item.replace(/^\\?"|\\?"$/g, '');
            cleanItem = cleanItem.replace(/^"|"$/g, '');
            return cleanItem || '';
          });
        }
        
        // Fallback: use parseArrayToItems
        const items = parseArrayToItems(parsed);
        return items.map(item => {
          // Remove escaped quotes if present
          let cleanItem = item.replace(/^\\?"|\\?"$/g, '');
          cleanItem = cleanItem.replace(/^"|"$/g, '');
          return cleanItem || '';
        });
      }
    }
    
    // If not an array format, try to parse as array
    const items = parseArrayToItems(parsed);
    return items.map(item => {
      // Remove escaped quotes if present
      let cleanItem = item.replace(/^\\?"|\\?"$/g, '');
      cleanItem = cleanItem.replace(/^"|"$/g, '');
      return cleanItem || '';
    });
  } catch (e) {
    console.error('Error parsing signature array:', e, signatureData);
    return [''];
  }
};

// Parse PICOS data into arrays
let picosDataRows = [];
let picosTakingOverRows = [];

try {
  if (record.personInChargeofSafety) {
    const parsed = typeof record.personInChargeofSafety === 'string' 
      ? JSON.parse(record.personInChargeofSafety) 
      : record.personInChargeofSafety;
    
    const names = parseArrayToItems(parsed.name || '');
    const dates = parseArrayToItems(parsed.date || '');
    const times = parseArrayToItems(parsed.time || '');
    const signatures = parseSignatureArray(parsed.signature || '');
    
    // Create rows - use names as primary length, match other fields by index
    // Each name gets its corresponding date, time, signature by index
    // Find the first valid signature to use for the first row
    let firstValidSignature = '';
    for (let j = 0; j < signatures.length; j++) {
      if (signatures[j] != null) {
        const sigVal = signatures[j];
        if (typeof sigVal === 'string') {
          if (sigVal.trim().length > 0) {
            firstValidSignature = sigVal;
            break;
          }
        } else if (sigVal) {
          firstValidSignature = String(sigVal);
          break;
        }
      }
    }
    
    // Count valid signatures
    const validSignatureCount = signatures.filter(sig => {
      if (sig == null) return false;
      if (typeof sig === 'string') return sig.trim().length > 0;
      return true;
    }).length;
    
    // Assign signatures by index - if only 1 valid signature exists, ensure it goes to first name (index 0)
    for (let i = 0; i < names.length; i++) {
      let signature = '';
      
      // Get signature at the same index as the name
      if (i < signatures.length && signatures[i] != null) {
        const sigValue = signatures[i];
        if (typeof sigValue === 'string') {
          const trimmed = sigValue.trim();
          if (trimmed.length > 0) {
            signature = sigValue; // Use original value (preserve base64)
          }
        } else {
          signature = String(sigValue);
        }
      }
      
      // Special case: if only 1 valid signature exists and first row has no signature, use the first valid one
      if (i === 0 && !signature && firstValidSignature && validSignatureCount === 1) {
        signature = firstValidSignature;
      }
      // If no signature at index i, signature remains empty (blank cell)
      
      picosDataRows.push({
        name: names[i] || '',
        date: (dates.length > i && dates[i]) ? dates[i] : '',
        time: (times.length > i && times[i]) ? times[i] : '',
        signature: signature
      });
    }
  }
} catch (e) {
  console.error('Error parsing personInChargeofSafety:', e);
  // Add at least one empty row
  if (picosDataRows.length === 0) {
    picosDataRows.push({ name: '', date: '', time: '', signature: '' });
  }
}

try {
  if (record.PersonTakingOverAsPICOS) {
    const parsed = typeof record.PersonTakingOverAsPICOS === 'string' 
      ? JSON.parse(record.PersonTakingOverAsPICOS) 
      : record.PersonTakingOverAsPICOS;
    
    const names = parseArrayToItems(parsed.name || '');
    const dates = parseArrayToItems(parsed.date || '');
    const times = parseArrayToItems(parsed.time || '');
    const signatures = parseSignatureArray(parsed.signature || '');
    
    // Create rows - use names as primary length, match other fields by index
    // Each name gets its corresponding date, time, signature by index
    // Find the first valid signature to use for the first row
    let firstValidSignature = '';
    for (let j = 0; j < signatures.length; j++) {
      if (signatures[j] != null) {
        const sigVal = signatures[j];
        if (typeof sigVal === 'string') {
          if (sigVal.trim().length > 0) {
            firstValidSignature = sigVal;
            break;
          }
        } else if (sigVal) {
          firstValidSignature = String(sigVal);
          break;
        }
      }
    }
    
    // Count valid signatures
    const validSignatureCount = signatures.filter(sig => {
      if (sig == null) return false;
      if (typeof sig === 'string') return sig.trim().length > 0;
      return true;
    }).length;
    
    // Assign signatures by index - if only 1 valid signature exists, ensure it goes to first name (index 0)
    for (let i = 0; i < names.length; i++) {
      let signature = '';
      
      // Get signature at the same index as the name
      if (i < signatures.length && signatures[i] != null) {
        const sigValue = signatures[i];
        if (typeof sigValue === 'string') {
          const trimmed = sigValue.trim();
          if (trimmed.length > 0) {
            signature = sigValue; // Use original value (preserve base64)
          }
        } else {
          signature = String(sigValue);
        }
      }
      
      // Special case: if only 1 valid signature exists and first row has no signature, use the first valid one
      if (i === 0 && !signature && firstValidSignature && validSignatureCount === 1) {
        signature = firstValidSignature;
      }
      // If no signature at index i, signature remains empty (blank cell)
      
      picosTakingOverRows.push({
        name: names[i] || '',
        date: (dates.length > i && dates[i]) ? dates[i] : '',
        time: (times.length > i && times[i]) ? times[i] : '',
        signature: signature
      });
    }
  }
} catch (e) {
  console.error('Error parsing PersonTakingOverAsPICOS:', e);
  // Add at least one empty row
  if (picosTakingOverRows.length === 0) {
    picosTakingOverRows.push({ name: '', date: '', time: '', signature: '' });
  }
}

// Build table body with separate rows for each entry
// Store signature images separately for rendering
const picosTableBody = [];
const signatureImages = []; // Store [rowIndex, columnIndex, base64Data] for signature cells

// First section: Person in Charge of Safety (PICOS)
if (picosDataRows.length > 0) {
  picosDataRows.forEach((row, index) => {
    const rowIndex = picosTableBody.length;
    picosTableBody.push([
      index === 0 ? "" : "", // Only show label in first row
      row.name || "",
      "", // Leave signature cell empty, we'll add image via didDrawCell
      row.date || "",
      row.time || ""
    ]);
    
    // Store signature data if it exists and is valid
    // Check if signature exists and is not empty (base64 signatures are typically long)
    if (row.signature != null && row.signature !== '') {
      const sigStr = typeof row.signature === 'string' ? row.signature : String(row.signature);
      const trimmed = sigStr.trim();
      // Only store if signature is not empty (base64 data is typically long)
      if (trimmed.length > 0) {
        signatureImages.push({ row: rowIndex, col: 2, data: row.signature }); // Use original value
      }
    }
  });
} else {
  // If no data, add one empty row
  picosTableBody.push(["", "", "", "", ""]);
}

// Second section: Person taking over as PICOS
if (picosTakingOverRows.length > 0) {
  picosTakingOverRows.forEach((row, index) => {
    const rowIndex = picosTableBody.length;
    picosTableBody.push([
      index === 0 ? "Person taking over as PICOS" : "", // Only show label in first row
      row.name || "",
      "", // Leave signature cell empty, we'll add image via didDrawCell
      row.date || "",
      row.time || ""
    ]);
    
    // Store signature data if it exists and is valid
    // Check if signature exists and is not empty (base64 signatures are typically long)
    if (row.signature != null && row.signature !== '') {
      const sigStr = typeof row.signature === 'string' ? row.signature : String(row.signature);
      const trimmed = sigStr.trim();
      // Only store if signature is not empty (base64 data is typically long)
      if (trimmed.length > 0) {
        signatureImages.push({ row: rowIndex, col: 2, data: row.signature }); // Use original value
      }
    }
  });
} else {
  // If no data, add one empty row
  picosTableBody.push(["Person taking over as PICOS", "", "", "", ""]);
}

// 🔹 Table 1 : PICOS rows
autoTable(doc, {
  startY: yPosition + 8,
  margin: { left: 10, right: 10 }, // ✅ ensures full width without cutting
  tableWidth: 'auto',
  head: [[
    "Person in Charge of Safety (PICOS)",
    "Name",
    "Signature",
    "Date",
    "Time"
  ]],
  body: picosTableBody,
  theme: "grid",
  styles: {
    font: "helvetica",
    fontSize: 8,
    valign: "middle",
    cellPadding: 2,
  },
  headStyles: {
    fillColor: [242, 242, 242],
    textColor: [0, 0, 0],
    fontStyle: "bold",
    halign: "center",
  },
  columnStyles: {
    0: { cellWidth: 70 },
    1: { cellWidth: 40 },
    2: { cellWidth: 35 },
    3: { cellWidth: 22 },
    4: { cellWidth: 23 },
  },
  didDrawCell: (data) => {
    // Check if this is a signature cell that needs an image (column 2, body rows only)
    if (data.column.index === 2 && data.row.section === 'body') {
      // Calculate the body row index (table row index minus header row if present)
      // picosTableBody array indices match directly with body row indices
      const bodyRowIndex = data.table.head && data.table.head.length > 0 
        ? data.row.index - 1  // Subtract 1 for header row
        : data.row.index;
      
      // Find matching signature data
      const sigData = signatureImages.find(sig => 
        sig.row === bodyRowIndex && sig.col === 2
      );
      
      if (sigData && sigData.data) {
        try {
          // Prepare base64 data
          let base64Data = sigData.data;
          // Remove any escaped quotes
          base64Data = base64Data.replace(/^\\?"|\\?"$/g, '');
          base64Data = base64Data.replace(/^"|"$/g, ''); // Remove any remaining quotes
          
          // Skip if empty after cleaning
          if (!base64Data || base64Data.trim().length === 0) {
            return;
          }
          
          // Add data URI prefix if not present
          if (!base64Data.startsWith('data:')) {
            // Check if it's base64 PNG (starts with iVBORw0KGgo)
            if (base64Data.startsWith('iVBORw0KGgo')) {
              base64Data = 'data:image/png;base64,' + base64Data;
            } else {
              base64Data = 'data:image/png;base64,' + base64Data;
            }
          }
          
          // Get cell dimensions and position
          const cellWidth = data.cell.width;
          const cellHeight = data.cell.height;
          const x = data.cell.x;
          const y = data.cell.y;
          
          // Calculate image size (fit within cell with padding)
          const padding = 1;
          const maxImgWidth = Math.max(5, cellWidth - (padding * 2));
          const maxImgHeight = Math.max(5, cellHeight - (padding * 2));
          
          // Add image to PDF
          doc.addImage(base64Data, 'PNG', x + padding, y + padding, maxImgWidth, maxImgHeight);
        } catch (e) {
          console.error('Error adding signature image at row', bodyRowIndex, ':', e);
          // Fallback: show text if image fails
          doc.setFontSize(6);
          doc.text('[Signature]', data.cell.x + 2, data.cell.y + 5);
        }
      }
    }
  },
});

yPosition = doc.lastAutoTable.finalY;

// Parse nameUkpnCompany data into arrays
let nameUkpnCompanyRows = [];
let ukpnNameHeader = "Name (and company if not UKPN)"; // Default fallback
let ukpnSignatureHeader = "Signature"; // Default fallback
let ukpnTimeHeader = "Time"; // Default fallback

try {
  if (record.nameUkpnCompany) {
    const parsed = typeof record.nameUkpnCompany === 'string' 
      ? JSON.parse(record.nameUkpnCompany) 
      : record.nameUkpnCompany;
    
    // Extract headers from API response
    ukpnNameHeader = parsed.nameHint || parsed.header || "Name (and company if not UKPN)";
    ukpnSignatureHeader = parsed.signatureHint || "Signature";
    ukpnTimeHeader = parsed.timeHint || "Time";
    
    const names = parseArrayToItems(parsed.name || '');
    const times = parseArrayToItems(parsed.time || '');
    const signatures = parseSignatureArray(parsed.signature || '');
    
    // Create rows - use names as primary length, match other fields by index
    for (let i = 0; i < names.length; i++) {
      let signature = '';
      
      // Get signature at the same index as the name
      if (i < signatures.length && signatures[i] != null) {
        const sigValue = signatures[i];
        if (typeof sigValue === 'string') {
          const trimmed = sigValue.trim();
          if (trimmed.length > 0) {
            signature = sigValue; // Use original value (preserve base64)
          }
        } else {
          signature = String(sigValue);
        }
      }
      
      nameUkpnCompanyRows.push({
        name: names[i] || '',
        time: (times.length > i && times[i]) ? times[i] : '',
        signature: signature
      });
    }
  }
} catch (e) {
  console.error('Error parsing nameUkpnCompany:', e);
  // Add at least one empty row if no data
  if (nameUkpnCompanyRows.length === 0) {
    nameUkpnCompanyRows.push({ name: '', time: '', signature: '' });
  }
}

// Build table body for single column layout
const ukpnTableBody = [];
const ukpnSignatureImages = [];

// Determine how many rows we need (max 4 rows)
const maxRows = 4;
const allEntries = nameUkpnCompanyRows.slice(0, maxRows); // Limit to 4 entries

// Fill rows with data
for (let i = 0; i < maxRows; i++) {
  const entry = allEntries[i] || { name: '', time: '', signature: '' };
  
  ukpnTableBody.push([
    entry.name || '',
    '', // Signature will be added via didDrawCell
    entry.time || ''
  ]);
  
  // Store signature data for column 1
  if (entry.signature != null && entry.signature !== '') {
    const sigStr = typeof entry.signature === 'string' ? entry.signature : String(entry.signature);
    const trimmed = sigStr.trim();
    if (trimmed.length > 0) {
      ukpnSignatureImages.push({ row: i, col: 1, data: entry.signature });
    }
  }
}

// If no data, add empty rows
if (ukpnTableBody.length === 0) {
  for (let i = 0; i < maxRows; i++) {
    ukpnTableBody.push(['', '', '']);
  }
}

// 🔹 Table 2 : Non-UKPN names
autoTable(doc, {
  startY: yPosition,
  margin: { left: 10, right: 10 }, // ✅ same margin for consistency
  tableWidth: 'auto',
  head: [[
    ukpnNameHeader,
    ukpnSignatureHeader,
    ukpnTimeHeader
  ]],
  body: ukpnTableBody,
  theme: "grid",
  styles: {
    font: "helvetica",
    fontSize: 8,
    valign: "middle",
    cellPadding: 2,
  },
  headStyles: {
    fillColor: [242, 242, 242],
    textColor: [0, 0, 0],
    fontStyle: "bold",
    halign: "center",
  },
  columnStyles: {
    0: { cellWidth: 95 },
    1: { cellWidth: 60 },
    2: { cellWidth: 35 },
  },
  didDrawCell: (data) => {
    // Check if this is a signature cell that needs an image (column 1, body rows only)
    if (data.column.index === 1 && data.row.section === 'body') {
      // Calculate the body row index
      const bodyRowIndex = data.table.head && data.table.head.length > 0 
        ? data.row.index - 1  // Subtract 1 for header row
        : data.row.index;
      
      // Find matching signature data
      const sigData = ukpnSignatureImages.find(sig => 
        sig.row === bodyRowIndex && sig.col === data.column.index
      );
      
      if (sigData && sigData.data) {
        try {
          // Prepare base64 data
          let base64Data = sigData.data;
          // Remove any escaped quotes
          base64Data = base64Data.replace(/^\\?"|\\?"$/g, '');
          base64Data = base64Data.replace(/^"|"$/g, ''); // Remove any remaining quotes
          
          // Skip if empty after cleaning
          if (!base64Data || base64Data.trim().length === 0) {
            return;
          }
          
          // Add data URI prefix if not present
          if (!base64Data.startsWith('data:')) {
            // Check if it's base64 PNG (starts with iVBORw0KGgo)
            if (base64Data.startsWith('iVBORw0KGgo')) {
              base64Data = 'data:image/png;base64,' + base64Data;
            } else {
              base64Data = 'data:image/png;base64,' + base64Data;
            }
          }
          
          // Get cell dimensions and position
          const cellWidth = data.cell.width;
          const cellHeight = data.cell.height;
          const x = data.cell.x;
          const y = data.cell.y;
          
          // Calculate image size (fit within cell with padding)
          const padding = 1;
          const maxImgWidth = Math.max(5, cellWidth - (padding * 2));
          const maxImgHeight = Math.max(5, cellHeight - (padding * 2));
          
          // Add image to PDF
          doc.addImage(base64Data, 'PNG', x + padding, y + padding, maxImgWidth, maxImgHeight);
        } catch (e) {
          console.error('Error adding signature image at row', bodyRowIndex, ':', e);
          // Fallback: show text if image fails
          doc.setFontSize(6);
          doc.text('[Signature]', data.cell.x + 2, data.cell.y + 5);
        }
      }
    }
  },
});

yPosition = doc.lastAutoTable.finalY + 2;

// Parse recordofAssessmentReview data
let assessmentReviewHeader = "Record of assessment review - if site hazards change (weather/working party changes)";
let assessmentReviewTimeHeader = "Time";
let assessmentReviewReissueHeader = "POWA re-issued (PICOS to initial that working party briefed)";
let assessmentReviewReasonHeader = "Reason for review";
let assessmentReviewTableBody = [];

try {
  if (record.recordofAssessmentReview) {
    const parsed = typeof record.recordofAssessmentReview === 'string' 
      ? JSON.parse(record.recordofAssessmentReview) 
      : record.recordofAssessmentReview;
    
    // Extract headers from API response
    assessmentReviewHeader = parsed.header || assessmentReviewHeader;
    assessmentReviewTimeHeader = parsed.timeHint || assessmentReviewTimeHeader;
    assessmentReviewReissueHeader = parsed.sssbReissueHint || assessmentReviewReissueHeader;
    assessmentReviewReasonHeader = parsed.reasonforReviewHint || assessmentReviewReasonHeader;

    // Parse data arrays
    const times = parseArrayToItems(parsed.time || '');
    const reissues = parseArrayToItems(parsed.sssbReissue || '');
    const reasons = parseArrayToItems(parsed.reasonforReview || '');
    
    // Create table body - use times as primary length, match other fields by index
    const maxRows = Math.max(times.length, reissues.length, reasons.length, 3); // At least 3 rows
    for (let i = 0; i < maxRows; i++) {
      assessmentReviewTableBody.push([
        (times.length > i && times[i]) ? times[i] : '',
        (reissues.length > i && reissues[i]) ? reissues[i] : '',
        (reasons.length > i && reasons[i]) ? reasons[i] : ''
      ]);
    }
  }
} catch (e) {
  console.error('Error parsing recordofAssessmentReview:', e);
  // Add empty rows if no data
  if (assessmentReviewTableBody.length === 0) {
    assessmentReviewTableBody = [
      ["", "", ""],
      ["", "", ""],
      ["", "", ""],
    ];
  }
}

// If no data, add empty rows
if (assessmentReviewTableBody.length === 0) {
  assessmentReviewTableBody = [
    ["", "", ""],
    ["", "", ""],
    ["", "", ""],
  ];
}

// ---- Section 7 : Record of assessment review ----
// Calculate approximate height needed for section 7 (header bar + table)
// Header bar: 8 units, Table header: ~8 units, Table rows: ~8-10 units per row
const section7Height = 8 + 8 + (assessmentReviewTableBody.length * 10) + 20; // Add buffer

// ✅ Check if Section 7 fits on current page; if not, add new page
if (yPosition + section7Height + 25 > pageHeight) {
  doc.addPage();
  yPosition = 20; // reset top margin for new page
}

doc.setFillColor(139, 22, 52); // dark magenta header
doc.setTextColor(255, 255, 255);
doc.setFont("helvetica", "bold");

// 🔹 Header bar
doc.rect(10, yPosition, pageWidth - 20, 8, "F");
doc.text("7", 14, yPosition + 5.5);
doc.text(
  assessmentReviewHeader,
  22,
  yPosition + 5.5
);

// 🔹 Table Header
yPosition += 8;
autoTable(doc, {
  startY: yPosition,
  head: [
    [
      { content: assessmentReviewTimeHeader, styles: { halign: "center", fillColor: [240, 240, 240], fontStyle: "bold" } },
      { content: assessmentReviewReissueHeader, styles: { halign: "center", fillColor: [240, 240, 240], fontStyle: "bold" } },
      { content: assessmentReviewReasonHeader, styles: { halign: "center", fillColor: [240, 240, 240], fontStyle: "bold" } },
    ],
  ],
  body: assessmentReviewTableBody,
  styles: {
    font: "helvetica",
    fontSize: 9,
    cellPadding: 4,
    halign: "left",
    valign: "middle",
  },
  theme: "grid",
  tableWidth: pageWidth - 20,
  margin: { left: 10 },
  headStyles: {
    textColor: [0, 0, 0],
    fillColor: [245, 245, 245],
    lineWidth: 0.1,
  },
  bodyStyles: {
    lineWidth: 0.1,
  },
});

yPosition = doc.lastAutoTable.finalY + 2;

// Parse stateReasonForReviewRequest data
let section8Header = "Further Attention Required  -  State reason for review request";
let section8Content = "";
let reviewerDetailsHeader = "details: (tick as appropriate HSS Advisor / Coach / Supervisor / Manager)";
let reviewerOptions = [];
let reviewerSelected = "";

// Extract Section 8 data from API
if (record.stateReasonForReviewRequest) {
  section8Content = String(record.stateReasonForReviewRequest);
}

// Check if there's a header field in the record
if (record.furtherAttentionRequired) {
  try {
    const parsed = typeof record.furtherAttentionRequired === 'string' 
      ? JSON.parse(record.furtherAttentionRequired) 
      : record.furtherAttentionRequired;
    
    if (parsed.header) {
      section8Header = parsed.header;
    }
    if (parsed.content) {
      section8Content = String(parsed.content);
    } else if (parsed.reason) {
      section8Content = String(parsed.reason);
    }
  } catch (e) {
    console.error('Error parsing furtherAttentionRequired:', e);
  }
}

// Parse mcqQuestionsHss for reviewer details
try {
  if (record.mcqQuestionsHss) {
    let parsed;
    // Handle different formats - could be string, object, or already parsed
    if (typeof record.mcqQuestionsHss === 'string') {
      try {
        // Try parsing as JSON first
        parsed = JSON.parse(record.mcqQuestionsHss);
      } catch (parseError) {
        // If JSON.parse fails, try parseSingleQuotedJSON
        parsed = parseSingleQuotedJSON(record.mcqQuestionsHss);
      }
    } else {
      parsed = record.mcqQuestionsHss;
    }
    
    if (parsed) {
      if (parsed.header) {
        reviewerDetailsHeader = String(parsed.header);
      }
      if (Array.isArray(parsed.options)) {
        reviewerOptions = parsed.options.map(opt => String(opt));
      }
      if (parsed.selected) {
        reviewerSelected = String(parsed.selected).trim();
      }
    }
  }
} catch (e) {
  console.error('Error parsing mcqQuestionsHss:', e);
  // Fallback: try to extract from string directly
  if (typeof record.mcqQuestionsHss === 'string') {
    try {
      const selectedMatch = record.mcqQuestionsHss.match(/"selected"\s*:\s*"([^"]+)"/);
      if (selectedMatch && selectedMatch[1]) {
        reviewerSelected = selectedMatch[1].trim();
      }
      const optionsMatch = record.mcqQuestionsHss.match(/"options"\s*:\s*\[(.*?)\]/);
      if (optionsMatch && optionsMatch[1]) {
        const optionsStr = optionsMatch[1];
        reviewerOptions = optionsStr.match(/"([^"]+)"/g)?.map(opt => opt.replace(/"/g, '')) || [];
      }
    } catch (extractError) {
      console.error('Error extracting from string:', extractError);
    }
  }
}

// ---- Section 8 : Further Attention Required ----
const sectionHeight = 36; // total height of this section

// ✅ Check if Section 8 fits on current page; if not, add new page
if (yPosition + sectionHeight + 25 > pageHeight) {
  doc.addPage();
  yPosition = 20; // reset top margin for new page
}

doc.setFillColor(139, 22, 52); // dark magenta
doc.setTextColor(255, 255, 255);
doc.setFont("helvetica", "bold");

// 🔹 Header bar
doc.rect(10, yPosition, pageWidth - 20, 8, "F");
doc.text("8", 14, yPosition + 5.5);
doc.text(
  section8Header,
  22,
  yPosition + 5.5
);

// 🔹 Large empty box area with dynamic content
doc.setDrawColor(0);
doc.setLineWidth(0.1);
doc.setTextColor(0, 0, 0);
doc.rect(10, yPosition + 8, pageWidth - 20, 20); // white box area

// Add dynamic content if available
if (section8Content && section8Content.trim()) {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  // Split text into lines if too long
  const maxWidth = pageWidth - 30;
  const lines = doc.splitTextToSize(section8Content, maxWidth);
  doc.text(lines, 12, yPosition + 13);
}

// 🔹 Reviewer details row
doc.setFont("helvetica", "bold");
doc.rect(10, yPosition + 28, pageWidth - 20, 8); // bottom row box

const reviewerY = yPosition + 33.5; // Center vertically in the box

// Display header text
doc.setFont("helvetica", "bold");
doc.setFontSize(8);
doc.text(
  reviewerDetailsHeader,
  12,
  reviewerY
);

// Display options with CheckTick for selected option
if (reviewerOptions.length > 0) {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  
  // Calculate starting position after header text (using same font size)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  const headerWidth = doc.getTextWidth(reviewerDetailsHeader);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  let optionX = 12 + headerWidth + 5; // Start after header with spacing
  
  reviewerOptions.forEach((option, index) => {
    const optionText = String(option).trim();
    // Case-insensitive comparison and trim both values
    const isSelected = optionText.toLowerCase() === reviewerSelected.toLowerCase() || 
                       optionText === reviewerSelected ||
                       (reviewerSelected && reviewerSelected.trim() === optionText);
    
    // Calculate text width to position CheckTick
    const textWidth = doc.getTextWidth(optionText);
    const textCenterX = optionX + (textWidth / 2);
    
    // Add CheckTick image above selected option (centered above text)
    if (isSelected) {
      const checkTickSize = 3.5;
      const checkTickX = textCenterX - (checkTickSize / 2); // Center above text
      const checkTickY = reviewerY - checkTickSize - 1.5; // Position above the text
      doc.addImage(processedCheckTick, "PNG", checkTickX, checkTickY, checkTickSize, checkTickSize);
    }
    
    // Draw option text
    doc.text(optionText, optionX, reviewerY);
    
    // Move to next option position
    optionX += textWidth + 3; // Small spacing between options
    
    // Add separator if not last option
    if (index < reviewerOptions.length - 1) {
      doc.text(" / ", optionX, reviewerY);
      optionX += doc.getTextWidth(" / ");
    }
  });
}

yPosition += 36; // update for next section or footer

// ✅ Footer Section (auto-aligned, always fits cleanly)
const footerY = pageHeight - 25;
drawRoundedRect(0, footerY, pageWidth, 25, 0, [248, 249, 250], [200, 200, 200]);

doc.setFont("helvetica", "normal");
doc.setFontSize(8);
doc.setTextColor(150, 150, 150);
doc.text(
  `Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`,
  20,
  footerY + 8
);
doc.text("Page 1 of 1", pageWidth - 30, footerY + 15);

      
      // Build filename and either save or upload
      const safeDate = String(record.date || 'Assessment').replace(/[\\/:"*?<>|]+/g, '-');
      
      let fileName;
      if (shouldUploadToSharePoint && uploadConfig) {
        const locationForUpload = record.location || record.locationDescription || '';
        fileName = buildSharePointFileName([
          addedByName(record),
          locationForUpload,
          toDdMmYyyy(record.date),
        ]);
      } else {
        fileName = `POWAssessment_${safeDate}.pdf`;
      }

      if (shouldUploadToSharePoint && uploadConfig) {
        try {
          const locationForUpload = record.location || record.locationDescription || '';
          const key = `${record._id || ''}|${locationForUpload}|${record.date || ''}`;
          setUploadingKey(key);
          const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
          const dataUri = doc.output('datauristring');
          
          // Prepare SharePoint upload parameters
          const uploadParams = {
            fileName,
            fileBase64: dataUri,
            phaseType: uploadConfig.phaseType,
            phaseNumber: uploadConfig.phaseNumber,
            network: uploadConfig.network,
            siteName: uploadConfig.siteName,
            uploadContext: "hseq-forms-tablets", uploadSubFolder: "POW Assessment"
          };
          
          const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
            method: 'POST',
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
            body: JSON.stringify(uploadParams)
          });
          if (res.status !== 204 && !res.ok) {
            let msg = 'SharePoint upload failed';
            try {
              const out = await res.json();
              msg = out?.message || msg;
            } catch (_) {}
            throw new Error(msg);
          }
          toast.success("PDF uploaded to SharePoint successfully!");
          if (uploadConfig.onSuccess) {
            uploadConfig.onSuccess();
          }
        } catch (err) {
          toast.error(err?.message || "SharePoint upload failed");
          if (uploadConfig.onError) {
            uploadConfig.onError(err);
          }
        } finally {
          setUploadingKey(null);
        }
      } else {
        doc.save(fileName);
        toast.success("Professional POW Assessment PDF generated successfully!");
      }
    };

  return (
    <>
      <div style={{
        background: '#ffffff',
        borderRadius: '20px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        {/* Header Section */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1a202c',
            margin: 0
          }}>POW Assessment</h2>
          
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: "#8b1634",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <i className="fa fa-search" style={{
                position: 'absolute',
                left: '12px',
                color: '#8b1634',
                fontSize: '14px'
              }}></i>
              <input 
                type="text" 
                placeholder="Type to Search" 
                value={search} 
                onChange={e=>{ setSearch(e.target.value); setPage(1); }}
                style={{
                  padding: '10px 12px 10px 36px',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  outline: 'none',
                  width: '250px',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#8b1634'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div style={{
          overflowX: 'auto',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.5fr 1fr 1fr 1fr 1fr 1.5fr',
            background: '#f7fafc',
            padding: '12px 16px',
            borderBottom: '1px solid #e2e8f0',
            gap: '16px',
            alignItems: 'center'
          }}>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Location</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Description of Works</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Date</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Job Number</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Added By</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Updated Date</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Actions</div>
          </div>

          {/* Table Body */}
          <div>
              {paged.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#718096',
                fontSize: '16px'
              }}>No records found.</div>
            ) : paged.map((r, idx) => {
                // First check if firstName and lastName are saved in the record
                // If not, try to find user from appUsers array
                let userName = '-';
                if (r.addedByFirstName && r.addedByLastName) {
                  userName = `${r.addedByFirstName} ${r.addedByLastName}`;
                } else {
                const user = appUsers.find(u => u._id === r.userId);
                  userName = user ? `${user.firstName} ${user.lastName}` : (r.userId || '-');
                }
                return (
                  <div 
                    key={r._id || idx}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1.5fr 1fr 1fr 1fr 1fr 1.5fr',
                      padding: '16px',
                      borderBottom: '1px solid #f1f5f9',
                      gap: '16px',
                      alignItems: 'center',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.location || r.locationDescription || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.descriptionOfWorks || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.date || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.jobNumber || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{userName || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{formatDateForDisplay(r.updatedAt || r.createdAt || r.date)}</div>
                    <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                  {canEditDelete && (
                    <button 
                      onClick={()=>handleEdit(r)}
                      title="Edit"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '6px',
                        borderRadius: '6px',
                        transition: 'background-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <i className="fa fa-edit" style={{color:'#22c55e', fontSize: '16px'}}></i>
                          </button>
                  )}
                  <button 
                    onClick={()=>generatePDF(r)}
                    title="Generate PDF"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '6px',
                      borderRadius: '6px',
                      transition: 'background-color 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <i className="fa fa-file-pdf-o" style={{color:'#dc2626', fontSize: '16px'}}></i>
                  </button>
                    {(() => { 
                      const locationForUpload = r.location || r.locationDescription || '';
                      const k = `${r._id || ''}|${locationForUpload}|${r.date || ''}`; 
                      const busy = uploadingKey === k; 
                      return (
                        <button 
                          onClick={()=>{
                            if (!busy) {
                              setSharePointUploadRecord(r);
                              setSharePointPhaseType('phase');
                              setSharePointPhaseNumber('15');
                              setSharePointNetwork('');
                              setSharePointPhaseType("non-phase"); setPendingDirectUpload(true);
                            }
                          }}
                          disabled={busy}
                          title={busy ? "Uploading..." : "Upload to SharePoint"}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: busy ? 'not-allowed' : 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: busy ? 0.6 : 1
                          }}
                          onMouseEnter={(e) => !busy && (e.currentTarget.style.backgroundColor = '#f0fdf4')}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{color:'#22c55e', fontSize: '16px'}}></i>
                        </button>
                      ); 
                    })()}
                  {canEditDelete && (
                    <button 
                      onClick={()=>handleDelete(r._id)}
                      title="Delete"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '6px',
                        borderRadius: '6px',
                        transition: 'background-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <i className="fa fa-trash" style={{color:'#e53e3e', fontSize: '16px'}}></i>
                    </button>
                  )}
                      </div>
        </div>
                );
            })}
          </div>
        </div>

        {/* Pagination */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginTop: '18px',
          gap: '12px'
        }}>
          <button 
            className="horizon-table-btn" 
            disabled={page===1 || filtered.length === 0} 
            onClick={()=>setPage(page-1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===1 || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===1 || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===1 || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Prev</button>
          <span style={{
            fontWeight: '700',
            color: '#8b1634',
            fontSize: '16px'
          }}>{page} / {totalPages}</span>
          <button 
            className="horizon-table-btn" 
            disabled={page===totalPages || filtered.length === 0} 
            onClick={()=>setPage(page+1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===totalPages || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===totalPages || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===totalPages || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Next</button>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Add POW Assessment</h5>
                <button type="button" className="btn-close" onClick={()=>setShowAddModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-2 col-md-6">
                    <label>User ID *</label>
                    <input type="text" className="form-control" value={newRecord.userId} onChange={e=>handleTextChange("userId", e.target.value)} />
                  </div>
                  <div className="mb-2 col-md-6">
                    <label>Location Description *</label>
                    <input type="text" className="form-control" value={newRecord.locationDescription} onChange={e=>handleTextChange("locationDescription", e.target.value)} />
                  </div>
                  <div className="mb-2 col-md-6">
                    <label>Job Number *</label>
                    <input type="text" className="form-control" value={newRecord.jobNumber} onChange={e=>handleTextChange("jobNumber", e.target.value)} />
                  </div>
                  <div className="mb-2 col-md-6">
                    <label>Person in Charge *</label>
                    <input type="text" className="form-control" value={newRecord.personInCharge} onChange={e=>handleTextChange("personInCharge", e.target.value)} />
                  </div>
                  <div className="mb-2 col-md-6">
                    <label>Date *</label>
                    <input type="date" className="form-control" value={newRecord.date} onChange={e=>handleTextChange("date", e.target.value)} />
                  </div>
                  {/* MCQ Sections */}
                  <div className="mb-2 col-md-6">
                    <label className="fw-bold" style={{color:'#8b1634'}}>MCQ Questions</label>
                    {Array.isArray(newRecord.mcqQuestionsList) && newRecord.mcqQuestionsList.map((section, sidx) => (
                      <div key={sidx} style={{border:'1px solid #eee', borderRadius:8, padding:12, marginTop:8}}>
                        {section.header === 'Steps List' ? (
                          <div>
                            {/* Show main title from API data */}
                            <div className="fw-bold" style={{marginBottom:8, color:'#8b1634', fontSize:'18px'}}>
                              {section.mainTitle || 'Steps List'}
                            </div>
                          </div>
                        ) : section.header === 'The Safe 6' ? (
                          <div>
                            {/* Add Recorded of Multi Site Visit before The Safe 6 */}
                            <div className="mb-3">
                              <label className="fw-bold" style={{color:'#8b1634', fontSize:'16px', marginBottom:8}}>Recorded of Multi Site Visit</label>
                              <textarea className="form-control" rows="3" value={newRecord.recodedOfMultiSiteVisit} onChange={e=>handleTextChange("recodedOfMultiSiteVisit", e.target.value)} />
                            </div>
                            <div className="fw-bold" style={{marginBottom:8}}>{section.header}</div>
                          </div>
                        ) : (
                        <div className="fw-bold" style={{marginBottom:8}}>{section.header}</div>
                        )}
                        {Array.isArray(section.checkboxOptions) && section.checkboxOptions.length > 0 && (
                          <div className="mb-2" style={{display:'flex', flexWrap:'wrap', gap:16}}>
                            {section.checkboxOptions.map(opt => (
                              <label key={opt} style={{display:'flex', alignItems:'center', gap:6}}>
                                <input type="checkbox" checked={!!section.checkboxValues?.[opt]} onChange={e=>handleMcqCheckboxChange(sidx, opt, e.target.checked)} /> {opt}
                              </label>
                            ))}
                  </div>
                        )}
                        {Array.isArray(section.items) && section.items.map((item, iidx) => (
                          <div key={iidx} className="mb-2" style={{paddingLeft:8}}>
                                {item.isMainTitle ? (
                                  <div className="fw-bold" style={{color:'#8b1634', fontSize:'18px', marginBottom:12, marginTop:8, }}>
                                    {item.title}
                                  </div>
                                ) : item.isStepHeader ? (
                                  <div className="fw-bold" style={{color:'#8b1634', fontSize:'16px', marginBottom:8, marginTop:12, paddingLeft:16}}>
                                    {item.title}
                                  </div>
                                ) : section.header === 'The Safe 6' ? (
                                  item.textField?.isTextBox ? (
                                    /* Text box items (reasonNotWorn, detailPpeRequired) */
                                    <div className="col-md-6" style={{
                                      marginBottom:12,
                                      padding:'12px 16px',
                                      backgroundColor:'white',
                                      borderRadius:'12px',
                                      border:'1px solid #e0e0e0',
                                      boxShadow:'0 2px 4px rgba(0,0,0,0.1)'
                                    }}>
                                      <div className="fw-bold" style={{color:'#8b1634', fontSize:'1.1rem', marginBottom:8}}>
                                        {item.title}
                                      </div>
                                      <input 
                                        type="text" 
                                        className="form-control" 
                                        style={{
                                          fontSize: '16px',
                                          fontFamily: 'system-ui, -apple-system, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
                                          lineHeight: '1.5',
                                          padding: '8px 12px'
                                        }}
                                        placeholder={item.textField?.hintText || ''} 
                                        value={item.textFieldValue !== undefined ? item.textFieldValue : (item.textField?.value || '')} 
                                        onChange={e=>handleMcqTextFieldChange(sidx, iidx, e.target.value)} 
                                      />
                                  </div>
                                ) : (
                                    /* Checkbox items with images */
                                    <div style={{
                                      display:'flex', 
                                      alignItems:'center', 
                                      justifyContent:'space-between', 
                                      marginBottom:12,
                                      padding:'12px 16px',
                                      backgroundColor:'white',
                                      borderRadius:'12px',
                                      border:'1px solid #e0e0e0',
                                      boxShadow:'0 2px 4px rgba(0,0,0,0.1)'
                                    }}>
                                      <div style={{display:'flex', alignItems:'center', gap:16}}>
                                      {/* Show image if available */}
                                      {item.img && (
                                          <div style={{
                                            width:'50px',
                                            height:'50px',
                                            borderRadius:'50%',
                                            backgroundColor:'#8b1634',
                                            display:'flex',
                                            alignItems:'center',
                                            justifyContent:'center',
                                            flexShrink:0
                                          }}>
                                            <img 
                                              src={getImageSrc(item.img)} 
                                          alt={item.title} 
                                          style={{
                                                width:'40px',
                                                height:'40px',
                                                filter: item.title === 'Arc Proof' ? 'none' : 'brightness(0) invert(1)'
                                              }}
                                              onError={(e) => {
                                                e.target.style.display = 'none';
                                              }}
                                            />
                                          </div>
                                        )}
                                        <div className="fw-bold" style={{color:'#8b1634', fontSize:'1.1rem'}}>
                                          {item.title}
                                        </div>
                                      </div>
                                      
                                      {/* Show checkboxes for items with hasCheckbox: true */}
                                      {item.hasCheckbox && (
                                        <input 
                                          type="checkbox" 
                                          checked={!!item.checkboxValue} 
                                          onChange={e=>handleMcqItemCheckboxChange(sidx, iidx, e.target.checked)} 
                                          style={{
                                            width:'18px',
                                            height:'18px',
                                            accentColor:'#8b1634'
                                          }}
                                        />
                                      )}
                                    </div>
                                  )
                                ) : (
                                  <div>{item.title}</div>
                                )}
                                
                                {/* Show radio buttons for items without hasCheckbox */}
                                {!item.hasCheckbox && !item.textField?.isTextBox && Array.isArray(item.options) && (
                                  <div style={{display:'flex', flexWrap:'wrap', gap:12, alignItems:'center', paddingLeft: item.hasCheckbox ? 0 : 20}}>
                                    {item.options.map(opt => (
                                      <label key={opt} style={{display:'flex', alignItems:'center', gap:6}}>
                                        <input type="radio" name={`add-${sidx}-${iidx}`} checked={item.value === opt} onChange={()=>handleMcqChange(sidx, iidx, opt)} /> {opt}
                                      </label>
                                    ))}
                                  </div>
                                )}
                              </div>
                        ))}
                      </div>
                    ))}
                  </div>
                  
                  {/* Section 6: Signatories - Exact match to image */}
                  <div className="mb-4" style={{border:'1px solid #ddd', borderRadius:'8px', padding:'20px', backgroundColor:'#f9f9f9'}}>
                    <div className="fw-bold" style={{color:'#8b1634', fontSize:'18px', marginBottom:'20px', backgroundColor:'#8b1634', color:'white', padding:'10px 15px', borderRadius:'4px'}}>
                      6 Signatories
                    </div>
                    
                    {/* PICOS Signatures Table */}
                    <div className="mb-4">
                      <table className="table table-bordered" style={{marginBottom:'0'}}>
                        <thead style={{backgroundColor:'#f0f0f0'}}>
                          <tr>
                            <th style={{width:'25%', fontWeight:'bold', padding:'8px'}}>Person in Charge of Safety (PICOS)</th>
                            <th style={{width:'15%', fontWeight:'bold', padding:'8px'}}>Name</th>
                            <th style={{width:'15%', fontWeight:'bold', padding:'8px'}}>Signature</th>
                            <th style={{width:'25%', fontWeight:'bold', padding:'8px'}}>In signing I confirm points 1-5 above</th>
                            <th style={{width:'10%', fontWeight:'bold', padding:'8px'}}>Date</th>
                            <th style={{width:'10%', fontWeight:'bold', padding:'8px'}}>Time</th>
                            </tr>
                          </thead>
                          <tbody>
                          <tr>
                            <td style={{padding:'8px', backgroundColor:'white'}}></td>
                            <td style={{padding:'8px', backgroundColor:'white'}}>
                              <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                            <td style={{padding:'8px', backgroundColor:'white'}}>
                              <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                            <td style={{padding:'8px', backgroundColor:'white'}}>
                              <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                            <td style={{padding:'8px', backgroundColor:'white'}}>
                              <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                            <td style={{padding:'8px', backgroundColor:'white'}}>
                              <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                            </tr>
                          <tr>
                            <td style={{padding:'8px', backgroundColor:'white'}}>Person taking over as PICOS</td>
                            <td style={{padding:'8px', backgroundColor:'white'}}>
                              <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                            <td style={{padding:'8px', backgroundColor:'white'}}>
                              <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                            <td style={{padding:'8px', backgroundColor:'white'}}>
                              <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                            <td style={{padding:'8px', backgroundColor:'white'}}>
                              <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                            <td style={{padding:'8px', backgroundColor:'white'}}>
                              <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                    </div>
                    
                    {/* Thick horizontal line separator */}
                    <div style={{borderTop:'3px solid #000', margin:'15px 0'}}></div>
                    
                    {/* Additional Signatories - Two Column Layout */}
                    <div className="row">
                      <div className="col-6" style={{borderRight:'3px solid #000', paddingRight:'15px'}}>
                        <table className="table table-bordered" style={{marginBottom:'0'}}>
                          <thead style={{backgroundColor:'#f0f0f0'}}>
                            <tr>
                              <th style={{fontWeight:'bold', padding:'8px'}}>Name (and company if not UKPN)</th>
                              <th style={{fontWeight:'bold', padding:'8px'}}>Signature</th>
                              <th style={{fontWeight:'bold', padding:'8px'}}>Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <div style={{color:'#888', fontSize:'12px', padding:'4px'}}>In signing I confirm points 1-4 above</div>
                              </td>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                            </tr>
                            <tr>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <div style={{color:'#888', fontSize:'12px', padding:'4px'}}>In signing I confirm points 1-4 above</div>
                              </td>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                            </tr>
                            <tr>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <div style={{color:'#888', fontSize:'12px', padding:'4px'}}>In signing I confirm points 1-4 above</div>
                              </td>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                            </tr>
                            <tr>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <div style={{color:'#888', fontSize:'12px', padding:'4px'}}>In signing I confirm points 1-4 above</div>
                              </td>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div className="col-6" style={{paddingLeft:'15px'}}>
                        <table className="table table-bordered" style={{marginBottom:'0'}}>
                          <thead style={{backgroundColor:'#f0f0f0'}}>
                            <tr>
                              <th style={{fontWeight:'bold', padding:'8px'}}>Name (and company if not UKPN)</th>
                              <th style={{fontWeight:'bold', padding:'8px'}}>Signature</th>
                              <th style={{fontWeight:'bold', padding:'8px'}}>Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <div style={{color:'#888', fontSize:'12px', padding:'4px'}}>In signing I confirm points 1-4 above</div>
                              </td>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                            </tr>
                            <tr>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <div style={{color:'#888', fontSize:'12px', padding:'4px'}}>In signing I confirm points 1-4 above</div>
                              </td>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                            </tr>
                            <tr>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <div style={{color:'#888', fontSize:'12px', padding:'4px'}}>In signing I confirm points 1-4 above</div>
                              </td>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                            </tr>
                            <tr>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <div style={{color:'#888', fontSize:'12px', padding:'4px'}}>In signing I confirm points 1-4 above</div>
                              </td>
                              <td style={{padding:'8px', backgroundColor:'white'}}>
                                <input type="text" className="form-control" style={{border:'none', padding:'4px'}} />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowAddModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{background:'#8b1634', color:'#fff', fontWeight:600}} onClick={handleAdd}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editRecord && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto', borderRadius:'12px', boxShadow:'0 10px 40px rgba(0,0,0,0.2)'}}>
              <div className="modal-header" style={{borderBottom:'2px solid #f0f0f0', padding:'16px 20px', background:'linear-gradient(135deg, #8b1634 0%, #a01a3f 100%)'}}>
                <h5 className="modal-title" style={{color:'#fff', fontWeight:'600', fontSize:'18px'}}>Edit POW Assessment</h5>
                <button type="button" className="btn-close btn-close-white" onClick={()=>setShowEditModal(false)} style={{opacity:0.9}}></button>
              </div>
              <div className="modal-body" style={{padding:'20px'}}>
                <div className="row">
                  <div className="mb-3 col-4">
                    <label style={{fontSize:'13px', fontWeight:'500', color:'#333', marginBottom:'6px', display:'block'}}>Location</label>
                    <input type="text" className="form-control" value={editRecord.location || ''} onChange={e=>handleTextChange("location", e.target.value, true)} style={{borderRadius:'6px', border:'1px solid #ddd', padding:'8px 12px', fontSize:'14px'}} />
                  </div>
                  <div className="mb-3 col-4">
                    <label style={{fontSize:'13px', fontWeight:'500', color:'#333', marginBottom:'6px', display:'block'}}>Job Number</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editRecord.jobNumber || ''} 
                      onChange={e=>{
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        handleTextChange("jobNumber", value, true);
                      }} 
                      style={{borderRadius:'6px', border:'1px solid #ddd', padding:'8px 12px', fontSize:'14px'}} 
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                  </div>
                  <div className="mb-3 col-4">
                    <label style={{fontSize:'13px', fontWeight:'500', color:'#333', marginBottom:'6px', display:'block'}}>Date</label>
                    <input type="date" className="form-control" value={editRecord.date ? editRecord.date.split('/').reverse().join('-') : ''} onChange={e=>handleTextChange("date", e.target.value.split('-').reverse().join('/'), true)} style={{borderRadius:'6px', border:'1px solid #ddd', padding:'8px 12px', fontSize:'14px'}} />
                  </div>
                  <div className="mb-3 col-md-6">
                    <label style={{fontSize:'13px', fontWeight:'500', color:'#333', marginBottom:'6px', display:'block'}}>Description of Works</label>
                    <textarea className="form-control" rows="3" value={editRecord.descriptionOfWorks || ''} onChange={e=>handleTextChange("descriptionOfWorks", e.target.value, true)} style={{borderRadius:'6px', border:'1px solid #ddd', padding:'8px 12px', fontSize:'14px', resize:'vertical'}} />
                  </div>
                  {/* MCQ Sections - Edit */}
                  <div className="mb-3 col-md-12">
                    <label className="fw-bold" style={{color:'#8b1634', fontSize:'16px', marginBottom:'8px', display:'block'}}>MCQ Questions</label>
                    {Array.isArray(editRecord.mcqQuestionsList) && editRecord.mcqQuestionsList.map((section, sidx) => (
                      <div key={sidx} style={{border:'1px solid #e2e8f0', borderRadius:'8px', padding:'12px', marginTop:'8px', background:'#ffffff', boxShadow:'0 1px 3px rgba(0,0,0,0.08)'}}>
                        {/* Show tableHeader for table sections, descriptiveHeader for sections with it, hide API key headers for all sections */}
                        {section.isTable && section.tableHeader ? (
                          <div className="fw-bold" style={{marginBottom:'8px', color:'#8b1634', fontSize:'14px', paddingBottom:'6px', borderBottom:'1px solid #e2e8f0'}}>{section.tableHeader}</div>
                        ) : section.descriptiveHeader ? (
                          <div className="fw-bold" style={{marginBottom:'8px', color:'#8b1634', fontSize:'14px', paddingBottom:'6px', borderBottom:'1px solid #e2e8f0'}}>{section.descriptiveHeader}</div>
                        ) : section.header && !['personInChargeofSafety', 'PersonTakingOverAsPICOS', 'nameUkpnCompany', 'mcqDetailedPreWorkList', 'mcqControlsPreWorkList', 'checklist', 'detailsofEmergencyProcedures', 'safeCheckList', 'mcqQuestionsHss', 'recordofAssessmentReview', 'stateReasonForReviewRequest', 'mcqQuestions'].includes(section.header) ? (
                          <div className="fw-bold" style={{marginBottom:'8px', color: section.header ? '#8b1634' : '#000', fontSize:'14px', paddingBottom:'6px', borderBottom:'1px solid #e2e8f0'}}>{section.header}</div>
                        ) : null}
                        
                        {/* Render as table if isTable is true */}
                        {section.isTable && section.tableData && (
                          <div className="mb-3">
                            <table className="table table-bordered">
                              <thead style={{backgroundColor:'#f0f0f0'}}>
                                <tr>
                                  {section.nameHint && <th style={{fontWeight:'bold', padding:'8px'}}>{section.nameHint}</th>}
                                  {section.tableData[0]?.signature !== undefined && <th style={{fontWeight:'bold', padding:'8px'}}>Signature</th>}
                                  {section.dateHint && <th style={{fontWeight:'bold', padding:'8px'}}>{section.dateHint}</th>}
                                  {section.timeHint && <th style={{fontWeight:'bold', padding:'8px'}}>{section.timeHint}</th>}
                                  {section.sssbReissueHint && <th style={{fontWeight:'bold', padding:'8px'}}>{section.sssbReissueHint}</th>}
                                  {section.reasonforReviewHint && <th style={{fontWeight:'bold', padding:'8px'}}>{section.reasonforReviewHint}</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {section.tableData.map((row, rowIdx) => (
                                  <tr key={rowIdx}>
                                    {section.nameHint && (
                                      <td style={{padding:'10px 12px'}}>
                                    <input 
                                      type="text" 
                                      className="form-control" 
                                          style={{
                                            border:'1px solid #dee2e6', 
                                            padding:'6px 10px',
                                            borderRadius:'6px',
                                            fontSize:'13px',
                                            transition:'all 0.15s ease',
                                            background:'#fff'
                                          }}
                                          value={row.name || ''}
                                          onChange={e => {
                                            const updated = [...section.tableData];
                                            updated[rowIdx] = { ...updated[rowIdx], name: e.target.value };
                                            const updatedSections = [...editRecord.mcqQuestionsList];
                                            updatedSections[sidx] = { ...updatedSections[sidx], tableData: updated };
                                            setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
                                          }}
                                          onFocus={(e)=>{
                                            e.target.style.borderColor='#8b1634';
                                            e.target.style.boxShadow='0 0 0 3px rgba(139,22,52,0.1)';
                                          }}
                                          onBlur={(e)=>{
                                            e.target.style.borderColor='#dee2e6';
                                            e.target.style.boxShadow='none';
                                          }}
                                        />
                                      </td>
                                    )}
                                    {row.signature !== undefined && (
                                      <td style={{padding:'8px'}}>
                                        {row.signature && String(row.signature).trim() && row.signature !== '__SHOW_CANVAS__' ? (
                    <div style={{
                      width: '100%',
                                            padding: '8px',
                                            textAlign: 'center'
                                          }}>
                                            <img 
                                              src={String(row.signature).startsWith('data:image') 
                                                ? row.signature 
                                                : `data:image/png;base64,${row.signature}`}
                                              alt="Signature" 
                                              style={{
                                                maxWidth: '200px',
                                                maxHeight: '60px',
                                                objectFit: 'contain',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                padding: '4px',
                                                backgroundColor: 'white'
                                              }}
                                              onError={(e) => {
                                                e.target.style.display = 'none';
                                              }}
                                            />
                      </div>
                                        ) : (
                      <div style={{
                        width: '100%',
                                            padding: '8px',
                                            textAlign: 'center',
                                            color: '#888',
                                            fontSize: '12px'
                                          }}>
                                            No signature
                      </div>
                                        )}
                                      </td>
                                    )}
                                    {section.dateHint && (
                                      <td style={{padding:'10px 12px'}}>
                                        <div className="site-register-datepicker-wrap">
                                          <DatePicker
                                            {...siteRegisterDatePickerProps}
                                            selected={parseSiteRegisterDate(row.date)}
                                            onChange={(d) => {
                                              const v = formatSiteRegisterDate(d);
                                              const updated = [...section.tableData];
                                              updated[rowIdx] = { ...updated[rowIdx], date: v };
                                              const updatedSections = [...editRecord.mcqQuestionsList];
                                              updatedSections[sidx] = { ...updatedSections[sidx], tableData: updated };
                                              setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
                                            }}
                                          />
                                        </div>
                                      </td>
                                    )}
                                    {section.timeHint && (
                                      <td style={{padding:'10px 12px'}}>
                                    <AnalogClockTimePicker
                                          value={row.time || ''}
                                          onChange={(time) => {
                                            const updated = [...section.tableData];
                                            updated[rowIdx] = { ...updated[rowIdx], time: time || '' };
                                            const updatedSections = [...editRecord.mcqQuestionsList];
                                            updatedSections[sidx] = { ...updatedSections[sidx], tableData: updated };
                                            setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
                                          }}
                                          isClearable
                                          placeholder="Select time"
                                          aria-label="Time"
                                        />
                                      </td>
                                    )}
                                    {section.sssbReissueHint && (
                                      <td style={{padding:'10px 12px'}}>
                                    <input
                                      type="text"
                                      className="form-control"
                                          style={{
                                            border:'1px solid #dee2e6',
                                            padding:'6px 10px',
                                            borderRadius:'6px',
                                            fontSize:'13px',
                                            transition:'all 0.15s ease',
                                            background:'#fff'
                                          }}
                                          value={row.sssbReissue || ''}
                                      onChange={e => {
                                            const updated = [...section.tableData];
                                            updated[rowIdx] = { ...updated[rowIdx], sssbReissue: e.target.value };
                                            const updatedSections = [...editRecord.mcqQuestionsList];
                                            updatedSections[sidx] = { ...updatedSections[sidx], tableData: updated };
                                            setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
                                          }}
                                          onFocus={(e)=>{
                                            e.target.style.borderColor='#8b1634';
                                            e.target.style.boxShadow='0 0 0 3px rgba(139,22,52,0.1)';
                                          }}
                                          onBlur={(e)=>{
                                            e.target.style.borderColor='#dee2e6';
                                            e.target.style.boxShadow='none';
                                          }}
                                        />
                                      </td>
                                    )}
                                    {section.reasonforReviewHint && (
                                      <td style={{padding:'10px 12px'}}>
                                    <input
                                      type="text"
                                      className="form-control"
                                          style={{
                                            border:'1px solid #dee2e6',
                                            padding:'6px 10px',
                                            borderRadius:'6px',
                                            fontSize:'13px',
                                            transition:'all 0.15s ease',
                                            background:'#fff'
                                          }}
                                          value={row.reasonforReview || ''}
                                      onChange={e => {
                                            const updated = [...section.tableData];
                                            updated[rowIdx] = { ...updated[rowIdx], reasonforReview: e.target.value };
                                            const updatedSections = [...editRecord.mcqQuestionsList];
                                            updatedSections[sidx] = { ...updatedSections[sidx], tableData: updated };
                                            setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
                                          }}
                                          onFocus={(e)=>{
                                            e.target.style.borderColor='#8b1634';
                                            e.target.style.boxShadow='0 0 0 3px rgba(139,22,52,0.1)';
                                          }}
                                          onBlur={(e)=>{
                                            e.target.style.borderColor='#dee2e6';
                                            e.target.style.boxShadow='none';
                                          }}
                                        />
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                  </div>
                            )}

                        {Array.isArray(section.checkboxOptions) && section.checkboxOptions.length > 0 && (
                          <div className="mb-2" style={{display:'flex', flexWrap:'wrap', gap:'8px', padding:'4px 0'}}>
                            {section.checkboxOptions.map(opt => (
                              <label key={opt} 
                                  style={{
                                    display:'flex', 
                                    alignItems:'center', 
                                  gap:'6px', 
                                  cursor:'pointer', 
                                  padding:'6px 10px', 
                                  borderRadius:'6px', 
                                  background:'#f8f9fa', 
                                  border:'1px solid #dee2e6', 
                                  transition:'all 0.15s ease', 
                                  fontSize:'13px',
                                  margin:0
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background='#e9ecef';
                                  e.currentTarget.style.borderColor='#8b1634';
                                  e.currentTarget.style.transform='translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background='#f8f9fa';
                                  e.currentTarget.style.borderColor='#dee2e6';
                                  e.currentTarget.style.transform='translateY(0)';
                                }}
                              >
                                <input type="checkbox" checked={!!section.checkboxValues?.[opt]} onChange={e=>handleMcqCheckboxChange(sidx, opt, e.target.checked, true)} style={{width:'16px', height:'16px', cursor:'pointer', accentColor:'#8b1634', margin:0}} /> 
                                <span style={{color:'#495057', userSelect:'none', fontWeight:'500'}}>{opt}</span>
                              </label>
                            ))}
                  </div>
                        )}
                        {Array.isArray(section.items) && section.items.length > 0 && section.items.map((item, iidx) => {
                          // Get image source if item has img property
                          // Show image only for main titles (hazard names) - matching PDF display
                          const shouldShowImage = item.img && item.isMainTitle;
                          const itemImageSrc = shouldShowImage ? getImageSrcFromPath(item.img) : null;
                          
                          return (
                          <div key={iidx} className="mb-1" style={{padding:'6px 0', borderBottom: item.isMainTitle ? '1px solid #e2e8f0' : 'none', marginBottom: item.isMainTitle ? '8px' : '4px', paddingBottom: item.isMainTitle ? '8px' : '4px'}}>
                            <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom: item.options && item.options.length > 0 ? '4px' : '0'}}>
                              {/* Display image if available - only for main items */}
                              {itemImageSrc && (
                                <div style={{flexShrink:0, width:'56px', height:'56px', display:'flex', alignItems:'center', justifyContent:'center', background:'#f8f9fa', borderRadius:'8px', padding:'6px', border:'1px solid #e2e8f0'}}>
                                  <img 
                                    src={itemImageSrc} 
                                    alt={item.title || 'Icon'} 
                                    style={{
                                      maxWidth:'100%',
                                      maxHeight:'100%',
                                      objectFit:'contain'
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </div>
                              )}
                                <div className="fw-medium" style={{fontSize: item.isMainTitle ? '14px' : '13px', color: item.isMainTitle ? '#1a202c' : '#4a5568', flex:'1', fontWeight: item.isMainTitle ? '600' : '500', lineHeight:'1.4'}}>{item.title}</div>
                              {item.hasCheckbox && !item.textField?.isTextBox && (
                                <input type="checkbox" checked={!!item.checkboxValue || !!item.booleanValue} onChange={e=>handleMcqItemCheckboxChange(sidx, iidx, e.target.checked, true)} style={{width:'18px', height:'18px', cursor:'pointer', accentColor:'#8b1634', flexShrink:0, margin:0}} />
                              )}
                                  </div>
                            <div style={{display:'flex', flexWrap:'wrap', gap:'6px', alignItems:'center', marginTop: item.options && item.options.length > 0 ? '4px' : '0', marginLeft: itemImageSrc ? '64px' : '0'}}>
                              {Array.isArray(item.options) && item.options.map(opt => (
                                <label key={opt} 
                                    style={{
                                  display:'flex', 
                                  alignItems:'center', 
                                    gap:'6px', 
                                    cursor:'pointer', 
                                    padding:'5px 10px', 
                                    borderRadius:'6px', 
                                    background:'#f8f9fa', 
                                  border:'1px solid #dee2e6',
                                    transition:'all 0.15s ease', 
                                    fontSize:'12px',
                                    margin:0
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background='#e9ecef';
                                    e.currentTarget.style.borderColor='#8b1634';
                                    e.currentTarget.style.transform='translateY(-1px)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background='#f8f9fa';
                                    e.currentTarget.style.borderColor='#dee2e6';
                                    e.currentTarget.style.transform='translateY(0)';
                                  }}
                                >
                                  <input type="radio" name={`edit-${sidx}-${iidx}`} checked={item.value === opt} onChange={()=>handleMcqChange(sidx, iidx, opt, true)} style={{width:'16px', height:'16px', cursor:'pointer', accentColor:'#8b1634', margin:0}} /> 
                                  <span style={{color:'#495057', userSelect:'none', fontWeight:'500'}}>{opt}</span>
                                </label>
                              ))}
                              {item.textField?.isTextBox && (
                                <div style={{display:'flex', alignItems:'center', gap:'6px', flex:'1', minWidth:'200px', marginLeft: itemImageSrc ? '64px' : '0'}}>
                                      <input 
                                        type="text" 
                                        className="form-control" 
                                        style={{
                                          flex:'1',
                                          fontSize: '13px',
                                          fontFamily: 'system-ui, -apple-system, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
                                          lineHeight: '1.4',
                                          padding: '6px 10px',
                                          borderRadius:'6px',
                                          border:'1px solid #dee2e6',
                                          transition:'all 0.15s ease',
                                          background:'#fff'
                                        }}
                                        placeholder={item.textField?.hintText || ''} 
                                    value={(() => {
                                      // If user has interacted with the field (textFieldValue is defined), use it (even if empty)
                                      // This allows user to clear the field
                                      if (item.textFieldValue !== undefined) {
                                        // Filter out "string" if it somehow got in there
                                        return item.textFieldValue === 'string' ? '' : item.textFieldValue;
                                      }
                                      // Otherwise, use the actual value from API (textField.value, not valueType)
                                      const apiValue = item.textField?.value;
                                      if (apiValue !== undefined && apiValue !== '' && apiValue !== 'string') {
                                        return apiValue;
                                      }
                                      // Fallback to empty string (never use valueType)
                                      return '';
                                    })()} 
                                        onChange={e=>handleMcqTextFieldChange(sidx, iidx, e.target.value, true)} 
                                        onFocus={(e)=>{
                                          e.target.style.borderColor='#8b1634';
                                          e.target.style.boxShadow='0 0 0 3px rgba(139,22,52,0.1)';
                                        }}
                                        onBlur={(e)=>{
                                          e.target.style.borderColor='#dee2e6';
                                          e.target.style.boxShadow='none';
                                        }}
                                      />
                  </div>
                                  )}
                  </div>
                          </div>
                        );
                        })}
                      </div>
                    ))}
                  </div>
                  
                 
                    </div>
                      </div>
              <div className="modal-footer" style={{borderTop:'2px solid #f0f0f0', padding:'16px 20px', background:'#fafafa'}}>
                <button type="button" className="btn btn-secondary" onClick={()=>setShowEditModal(false)} style={{borderRadius:'6px', padding:'8px 20px', fontSize:'14px', fontWeight:'500'}}>Cancel</button>
                <button type="button" className="btn" style={{background:'#8b1634', color:'#fff', fontWeight:600, borderRadius:'6px', padding:'8px 24px', fontSize:'14px', border:'none', boxShadow:'0 2px 4px rgba(139,22,52,0.3)', transition:'all 0.2s'}} onClick={handleEditSave} onMouseEnter={(e)=>e.target.style.background='#a01a3f'} onMouseLeave={(e)=>e.target.style.background='#8b1634'}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SharePoint Upload Modal */}
      {showSharePointUploadModal && sharePointUploadRecord && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'600px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Upload to SharePoint</h5>
                <button type="button" className="btn-close" onClick={()=>{
                  setShowSharePointUploadModal(false);
                  setSharePointUploadRecord(null);
                  setSharePointPhaseType('phase');
                  setSharePointPhaseNumber('15');
                  setSharePointNetwork('');
                }}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-12">
                    <label className="form-label fw-semibold">Type <span style={{color:'red'}}>*</span></label>
                    <select 
                      className="form-select" 
                      value={sharePointPhaseType}
                      onChange={(e) => {
                        setSharePointPhaseType(e.target.value);
                        if (e.target.value === 'non-phase') {
                          setSharePointNetwork('');
                        }
                      }}
                    >
                      <option value="phase">Phase</option>
                      <option value="non-phase">Non Phase</option>
                    </select>
                  </div>
                  
                  {sharePointPhaseType === 'phase' && (
                    <>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Phase Number <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointPhaseNumber}
                          onChange={(e) => {
                            setSharePointPhaseNumber(e.target.value);
                          }}
                        >
                          <option value="15">Phase 15 (2025)</option>
                          <option value="16">Phase 16 (2026)</option>
                        </select>
                      </div>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Area <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointNetwork}
                          onChange={(e) => {
                            setSharePointNetwork(e.target.value);
                          }}
                        >
                          <option value="">Select Area</option>
                          <option value="EPN">EPN</option>
                          <option value="LPN">LPN</option>
                          <option value="SPN">SPN</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={()=>{
                    setShowSharePointUploadModal(false);
                    setSharePointUploadRecord(null);
                    setSharePointPhaseType('phase');
                    setSharePointPhaseNumber('15');
                    setSharePointNetwork('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn" 
                  style={{background:'#8b1634', color:'#fff', fontWeight:600}}
                  onClick={handleSharePointUpload}
                >
                  Upload to SharePoint
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload POW Assessment File"
        importPath="pow-assessment/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setRecords(prev => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Delete POW Assessment</h5>
                <button type="button" className="btn-close" onClick={()=>setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this record?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

export default PowAssessmentPage;

